import { z } from 'zod';
import { PlanningBuyingEvidence, PlanningVelocityTier } from './planning-run';
import {
  Uuid,
  Email,
  Iso,
  Cents,
  Provider,
  Role,
  RoiGrade,
  FinanceGrade,
  KeepaTier,
  Feature,
  SpApiOnboardingStatus,
  SupplierPipelineStatus,
} from './primitives';
import {
  User,
  Tenant,
  CredentialMeta,
  DiscoveryRun,
  FeeProfile,
  FeeProfileData,
  KeepaQuerySchedule,
  KeepaQueryTemplate,
  KeepaSearchRun,
  ScoredProduct,
  SortRotationEntry,
  Supplier,
  SupplierCandidate,
  SupplierDetail,
  TemplateOrigin,
  TemplateParameterSpec,
} from './domain';
import { KeepaSearchRunStatus } from './primitives';
import { KeepaFilters } from './keepa-filters';
import { OPENROUTER_MODEL_REGEX } from './openrouter-models';
import { BrandSizeBucket } from './brand';

// ─────────────────────────────────────────────────────────
// Error
// ─────────────────────────────────────────────────────────

export const ApiError = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;

// ─────────────────────────────────────────────────────────
// Auth
// ─────────────────────────────────────────────────────────

export const SignInRequest = z.object({
  email: Email,
  password: z.string().min(8),
});
export type SignInRequest = z.infer<typeof SignInRequest>;

export const SignInResponse = z.object({
  user: User,
  sessionExpiresAt: Iso,
});
export type SignInResponse = z.infer<typeof SignInResponse>;

export const MeResponse = z.object({
  user: User,
  tenant: Tenant,
  /** M0 entitlements: effective module map (defaults + admin overrides). Keyed
   *  by module (sourcing/orders/warehouse/shipments/amazon); record so new
   *  modules don't break old clients. Defaulted so a web bundle deployed ahead
   *  of the api Worker degrades to everything-off instead of failing the me
   *  query (which would sign everyone out until the api ships). */
  modules: z.record(z.boolean()).default({}),
});
export type MeResponse = z.infer<typeof MeResponse>;

/** PATCH /api/me — any authenticated role may rename their own profile. */
export const UpdateMeRequest = z
  .object({
    name: z.string().trim().min(1).max(80),
  })
  .strict();
export type UpdateMeRequest = z.infer<typeof UpdateMeRequest>;

// ─────────────────────────────────────────────────────────
// Credentials (BYOK Vault)
// ─────────────────────────────────────────────────────────

export const ListCredentialsResponse = z.array(CredentialMeta);
export type ListCredentialsResponse = z.infer<typeof ListCredentialsResponse>;

export const CreateCredentialRequest = z
  .object({
    provider: Provider,
    /**
     * Plaintext value. For most providers this is the API key itself. For
     * SP-API the route ignores this in favor of the dedicated spApi* fields
     * below — the route json-encodes them into the encrypted blob — but
     * zod requires `value` to satisfy the base type. The SP-API path
     * synthesizes a value at the route layer when needed; the field is
     * still min(1) here so the SPA form picks up the missing-field error
     * for non-SP-API providers immediately.
     */
    value: z.string().min(1),
    label: z.string().max(100).optional(),
    // Provider-specific metadata. Validators below enforce required-by-provider.
    keepaTier: KeepaTier.optional(),
    // Empty string is allowed at the field level so the SPA form's
    // react-hook-form state (which carries '' as the default for hidden
    // fields) doesn't get blocked by the resolver before the submit handler
    // can run. The bottom `.refine()` re-enforces non-empty when the
    // provider IS openrouter — that's where the user-facing rule lives.
    openrouterModel: z
      .string()
      .refine(
        (v) => v === '' || OPENROUTER_MODEL_REGEX.test(v),
        'Must be in `org/model` format (e.g. anthropic/claude-haiku-4.5)'
      )
      .optional(),
    // ── SP-API customer-as-developer credential fields (Phase 1.8 Pass 4) ─
    // These five are all required together when `provider === 'sp_api'`.
    // The route json-encodes them into the encrypted ciphertext as a single
    // blob so a single key rotation atomically swaps the whole bundle.
    spApiLwaRefreshToken: z.string().min(1).optional(),
    spApiLwaClientId: z.string().min(1).optional(),
    spApiLwaClientSecret: z.string().min(1).optional(),
    spApiSellerId: z.string().min(1).optional(),
    /** Defaults to ATVPDKIKX0DER (US) at the route layer when omitted. */
    spApiMarketplaceId: z.string().min(1).optional(),
  })
  .refine((v) => v.provider !== 'openrouter' || (v.openrouterModel?.length ?? 0) > 0, {
    message: 'openrouterModel is required when provider is openrouter',
    path: ['openrouterModel'],
  })
  .refine(
    (v) =>
      v.provider !== 'sp_api' ||
      (Boolean(v.spApiLwaRefreshToken) &&
        Boolean(v.spApiLwaClientId) &&
        Boolean(v.spApiLwaClientSecret) &&
        Boolean(v.spApiSellerId)),
    {
      message:
        'SP-API requires spApiLwaRefreshToken, spApiLwaClientId, spApiLwaClientSecret, and spApiSellerId',
      path: ['spApiLwaRefreshToken'],
    }
  );
export type CreateCredentialRequest = z.infer<typeof CreateCredentialRequest>;

export const CreateCredentialResponse = CredentialMeta;
export type CreateCredentialResponse = z.infer<typeof CreateCredentialResponse>;

export const ValidateCredentialResponse = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
});
export type ValidateCredentialResponse = z.infer<typeof ValidateCredentialResponse>;

// ─────────────────────────────────────────────────────────
// Cost Estimate
// ─────────────────────────────────────────────────────────

export const CostBreakdown = z.object({
  estimatedUsd: z.number().nonnegative(),
  breakdown: z.object({
    // Provider-agnostic AI usage (OpenRouter routes to whichever model the
    // tenant configured). Token counts use the OpenAI-shape names.
    ai: z
      .object({
        provider: z.literal('openrouter'),
        model: z.string(),
        promptTokens: z.number().int(),
        completionTokens: z.number().int(),
        costUsd: z.number().nonnegative(),
      })
      .optional(),
    keepa: z
      .object({
        tokens: z.number().int(),
        tier: KeepaTier,
        costUsd: z.number().nonnegative(),
      })
      .optional(),
  }),
});
export type CostBreakdown = z.infer<typeof CostBreakdown>;

export const CostEstimateRequest = z.object({
  feature: Feature,
  inputText: z.string(),
});
export type CostEstimateRequest = z.infer<typeof CostEstimateRequest>;

export const CostEstimateResponse = CostBreakdown;
export type CostEstimateResponse = z.infer<typeof CostEstimateResponse>;

// ─────────────────────────────────────────────────────────
// Fee Profiles
// ─────────────────────────────────────────────────────────

export const ListFeeProfilesResponse = z.array(FeeProfile);
export type ListFeeProfilesResponse = z.infer<typeof ListFeeProfilesResponse>;

export const CreateFeeProfileRequest = z.object({
  name: z.string().min(1).max(100),
  data: FeeProfileData,
  isDefault: z.boolean().optional(),
});
export type CreateFeeProfileRequest = z.infer<typeof CreateFeeProfileRequest>;

export const UpdateFeeProfileRequest = z.object({
  name: z.string().min(1).max(100).optional(),
  data: FeeProfileData.optional(),
  isDefault: z.boolean().optional(),
});
export type UpdateFeeProfileRequest = z.infer<typeof UpdateFeeProfileRequest>;

// ─────────────────────────────────────────────────────────
// Keepa balance (live token-bucket readout for UI header)
// ─────────────────────────────────────────────────────────

export const KeepaBalanceResponse = z.object({
  tokensLeft: z.number().int(),
  refillRate: z.number().int(),
  /** Milliseconds until the next refill tick, per Keepa's /token. */
  refillIn: z.number().int(),
});
export type KeepaBalanceResponse = z.infer<typeof KeepaBalanceResponse>;

// ─────────────────────────────────────────────────────────
// Tavily balance (live usage / limit readout for the SPA header chip)
// ─────────────────────────────────────────────────────────

export const TavilyBalanceResponse = z.object({
  /** Credits used by THIS api key in the current billing cycle. */
  usage: z.number().int().nonnegative(),
  /** Hard cap for this key (null when the plan has no fixed key-level cap). */
  limit: z.number().int().nullable(),
  /** `limit - usage`, or null when limit is null. */
  remaining: z.number().int().nullable(),
  /** Tavily plan slug — `free` / `paygo` / `pro` / etc. */
  plan: z.string(),
});
export type TavilyBalanceResponse = z.infer<typeof TavilyBalanceResponse>;

// ─────────────────────────────────────────────────────────
// Keepa NL Search (the atomic Phase 1 feature)
// ─────────────────────────────────────────────────────────

/**
 * Either a natural-language query (the AI translates) or pre-translated filters
 * (the SPA sends after a user removed a chip). Validation enforces at least one.
 */
export const NlSearchRequest = z
  .object({
    query: z.string().min(1).max(2000).optional(),
    filters: KeepaFilters.optional(),
    feeProfileId: Uuid.optional(),
  })
  .refine((v) => v.query !== undefined || v.filters !== undefined, {
    message: 'Either query or filters is required',
  });
export type NlSearchRequest = z.infer<typeof NlSearchRequest>;

export const NlSearchResponse = z.object({
  translatedFilters: KeepaFilters,
  products: z.array(ScoredProduct),
  /**
   * Total products matching the filter set on Keepa's side before pagination
   * + slicing. `products.length` may be smaller (capped by tier-aware limit
   * or Keepa's /product batch ceiling). Used for empty-state diagnostics.
   */
  totalResults: z.number().int().nonnegative(),
  /**
   * How many fetched products were dropped for having a brand size above the
   * tenant's cap. When `products` is empty but this is > 0, the empty state
   * should say "all results were above your size cap" — not blame a Keepa
   * fetch failure. Defaults to 0 for older payloads.
   */
  brandSizeFilteredCount: z.number().int().nonnegative().default(0),
  costs: z.object({
    estimatedUsd: z.number(),
    actualUsd: z.number(),
    breakdown: CostBreakdown.shape.breakdown,
  }),
});
export type NlSearchResponse = z.infer<typeof NlSearchResponse>;

/**
 * Preview-only request: translate the NL query into filters + estimate the
 * Keepa token cost, without burning any Keepa tokens. Gates the expensive
 * /query+/product call on user confirmation.
 */
export const NlSearchPreviewRequest = z.object({
  query: z.string().min(1).max(2000),
});
export type NlSearchPreviewRequest = z.infer<typeof NlSearchPreviewRequest>;

export const NlSearchPreviewResponse = z.object({
  translatedFilters: KeepaFilters,
  /** Conservative upper bound on Keepa tokens this filter set would burn. */
  estimatedKeepaTokens: z.number().int().nonnegative(),
  /** Live Keepa balance fetched right after translation, if available. */
  keepaBalance: KeepaBalanceResponse.nullable(),
  /**
   * Inferred Keepa subscription tier from the balance reading.
   * - `free`: refillRate < 10/min, balance caps at ~60. Translator forces
   *   limit ≤ 10 so default scouts fit the cap.
   * - `paid`: refillRate ≥ 10/min, no cap.
   * - `unknown`: balance fetch failed (e.g. no Keepa key).
   */
  tierHint: z.enum(['free', 'paid', 'unknown']),
  /** Raw token usage from the LLM translation (prompt + completion). */
  llmUsage: z.object({
    model: z.string(),
    promptTokens: z.number().int(),
    completionTokens: z.number().int(),
    promptVersion: z.string(),
  }),
});
export type NlSearchPreviewResponse = z.infer<typeof NlSearchPreviewResponse>;

// ─────────────────────────────────────────────────────────
// Audit logs (read-only history for the SPA)
// ─────────────────────────────────────────────────────────

/**
 * Slim, owner-safe view of an `audit_logs` row. Excludes IP / user-agent /
 * raw cost-metadata that aren't needed to debug a search history page.
 */
export const AuditLogEntry = z.object({
  id: Uuid,
  occurredAt: Iso,
  action: z.string(),
  result: z.enum(['success', 'failure', 'partial']),
  errorMessage: z.string().nullable(),
  userEmail: z.string().nullable(),
  changes: z.unknown().nullable(),
  costMetadata: z.unknown().nullable(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntry>;

export const ListAuditLogsQuery = z.object({
  /** Filter by action prefix (e.g. "nl_search" matches both preview + discovered). */
  action: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListAuditLogsQuery = z.infer<typeof ListAuditLogsQuery>;

export const ListAuditLogsResponse = z.array(AuditLogEntry);
export type ListAuditLogsResponse = z.infer<typeof ListAuditLogsResponse>;

// ─────────────────────────────────────────────────────────
// Products
// ─────────────────────────────────────────────────────────

export const ListProductsQuery = z.object({
  gradeMin: RoiGrade.optional(),
  brand: z.string().optional(),
  /** Exact-match filter on products.category (Keepa's reported category name). */
  category: z.string().optional(),
  /** When set, filter to ONLY this status. Comma-separated values OR'd. */
  status: z.string().optional(),
  /** Hide rows whose status === 'passed'. Defaults to true on the SPA. */
  hidePassed: z
    .union([z.literal('true'), z.literal('false')])
    .transform((v) => v === 'true')
    .optional(),
  /**
   * Filter to products that were created during a given keepa_search_runs row's
   * execution window — Phase 1.8 Pass 5. The route resolves the run, then
   * matches products with `created_at` between `started_at` and
   * `completed_at + 30s`. Returns 404 if the run isn't this tenant's.
   */
  searchRunId: Uuid.optional(),
  sortBy: z.enum(['roi', 'bsr', 'price', 'recent']).default('roi'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListProductsQuery = z.infer<typeof ListProductsQuery>;

/**
 * GET /api/products/review-queue-count — Phase 1.8 Pass 5.
 * Cheap query for the app-header chip: count of `pending` rows surfaced in
 * the last 24h. The SPA polls this every 60s and hides the chip on 0.
 */
export const ReviewQueueCountResponse = z.object({
  count: z.number().int().nonnegative(),
});
export type ReviewQueueCountResponse = z.infer<typeof ReviewQueueCountResponse>;

export const ListProductsResponse = z.object({
  items: z.array(ScoredProduct),
  nextCursor: z.string().nullable(),
});
export type ListProductsResponse = z.infer<typeof ListProductsResponse>;

// Single product detail. Same shape as the list item plus a raw Keepa
// snapshot (kept opaque on the client — for now we just pass through whatever
// the adapter stored).
import { ProductStatus } from './primitives';

// ─────────────────────────────────────────────────────────
// Suppliers (Phase 1.5)
// ─────────────────────────────────────────────────────────

export const ListSuppliersQuery = z.object({
  /** Filter to one or more pipeline statuses (comma-separated). */
  status: z.string().optional(),
  /** Hide rows whose status === 'passed'. SPA defaults to true. */
  hidePassed: z
    .union([z.literal('true'), z.literal('false')])
    .transform((v) => v === 'true')
    .optional(),
});
export type ListSuppliersQuery = z.infer<typeof ListSuppliersQuery>;

export const ListSuppliersResponse = z.array(Supplier);
export type ListSuppliersResponse = z.infer<typeof ListSuppliersResponse>;

export const CreateSupplierRequest = z.object({
  name: z.string().trim().min(1).max(255),
  website: z.string().trim().max(2000).optional().nullable(),
  primaryEmail: z.string().trim().max(255).email().optional().nullable(),
  primaryContactName: z.string().trim().max(255).optional().nullable(),
  status: SupplierPipelineStatus.default('lead'),
  notes: z.string().max(10_000).optional().nullable(),
});
export type CreateSupplierRequest = z.infer<typeof CreateSupplierRequest>;

export const UpdateSupplierRequest = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    website: z.string().trim().max(2000).optional().nullable(),
    primaryEmail: z.string().trim().max(255).email().optional().nullable(),
    primaryContactName: z.string().trim().max(255).optional().nullable(),
    status: SupplierPipelineStatus.optional(),
    notes: z.string().max(10_000).optional().nullable(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field is required',
  });
export type UpdateSupplierRequest = z.infer<typeof UpdateSupplierRequest>;

export const PinAsinRequest = z.object({
  asin: z.string().length(10),
  notes: z.string().max(2000).optional().nullable(),
});
export type PinAsinRequest = z.infer<typeof PinAsinRequest>;

export const SupplierResponse = Supplier;
export type SupplierResponse = z.infer<typeof SupplierResponse>;

export const SupplierDetailResponse = SupplierDetail;
export type SupplierDetailResponse = z.infer<typeof SupplierDetailResponse>;

/**
 * GET /api/products/:asin/suppliers — suppliers pinned to a given ASIN, plus
 * (later, Week 15+) candidates and recent discovery runs. Week 11 ships the
 * `suppliers` field only; the others arrive with the discovery slice.
 */
export const SuppliersForAsinResponse = z.object({
  suppliers: z.array(Supplier),
});
export type SuppliersForAsinResponse = z.infer<typeof SuppliersForAsinResponse>;

// ─────────────────────────────────────────────────────────
// Discovery (Phase 1.5 Weeks 13–14)
// ─────────────────────────────────────────────────────────

/**
 * SSE event payload streamed during a discovery run. Each event is a
 * single JSON object on its own SSE `data:` line, with a `type` discriminant.
 * Mirrors the NL-search streaming pattern.
 */
export const DiscoveryEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('run_started'), runId: Uuid }),
  z.object({
    type: z.literal('queries_generated'),
    queries: z.array(
      z.object({
        query: z.string(),
        tier: z.union([z.literal(1), z.literal(3), z.literal(4)]),
      })
    ),
  }),
  z.object({
    type: z.literal('searched'),
    query: z.string(),
    tier: z.union([z.literal(1), z.literal(3), z.literal(4)]),
    resultCount: z.number().int().nonnegative(),
  }),
  z.object({ type: z.literal('parsing'), totalResults: z.number().int().nonnegative() }),
  z.object({
    type: z.literal('candidates_found'),
    candidates: z.array(SupplierCandidate),
  }),
  z.object({
    type: z.literal('run_completed'),
    runId: Uuid,
    candidateCount: z.number().int().nonnegative(),
  }),
  z.object({ type: z.literal('run_failed'), runId: Uuid, error: z.string() }),
]);
export type DiscoveryEvent = z.infer<typeof DiscoveryEvent>;

/**
 * GET /api/discovery-runs/:id — poll a single run's terminal state. The SSE
 * stream is the live channel; this endpoint is for "what happened in run X"
 * after the fact (e.g., audit log row, run-history collapsible).
 */
export const DiscoveryRunResponse = DiscoveryRun;
export type DiscoveryRunResponse = z.infer<typeof DiscoveryRunResponse>;

/** GET /api/discovery-runs?asin=X — recent runs for an ASIN, newest first. */
export const ListDiscoveryRunsQuery = z.object({
  asin: z.string().length(10),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type ListDiscoveryRunsQuery = z.infer<typeof ListDiscoveryRunsQuery>;

export const ListDiscoveryRunsResponse = z.array(DiscoveryRun);
export type ListDiscoveryRunsResponse = z.infer<typeof ListDiscoveryRunsResponse>;

/** GET /api/supplier-candidates?asin=X[&status=pending] — review queue. */
export const ListSupplierCandidatesQuery = z.object({
  asin: z.string().length(10),
  status: z.string().optional(),
});
export type ListSupplierCandidatesQuery = z.infer<typeof ListSupplierCandidatesQuery>;

export const ListSupplierCandidatesResponse = z.array(SupplierCandidate);
export type ListSupplierCandidatesResponse = z.infer<typeof ListSupplierCandidatesResponse>;

/**
 * POST /api/supplier-candidates/:id/promote — turn a candidate into a real
 * supplier row + auto-pin the candidate's ASIN. Optional overrides for the
 * supplier's name / website if the user wants to fix what the LLM extracted.
 */
export const PromoteCandidateRequest = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    website: z.string().trim().max(2000).optional().nullable(),
    primaryEmail: z.string().trim().max(255).email().optional().nullable(),
  })
  .optional();
export type PromoteCandidateRequest = z.infer<typeof PromoteCandidateRequest>;

export const PromoteCandidateResponse = z.object({
  supplier: Supplier,
  candidate: SupplierCandidate,
});
export type PromoteCandidateResponse = z.infer<typeof PromoteCandidateResponse>;

/**
 * POST /api/products/:asin/discover/preview — runs ONLY the LLM query
 * generator and returns the planned queries plus a conservative cost
 * estimate. No Tavily call, no DB write. Costs ~1 LLM call worth of
 * tokens to surface before the user commits to a full run.
 */
export const DiscoveryPreviewResponse = z.object({
  queries: z.array(
    z.object({
      query: z.string(),
      tier: z.union([z.literal(1), z.literal(3), z.literal(4)]),
    })
  ),
  /** Tavily credits the run will burn (1 per query in current pricing). */
  estimatedTavilyCredits: z.number().int().nonnegative(),
  /** Generator usage that already burned to produce this preview. */
  generatorUsage: z.object({
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    promptVersion: z.string(),
    model: z.string(),
  }),
  /**
   * Conservative upper bound on the parser LLM call's tokens.
   * `inputTokens` is roughly the surviving search results' bytes / 4
   * (post-dedupe); we estimate optimistically against query-count.
   */
  estimatedParserTokens: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
  }),
  /**
   * Live Tavily balance at preview time so the UI can warn
   * "estimated cost > balance" inline.
   */
  tavilyBalance: z
    .object({
      usage: z.number().int().nonnegative(),
      limit: z.number().int().nullable(),
      remaining: z.number().int().nullable(),
    })
    .nullable(),
});
export type DiscoveryPreviewResponse = z.infer<typeof DiscoveryPreviewResponse>;

export const ProductDetail = ScoredProduct.extend({
  keepaSnapshot: z.unknown().nullable(),
});
export type ProductDetail = z.infer<typeof ProductDetail>;

/**
 * PATCH /api/products/:asin payload. All fields are optional; only the
 * provided ones get touched. Setting `manualSupplierCostCents` triggers a
 * server-side ROI recompute.
 */
export const UpdateProductRequest = z
  .object({
    status: ProductStatus.optional(),
    notes: z.string().max(2000).nullable().optional(),
    manualSupplierCostCents: z.number().int().nonnegative().max(10_000_000).nullable().optional(),
  })
  .refine(
    (v) =>
      v.status !== undefined || v.notes !== undefined || v.manualSupplierCostCents !== undefined,
    { message: 'At least one field is required' }
  );
export type UpdateProductRequest = z.infer<typeof UpdateProductRequest>;

export const UpdateProductResponse = ProductDetail;
export type UpdateProductResponse = z.infer<typeof UpdateProductResponse>;

// ─────────────────────────────────────────────────────────
// Outreach drafting (Phase 1.5 Pass 5 — Week 17)
// ─────────────────────────────────────────────────────────

/**
 * Three template kinds keyed to the supplier pipeline transition the user
 * is about to make. The route enforces stage→template compatibility:
 *   cold_intro      requires status === 'lead'
 *   follow_up       requires status === 'contacted'
 *   pricing_request requires status === 'replied'
 *
 * `negotiating` / `active` / `passed` have no v1 templates — Phase 3 work.
 */
export const OutreachTemplateKind = z.enum(['cold_intro', 'follow_up', 'pricing_request']);
export type OutreachTemplateKind = z.infer<typeof OutreachTemplateKind>;

export const DraftOutreachRequest = z.object({
  templateKind: OutreachTemplateKind,
});
export type DraftOutreachRequest = z.infer<typeof DraftOutreachRequest>;

export const DraftOutreachResponse = z.object({
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(1400),
  templateKind: OutreachTemplateKind,
  promptVersion: z.string(),
  model: z.string(),
  usage: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
  }),
});
export type DraftOutreachResponse = z.infer<typeof DraftOutreachResponse>;

/**
 * POST /api/suppliers/:id/refine-outreach — one-shot revision of the
 * CURRENT edited subject/body per an explicit user instruction (preset or
 * free text). Bounds mirror SendOutreachRequest's — the input is whatever
 * the operator currently has in the editable composer, not raw model
 * output, so it can be longer than a fresh DraftOutreachResponse.
 */
export const RefineOutreachRequest = z.object({
  subject: z.string().min(1).max(300),
  body: z.string().min(1).max(10_000),
  instruction: z.string().min(1).max(500),
});
export type RefineOutreachRequest = z.infer<typeof RefineOutreachRequest>;

/** Mirrors DraftOutreachResponse conventions (minus templateKind — refine has none). */
export const RefineOutreachResponse = z.object({
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(1400),
  promptVersion: z.string(),
  model: z.string(),
  usage: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
  }),
});
export type RefineOutreachResponse = z.infer<typeof RefineOutreachResponse>;

// ─────────────────────────────────────────────────────────
// Keepa query templates (Phase 1.8 Pass 1)
// ─────────────────────────────────────────────────────────

export const ListKeepaTemplatesQuery = z.object({
  origin: TemplateOrigin.optional(),
  /** Default false: archived rows are hidden from picker UIs. */
  includeArchived: z
    .union([z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => v === 'true'),
});
export type ListKeepaTemplatesQuery = z.infer<typeof ListKeepaTemplatesQuery>;

export const ListKeepaTemplatesResponse = z.array(KeepaQueryTemplate);
export type ListKeepaTemplatesResponse = z.infer<typeof ListKeepaTemplatesResponse>;

export const KeepaTemplateResponse = KeepaQueryTemplate;
export type KeepaTemplateResponse = z.infer<typeof KeepaTemplateResponse>;

export const CreateKeepaTemplateRequest = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  /**
   * Original natural-language query captured when the operator saves at preview
   * time (SaveQueryDialog on SearchPage). Optional + nullable — templates
   * created via the raw-JSON TemplateForm in /templates have no NL source.
   * Capped at 2000 chars to match the same envelope the LLM translator accepts.
   */
  naturalLanguageQuery: z.string().trim().min(1).max(2000).nullable().optional(),
  /**
   * Partial KeepaFilters JSON. We don't full-parse here because templates are
   * intentionally partial; the resolver merges parameter values in and only
   * then validates against the full KeepaFilters schema. We still cap key
   * count + depth to prevent JSONB abuse.
   */
  fixedFilters: z.record(z.unknown()),
  parameters: z.array(TemplateParameterSpec).max(10),
});
export type CreateKeepaTemplateRequest = z.infer<typeof CreateKeepaTemplateRequest>;

/**
 * Combined create — single round trip that persists a template AND
 * starts a recurring schedule against it. Used by the preview-card
 * "Save & start hourly" action on SearchPage. The route enforces the
 * 1-enabled-hourly-schedule-per-tenant cap (HOURLY_LIMIT_REACHED 409)
 * BEFORE touching the database, so this never leaves partial state.
 *
 * See docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §3 + §4.
 */
export const CreateKeepaTemplateWithScheduleRequest = CreateKeepaTemplateRequest.extend({
  schedule: z.object({
    name: z.string().trim().min(1).max(120),
    cronExpression: z.string().trim().min(1).max(64),
    /** Optional — server defaults to 1000 for hourly, 30 otherwise. */
    monthlyRunCap: z.number().int().min(1).max(1000).optional(),
    parameterValues: z.record(z.unknown()).default({}),
  }),
});
export type CreateKeepaTemplateWithScheduleRequest = z.infer<
  typeof CreateKeepaTemplateWithScheduleRequest
>;

/**
 * Response shape for `POST /api/keepa-templates/with-schedule` — both
 * rows that were created in the transaction, so the client can attach
 * the template + schedule to its caches in one go.
 */
export const CreateKeepaTemplateWithScheduleResponse = z.object({
  template: KeepaQueryTemplate,
  schedule: KeepaQuerySchedule,
});
export type CreateKeepaTemplateWithScheduleResponse = z.infer<
  typeof CreateKeepaTemplateWithScheduleResponse
>;

export const UpdateKeepaTemplateRequest = z
  .object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(500).nullable().optional(),
    naturalLanguageQuery: z.string().trim().min(1).max(2000).nullable().optional(),
    fixedFilters: z.record(z.unknown()).optional(),
    parameters: z.array(TemplateParameterSpec).max(10).optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.description !== undefined ||
      v.naturalLanguageQuery !== undefined ||
      v.fixedFilters !== undefined ||
      v.parameters !== undefined,
    { message: 'At least one field is required' }
  );
export type UpdateKeepaTemplateRequest = z.infer<typeof UpdateKeepaTemplateRequest>;

export const CloneKeepaTemplateRequest = z
  .object({
    name: z.string().min(1).max(120).optional(),
  })
  .optional();
export type CloneKeepaTemplateRequest = z.infer<typeof CloneKeepaTemplateRequest>;

// ─── Phase 1.9 Pass 3 — Per-template discovered products ───────────────
//
// GET /api/keepa-templates/:id/products — every distinct ASIN this template
// has surfaced across all its runs, deduped, with current status + ROI grade.
// The deduplication picks the most-recent createdAt (= first discovery by this
// template) so the operator sees a single row per product even if multiple
// runs touched the same ASIN.

export const ListTemplateProductsQuery = z.object({
  /** Comma-separated product_status values (pending / interested / pursuing / passed). */
  status: z.string().optional(),
  /** Default true: hide rows where status='passed'. */
  hidePassed: z
    .union([z.literal('true'), z.literal('false')])
    .transform((v) => v === 'true')
    .optional(),
  sortBy: z.enum(['roi', 'bsr', 'price', 'recent']).default('recent'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type ListTemplateProductsQuery = z.infer<typeof ListTemplateProductsQuery>;

export const ListTemplateProductsResponse = z.object({
  items: z.array(ScoredProduct),
  /** Total distinct ASINs this template has surfaced (post-filter). */
  total: z.number().int().nonnegative(),
  /** Newest run timestamp seen across the join — null when the template has no runs. */
  latestRunAt: Iso.nullable(),
});
export type ListTemplateProductsResponse = z.infer<typeof ListTemplateProductsResponse>;

// ─────────────────────────────────────────────────────────
// Keepa search runs (Phase 1.8 Pass 2)
// ─────────────────────────────────────────────────────────

/**
 * Body for POST /api/keepa-templates/:id/run — resolves parameters into a
 * full KeepaFilters, then queues or runs inline depending on `runMode`.
 *
 * `parameterValues` is keyed by parameter `path` (same convention as the
 * parameter-resolver). `feeProfileId` overrides the tenant default snapshot.
 */
export const RunKeepaTemplateRequest = z.object({
  parameterValues: z.record(z.unknown()).default({}),
  runMode: z.enum(['now', 'queued']).default('now'),
  feeProfileId: Uuid.optional(),
});
export type RunKeepaTemplateRequest = z.infer<typeof RunKeepaTemplateRequest>;

/**
 * Body for POST /api/keepa-search-runs — either resolved filters directly
 * OR templateId + parameter values. Exactly one of the two must be present.
 */
export const CreateKeepaSearchRunRequest = z
  .object({
    filters: KeepaFilters.optional(),
    templateId: Uuid.optional(),
    parameterValues: z.record(z.unknown()).optional(),
    runMode: z.enum(['now', 'queued']).default('queued'),
    feeProfileId: Uuid.optional(),
  })
  .refine((v) => (v.filters !== undefined) !== (v.templateId !== undefined), {
    message: 'Provide exactly one of `filters` or `templateId`',
  });
export type CreateKeepaSearchRunRequest = z.infer<typeof CreateKeepaSearchRunRequest>;

/** Standard run-row response, kept identical across all run endpoints. */
export const KeepaSearchRunResponse = KeepaSearchRun;
export type KeepaSearchRunResponse = z.infer<typeof KeepaSearchRunResponse>;

export const ListKeepaSearchRunsQuery = z.object({
  status: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListKeepaSearchRunsQuery = z.infer<typeof ListKeepaSearchRunsQuery>;

export const ListKeepaSearchRunsResponse = z.array(KeepaSearchRun);
export type ListKeepaSearchRunsResponse = z.infer<typeof ListKeepaSearchRunsResponse>;

/** Re-export so SPA code can compare against `'queued'` etc. without primitives import. */
export { KeepaSearchRunStatus };

/**
 * GET /api/keepa-search-runs/budget — Phase 1.8 Pass 5.
 *
 * Single round-trip readout for the SPA's pre-flight "is there budget?" hint
 * on the search form's "Run now / Queue" toggle. Computed via the same
 * `canRunNow` helper the cron sweep uses, against a default 25-ASIN search
 * (which is the SPA's freeform / template-default cost). The route accepts an
 * optional `?estimatedTokens=N` if the caller wants a precise check against
 * a specific filter set.
 */
export const KeepaBudgetQuery = z.object({
  estimatedTokens: z.coerce.number().int().min(1).max(1000).optional(),
});
export type KeepaBudgetQuery = z.infer<typeof KeepaBudgetQuery>;

export const KeepaBudgetResponse = z.object({
  ok: z.boolean(),
  tokensUsedLast60Min: z.number().int().nonnegative(),
  hourlyBudget: z.number().int().nonnegative(),
  reserveTokens: z.number().int().nonnegative(),
  /** budget - reserve - usedInWindow. Negative when over-budget. */
  remaining: z.number().int(),
  /** Positive number of tokens short when `ok=false`; 0 otherwise. */
  deficit: z.number().int().nonnegative(),
  /** Conservative ETA to refill the deficit; null when refill rate is unknown. */
  nextEligibleAt: Iso.nullable(),
  /** Estimated tokens the budget check ran against (echo of query / default). */
  estimatedTokens: z.number().int().nonnegative(),
});
export type KeepaBudgetResponse = z.infer<typeof KeepaBudgetResponse>;

// ─────────────────────────────────────────────────────────
// Keepa query schedules (Phase 1.8 Pass 3)
// ─────────────────────────────────────────────────────────

/** Standard schedule-row response, reused by every schedule endpoint. */
export const KeepaScheduleResponse = KeepaQuerySchedule;
export type KeepaScheduleResponse = z.infer<typeof KeepaScheduleResponse>;

export const ListKeepaSchedulesResponse = z.array(KeepaQuerySchedule);
export type ListKeepaSchedulesResponse = z.infer<typeof ListKeepaSchedulesResponse>;

/**
 * Response for `POST /api/keepa-schedules/:id/run` — the on-demand "Run now"
 * trigger. Returns the queued keepa_search_runs row identifiers so the SPA
 * can confirm the click landed (the actual execution happens on the next
 * sub-sweep B tick, ≤60s later).
 */
export const RunScheduleNowResponse = z.object({
  runId: Uuid,
  status: KeepaSearchRunStatus,
  queuedAt: Iso,
});
export type RunScheduleNowResponse = z.infer<typeof RunScheduleNowResponse>;

/**
 * Response for `GET /api/keepa-schedules/active`. Powers the
 * ActiveCrawlPanel on SearchPage — one row per enabled schedule with
 * its last-run summary, plus a tenant-wide capacity estimate so the UI
 * can show "you're using ~42% of your Keepa hourly refill."
 *
 * See docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §1 + §4.
 */
export const ActiveScheduleStatus = z.enum([
  /** Enabled and on cron. The common case. */
  'running',
  /** `enabled = false`. */
  'paused',
  /** Auto-paused because 30-day reconfirm expired. */
  'compliance_paused',
  /** Latest fire bounced off the bucket preflight. Will retry next tick. */
  'budget_waiting',
]);
export type ActiveScheduleStatus = z.infer<typeof ActiveScheduleStatus>;

export const ActiveScheduleEntry = z.object({
  id: Uuid,
  name: z.string(),
  templateId: Uuid,
  templateName: z.string(),
  cronExpression: z.string(),
  nextFireAt: Iso.nullable(),
  enabled: z.boolean(),
  status: ActiveScheduleStatus,
  lastRun: z
    .object({
      occurredAt: Iso,
      /** Products the search returned. */
      productCount: z.number().int().nonnegative(),
      /** Distinct brand-enrichment-queue rows this run created. */
      uniqueBrandCount: z.number().int().nonnegative(),
      /** Subset already transitioned to status='done'. */
      brandsEnriched: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type ActiveScheduleEntry = z.infer<typeof ActiveScheduleEntry>;

export const ActiveSchedulesResponse = z.object({
  schedules: z.array(ActiveScheduleEntry),
  capacity: z.object({
    /** Keepa's hard ceiling. Always 60 today; surfaced for future-proofing. */
    ceilingTokensPerHour: z.number().int().positive(),
    /** Sum of (fires/hr × tokens/fire) across enabled schedules + sweep overhead. */
    estimatedTokensPerHour: z.number().int().nonnegative(),
    /** Rounded percentage used. 0 when nothing enabled. */
    percentUsed: z.number().int().min(0),
  }),
});
export type ActiveSchedulesResponse = z.infer<typeof ActiveSchedulesResponse>;

/**
 * Body for POST /api/keepa-schedules. The route resolves parameters against
 * the template's spec once at create time so misconfigured schedules fail
 * loudly upfront instead of in the sweep.
 */
export const CreateKeepaScheduleRequest = z.object({
  templateId: Uuid,
  name: z.string().trim().min(1).max(120),
  parameterValues: z.record(z.unknown()).default({}),
  cronExpression: z.string().trim().min(1).max(64),
  /** Optional hard cap on fires per 30-day window; defaults to 30 server-side. */
  monthlyRunCap: z.number().int().min(1).max(1000).optional(),
  /**
   * Optional discovery rotation. Each schedule fire picks the next entry and
   * overrides the template's sort. Capped at 10 entries to keep the cycle
   * comprehensible. Null/empty = "use the template's sort" (default).
   */
  sortRotation: z.array(SortRotationEntry).max(10).nullable().optional(),
});
export type CreateKeepaScheduleRequest = z.infer<typeof CreateKeepaScheduleRequest>;

export const UpdateKeepaScheduleRequest = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    parameterValues: z.record(z.unknown()).optional(),
    cronExpression: z.string().trim().min(1).max(64).optional(),
    enabled: z.boolean().optional(),
    monthlyRunCap: z.number().int().min(1).max(1000).optional(),
    /** Pass `null` to disable rotation; pass an array to set/replace. */
    sortRotation: z.array(SortRotationEntry).max(10).nullable().optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.parameterValues !== undefined ||
      v.cronExpression !== undefined ||
      v.enabled !== undefined ||
      v.monthlyRunCap !== undefined ||
      v.sortRotation !== undefined,
    { message: 'At least one field is required' }
  );
export type UpdateKeepaScheduleRequest = z.infer<typeof UpdateKeepaScheduleRequest>;

/**
 * POST /api/keepa-schedules/preview-cron — Phase 1.8 Pass 5.
 *
 * Lightweight preview helper for the schedule-create modal: returns the next
 * fire time from the same parser the sweep uses. Always 200 — `valid=false`
 * with `error` is the parse-failure shape so the form can show inline help
 * without juggling 422s.
 */
export const PreviewCronRequest = z.object({
  cronExpression: z.string().trim().min(1).max(64),
});
export type PreviewCronRequest = z.infer<typeof PreviewCronRequest>;

export const PreviewCronResponse = z.discriminatedUnion('valid', [
  z.object({
    valid: z.literal(true),
    nextFireAt: Iso,
  }),
  z.object({
    valid: z.literal(false),
    error: z.string(),
  }),
]);
export type PreviewCronResponse = z.infer<typeof PreviewCronResponse>;

// ─────────────────────────────────────────────────────────
// SP-API status / verify (Phase 1.8 Pass 4)
// ─────────────────────────────────────────────────────────

/**
 * GET /api/credentials/sp-api/status — read for the Pass-5 onboarding wizard.
 * `credentialPresent` distinguishes "user pasted creds but verify failed"
 * (failed + credentialPresent=true) from "user never opened the wizard"
 * (not_started + credentialPresent=false).
 */
export const SpApiStatusResponse = z.object({
  status: SpApiOnboardingStatus,
  credentialPresent: z.boolean(),
});
export type SpApiStatusResponse = z.infer<typeof SpApiStatusResponse>;

/** POST /api/credentials/sp-api/verify — re-runs the LWA+SP-API probe. */
export const SpApiVerifyResponse = z.object({
  ok: z.boolean(),
  status: SpApiOnboardingStatus,
  error: z.string().optional(),
});
export type SpApiVerifyResponse = z.infer<typeof SpApiVerifyResponse>;

/**
 * POST /api/credentials/sp-api/mark-awaiting-approval — Phase 1.8 Pass 5.
 *
 * Flips `tenants.sp_api_onboarding_status` to `awaiting_approval` so the SPA
 * wizard can record "user clicked through step 1 (Amazon dev profile setup)"
 * without yet having credentials to verify. The status is idempotent — a
 * second click on the same step doesn't error, but transitioning FROM
 * `connected` is rejected (use disconnect / delete-credential instead).
 */
export const SpApiMarkAwaitingApprovalResponse = z.object({
  status: SpApiOnboardingStatus,
});
export type SpApiMarkAwaitingApprovalResponse = z.infer<typeof SpApiMarkAwaitingApprovalResponse>;

// ─────────────────────────────────────────────────────────
// Product flags (Phase 1.9 Pass 4 — indie auto-flag + inbox)
// ─────────────────────────────────────────────────────────

export const ProductFlagType = z.enum(['indie_candidate']);
export type ProductFlagType = z.infer<typeof ProductFlagType>;

export const ProductFlagDecision = z.enum(['pending', 'promoted', 'dismissed']);
export type ProductFlagDecision = z.infer<typeof ProductFlagDecision>;

/**
 * Inbox row — flag + a slim view of the underlying product.
 * Designed so the SPA can render the triage table without extra round-trips.
 */
export const ProductFlagInboxRow = z.object({
  id: Uuid,
  flag: ProductFlagType,
  score: z.number(),
  signals: z.array(z.string()),
  ruleVersion: z.string(),
  flaggedAt: Iso,
  reviewedAt: Iso.nullable(),
  reviewedDecision: ProductFlagDecision,
  product: z.object({
    id: Uuid,
    asin: z.string(),
    title: z.string().nullable(),
    brand: z.string().nullable(),
    currentPriceCents: z.number().int().nullable(),
    currentBsr: z.number().int().nullable(),
    reviewCount: z.number().int().nullable(),
    estimatedRoiPct: z.number().nullable(),
    roiGrade: RoiGrade.nullable(),
    status: ProductStatus,
    listedSince: z.number().int().nullable(),
  }),
});
export type ProductFlagInboxRow = z.infer<typeof ProductFlagInboxRow>;

export const ListProductFlagsQuery = z.object({
  flag: ProductFlagType.optional(),
  decision: ProductFlagDecision.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type ListProductFlagsQuery = z.infer<typeof ListProductFlagsQuery>;

export const ListProductFlagsResponse = z.object({
  items: z.array(ProductFlagInboxRow),
});
export type ListProductFlagsResponse = z.infer<typeof ListProductFlagsResponse>;

export const ProductFlagsCountQuery = z.object({
  flag: ProductFlagType.optional(),
  decision: ProductFlagDecision.optional().default('pending'),
});
export type ProductFlagsCountQuery = z.infer<typeof ProductFlagsCountQuery>;

export const ProductFlagsCountResponse = z.object({
  count: z.number().int().nonnegative(),
});
export type ProductFlagsCountResponse = z.infer<typeof ProductFlagsCountResponse>;

export const ReviewProductFlagRequest = z.object({
  decision: z.enum(['promoted', 'dismissed']),
});
export type ReviewProductFlagRequest = z.infer<typeof ReviewProductFlagRequest>;

export const ReviewProductFlagResponse = ProductFlagInboxRow;
export type ReviewProductFlagResponse = z.infer<typeof ReviewProductFlagResponse>;

export const BulkDismissFlagsRequest = z.object({
  ids: z.array(Uuid).min(1).max(200),
});
export type BulkDismissFlagsRequest = z.infer<typeof BulkDismissFlagsRequest>;

export const BulkDismissFlagsResponse = z.object({
  dismissed: z.number().int().nonnegative(),
});
export type BulkDismissFlagsResponse = z.infer<typeof BulkDismissFlagsResponse>;

// ─────────────────────────────────────────────────────────
// Tenant preferences (brand-size filtering)
// ─────────────────────────────────────────────────────────

export const TenantPreferences = z
  .object({
    defaultBrandSizeCap: BrandSizeBucket,
    brandStatsEnabled: z.boolean(),
  })
  .strict();
export type TenantPreferences = z.infer<typeof TenantPreferences>;

export const UpdateTenantPreferencesRequest = z
  .object({
    defaultBrandSizeCap: BrandSizeBucket.optional(),
    brandStatsEnabled: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.defaultBrandSizeCap !== undefined || v.brandStatsEnabled !== undefined, {
    message: 'At least one preference must be supplied',
  });
export type UpdateTenantPreferencesRequest = z.infer<typeof UpdateTenantPreferencesRequest>;

// ─────────────────────────────────────────────────────────
// Team management (owner-only) — apps/api/src/routes/team.ts.
// Permission-key whitelist enforcement lives server-side next to the auth
// guard's VA_PERMISSION_PATHS map (middleware/auth.ts's VA_PERMISSION_KEYS)
// so the schemas package — shared with the frontend — never has to be kept
// in sync with that list by hand. `permissions` here is just "array of
// strings"; the 400 on an unknown key comes from the route.
// ─────────────────────────────────────────────────────────

export const TeamMember = z.object({
  id: Uuid,
  email: Email,
  name: z.string().nullable(),
  role: Role,
  permissions: z.array(z.string()),
  createdAt: Iso,
});
export type TeamMember = z.infer<typeof TeamMember>;

export const ListTeamResponse = z.array(TeamMember);
export type ListTeamResponse = z.infer<typeof ListTeamResponse>;

export const CreateTeamMemberRequest = z
  .object({
    email: Email,
    name: z.string().trim().min(1).max(80),
    permissions: z.array(z.string()),
  })
  .strict();
export type CreateTeamMemberRequest = z.infer<typeof CreateTeamMemberRequest>;

export const CreateTeamMemberResponse = z.object({
  user: TeamMember,
  /**
   * Plaintext of the server-generated password — returned ONCE in this
   * response for the owner to hand off via their own password manager.
   * Never stored or logged; the DB only ever holds the bcrypt hash.
   */
  oneTimePassword: z.string(),
});
export type CreateTeamMemberResponse = z.infer<typeof CreateTeamMemberResponse>;

export const UpdateTeamMemberRequest = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    permissions: z.array(z.string()).optional(),
  })
  .strict()
  .refine((v) => v.name !== undefined || v.permissions !== undefined, {
    message: 'At least one field must be supplied',
  });
export type UpdateTeamMemberRequest = z.infer<typeof UpdateTeamMemberRequest>;

// ─────────────────────────────────────────────────────────
// Feed + dossier (Phase A) — customer-facing read surface over the shared
// wholesale catalog. See apps/api/src/routes/feed.ts.
// ─────────────────────────────────────────────────────────

/** Mirrors `brand_claims.status` (drizzle/schema.ts brandClaimStatusEnum). */
export const BrandClaimStatus = z.enum([
  'claimed',
  'contacted',
  'replied',
  'account_opened',
  'dropped',
]);
export type BrandClaimStatus = z.infer<typeof BrandClaimStatus>;

/** Mirrors `catalog_products.source` (drizzle/schema.ts catalogSourceEnum). */
export const CatalogSource = z.enum(['sweep', 'catalog_pull']);
export type CatalogSource = z.infer<typeof CatalogSource>;

/**
 * A ranked dossier card in the feed list — one row per qualifying brand.
 * `qualifyingCount`/`velocitySum`/`offerCountAvg`/`amazonShare`/`freshnessAt`
 * are computed from the brand's `catalog_products` rows; the feed list scopes
 * that population to the requested category, while the single-brand dossier
 * (below) computes them across ALL of the brand's catalog rows.
 */
export const FeedBrand = z.object({
  brandCacheId: Uuid,
  displayName: z.string(),
  /** Nullable because a cache row's size classification can still be pending. */
  sizeBucket: BrandSizeBucket.nullable(),
  /** `keepa_brand_cache.product_count` — Keepa's total SKU count for the brand. */
  catalogSize: z.number().int().nonnegative().nullable(),
  qualifyingCount: z.number().int().nonnegative(),
  /** Sum of monthly_sold across the qualifying rows; nulls treated as 0. */
  velocitySum: z.number().int().nonnegative(),
  /** Avg offer_count_new across the qualifying rows; null when every row is null. */
  offerCountAvg: z.number().nullable(),
  /** Fraction of rows with amazon_on_listing=true; null-row denominator excluded, null when 0 rows have a value. */
  amazonShare: z.number().min(0).max(1).nullable(),
  freshnessAt: Iso.nullable(),
  /** This tenant's brand_claims status, or null if the tenant hasn't claimed the brand. */
  claimStatus: BrandClaimStatus.nullable(),
  hasWholesalePage: z.boolean(),
  // ── Wholesale vetting (2026-08-17; null = not yet vetted) ──
  /** Independent (non-brand, non-Amazon) sellers on the brand's top listing. */
  independentSellers: z.number().int().nullable(),
  /** True when the brand (or one opaque seller) is the only seller — door likely closed. */
  brandDirectOnly: z.boolean().nullable(),
  /** Major-CPG parent company when detected (e.g. "PepsiCo"), else null. */
  cpgParent: z.string().nullable(),
  /** FBA handling risks from title classification; null = not yet checked. */
  handlingRisks: z
    .array(z.enum(['refrigerated', 'meltable', 'glass_liquid', 'short_shelf_life']))
    .nullable(),
});
export type FeedBrand = z.infer<typeof FeedBrand>;

/** GET /api/feed?categoryId=<id> — ranked dossier cards, capped at 50. */
export const FeedResponse = z.object({
  brands: z.array(FeedBrand),
});
export type FeedResponse = z.infer<typeof FeedResponse>;

/**
 * A single catalog_products row for the dossier's product list. RAW Keepa
 * numbers — banding/rounding for the ToS-safe customer-facing display is the
 * web layer's job, not this API's.
 */
export const DossierProduct = z.object({
  asin: z.string(),
  title: z.string().nullable(),
  priceCents: z.number().int().nullable(),
  bsr: z.number().int().nullable(),
  reviewCount: z.number().int().nullable(),
  monthlySold: z.number().int().nullable(),
  offerCountNew: z.number().int().nullable(),
  amazonOnListing: z.boolean().nullable(),
  source: CatalogSource,
  refreshedAt: Iso,
});
export type DossierProduct = z.infer<typeof DossierProduct>;

/** GET /api/feed/:brandCacheId — the FeedBrand fields plus every catalog row. */
export const DossierResponse = FeedBrand.extend({
  /** All catalog_products rows for the brand, bsr ASC nulls last, capped at 50. */
  products: z.array(DossierProduct),
});
export type DossierResponse = z.infer<typeof DossierResponse>;

/**
 * POST /api/feed/:brandCacheId/discover-supplier — brand-level discovery
 * kickoff from the dossier, catalog-shaped (see orchestrator.ts
 * RunDiscoveryInputCatalog) instead of the tenant-products-by-ASIN path.
 * Same shape as `kickoffBackgroundDiscovery`'s return value: the run is
 * pre-created and fired into the background, so the SPA polls it via the
 * existing GET /api/discovery-runs/:id.
 */
export const DiscoverSupplierForDossierResponse = z.object({
  runId: Uuid,
  asin: z.string(),
});
export type DiscoverSupplierForDossierResponse = z.infer<typeof DiscoverSupplierForDossierResponse>;

// ─────────────────────────────────────────────────────────
// Claims (Phase A) — the manual outcome loop's write surface over
// brand_claims. See apps/api/src/routes/claims.ts.
// ─────────────────────────────────────────────────────────

/** POST /api/claims — claim a brand for outreach. */
export const CreateClaimRequest = z.object({
  brandCacheId: Uuid,
});
export type CreateClaimRequest = z.infer<typeof CreateClaimRequest>;

/** PATCH /api/claims/:id — advance (or drop) a claim's status. */
export const UpdateClaimRequest = z.object({
  status: BrandClaimStatus,
});
export type UpdateClaimRequest = z.infer<typeof UpdateClaimRequest>;

export const ClaimResponse = z.object({
  id: Uuid,
  brandCacheId: Uuid,
  brandDisplayName: z.string(),
  status: BrandClaimStatus,
  createdAt: Iso,
  updatedAt: Iso,
});
export type ClaimResponse = z.infer<typeof ClaimResponse>;

/** GET /api/claims — active statuses first, then dropped; updatedAt DESC within each group. */
export const ListClaimsResponse = z.object({
  claims: z.array(ClaimResponse),
});
export type ListClaimsResponse = z.infer<typeof ListClaimsResponse>;

// ─────────────────────────────────────────────────────────
// Outreach settings (Phase B) — per-tenant standing instructions appended
// to every outreach-draft system prompt. See apps/api/src/routes/
// outreach-settings.ts and lib/ai/prompts/outreach.ts.
// ─────────────────────────────────────────────────────────

export const OutreachSettingsResponse = z.object({
  /** tenants.outreach_context — null when the tenant has set nothing. */
  instructions: z.string().nullable(),
});
export type OutreachSettingsResponse = z.infer<typeof OutreachSettingsResponse>;

/** PUT /api/outreach-settings — empty/whitespace string clears to null. */
export const UpdateOutreachSettingsRequest = z.object({
  instructions: z.string().max(2000),
});
export type UpdateOutreachSettingsRequest = z.infer<typeof UpdateOutreachSettingsRequest>;

// ─────────────────────────────────────────────────────────
// Store settings (Faraz, 2026-08-24) — the store's birthday, a per-tenant
// floor under every Amazon report backfill window (reimbursements' 18mo
// walk, sales' 90d walk) so a tenant whose store started in 2026 never
// walks into pre-existence history. See apps/api/src/routes/
// store-settings.ts and lib/amazon/sync.ts.
// ─────────────────────────────────────────────────────────

export const StoreSettingsResponse = z.object({
  /** tenants.store_started_at — null when the tenant has set nothing (no
   *  floor, today's behavior). ISO datetime string. */
  storeStartedAt: z.string().nullable(),
  /**
   * Price calibration (COCKPIT-VISION.md build-order item 7, 2026-08-25) —
   * tenants.min_margin_pct / target_margin_pct. FRACTIONS 0-1 (FeeProfileData
   * convention), not 0-100 percentages — the SPA converts at the display
   * edge. `.default` covers deploy skew: an older API build that doesn't
   * send these fields yet reads back the same defaults the DB column uses.
   */
  minMarginPct: z.number().min(0).max(1).default(0.1),
  targetMarginPct: z.number().min(0).max(1).default(0.2),
});
export type StoreSettingsResponse = z.infer<typeof StoreSettingsResponse>;

/**
 * PUT /api/store-settings — partial update; every field is independently
 * optional so the Store-date card and the Pricing-targets card can each
 * save without needing to know (or resend) the other's current value. Null
 * on storeStartedAt clears the floor; a future date is rejected (400).
 * min/targetMarginPct validation (0 <= min < 1, 0 <= target < 1, min <=
 * target) happens in the route handler, not the schema, so the error
 * messages can name which field/rule failed.
 */
export const UpdateStoreSettingsRequest = z.object({
  storeStartedAt: z.string().datetime().nullable().optional(),
  minMarginPct: z.number().min(0).max(1).optional(),
  targetMarginPct: z.number().min(0).max(1).optional(),
});
export type UpdateStoreSettingsRequest = z.infer<typeof UpdateStoreSettingsRequest>;

// ─────────────────────────────────────────────────────────
// Outreach sending (Phase B.5 — BYO-mailbox connector). See
// apps/api/src/routes/outreach.ts (send-outreach) and the mail daemon.
// ─────────────────────────────────────────────────────────

/** POST /api/suppliers/:id/send-outreach — queue one email for the daemon. */
export const SendOutreachRequest = z.object({
  subject: z.string().min(1).max(300),
  body: z.string().min(1).max(10_000),
  /** brand_claims row to auto-advance (claimed→contacted on send, →replied on reply). */
  claimId: Uuid.optional(),
});
export type SendOutreachRequest = z.infer<typeof SendOutreachRequest>;

export const SendOutreachResponse = z.object({
  id: Uuid,
  status: z.enum(['queued']),
});
export type SendOutreachResponse = z.infer<typeof SendOutreachResponse>;

/** What an inbound reply actually is (0033). Only human_reply advances the claim. */
export const OutreachInboundClassification = z.enum(['human_reply', 'auto_ack', 'bounce']);
export type OutreachInboundClassification = z.infer<typeof OutreachInboundClassification>;

/** GET /api/suppliers/:id/messages — full thread, newest first. */
export const OutreachMessageItem = z.object({
  id: Uuid,
  direction: z.enum(['outbound', 'inbound']),
  status: z.enum(['queued', 'sent', 'failed', 'received']),
  subject: z.string(),
  body: z.string(),
  /** Inbound only; null on outbound rows and on pre-0033 inbound rows. */
  classification: OutreachInboundClassification.nullable(),
  queuedAt: z.string(),
  sentAt: z.string().nullable(),
});
export type OutreachMessageItem = z.infer<typeof OutreachMessageItem>;

export const ListOutreachMessagesResponse = z.array(OutreachMessageItem);
export type ListOutreachMessagesResponse = z.infer<typeof ListOutreachMessagesResponse>;

// ─────────────────────────────────────────────────────────
// Purchase orders (M1 Orders module, 2026-08-22). Money is integer CENTS.
// Routes are gated by the 'orders' module entitlement.
// ─────────────────────────────────────────────────────────

export const PoStatus = z.enum(['placed', 'confirmed', 'in_transit', 'received_partial', 'closed']);
export type PoStatus = z.infer<typeof PoStatus>;

export const PoDocType = z.enum(['invoice', 'credit_memo']);
export type PoDocType = z.infer<typeof PoDocType>;

export const PurchaseOrderSummary = z.object({
  id: Uuid,
  vendor: z.string(),
  source: z.enum(['invoice_email', 'manual']),
  docType: PoDocType,
  externalId: z.string().nullable(),
  status: PoStatus,
  orderedAt: z.string().nullable(),
  expectedAt: z.string().nullable(),
  totalCents: z.number().int(),
  lineItemsVerified: z.boolean(),
  lineCount: z.number().int(),
});
export type PurchaseOrderSummary = z.infer<typeof PurchaseOrderSummary>;

export const PurchaseOrderLine = z.object({
  id: Uuid,
  itemNo: z.string().nullable(),
  upc: z.string().nullable(),
  description: z.string(),
  qty: z.number().int(),
  unitCostCents: z.number().int(),
  lineTotalCents: z.number().int(),
  /** Pre-allowance wholesale unit cost (KeHE FMT1 SN1 lines with an
   *  allowance block only; null otherwise — 2026-08-24 allowance
   *  surfacing). `.default(null)` covers deploy skew. */
  grossUnitCostCents: z.number().int().nullable().default(null),
  /** Extended (line-total) allowance amount; null when the line carried no
   *  allowance (never 0 — "no allowance" and "zero-dollar allowance" are
   *  different facts). */
  allowanceCents: z.number().int().nullable().default(null),
  /** M2 receiving progress (0 until a receipt exists). */
  qtyReceived: z.number().int().default(0),
  qtyDamaged: z.number().int().default(0),
  /** Catalog Items MAIN image link, joined via listing_map's normalized
   *  item-number mapping to amazon_inventory (Revision 3). `.default(null)`
   *  covers deploy skew. */
  imageUrl: z.string().nullable().default(null),
  /** Amazon product name from the same amazon_inventory join, for lines
   *  where the invoice description alone doesn't read as a product name. */
  productName: z.string().nullable().default(null),
  /** ASIN from the same amazon_inventory join — powers the clickable
   *  Amazon-listing link on the product name (Faraz: "all product names,
   *  everywhere, should be a clickable link"). `.default(null)` covers
   *  deploy skew. */
  asin: z.string().nullable().default(null),
  /** R2 item 5 (returned to vendor): units returned across this line's
   *  lots. `.default(0)` covers deploy skew. */
  unitsReturned: z.number().int().default(0),
  /** R2 item 9 (2026-09-03 intake sanity checks): non-null holds this line
   *  for review — the parser couldn't reconcile its case-pack arithmetic,
   *  or its unit cost jumped >3x/<1/3x vs. this vendor item's most recent
   *  prior PO line. Cleared via PATCH .../lines/:lineId. `.default(null)`
   *  covers deploy skew. */
  reviewReason: z.string().nullable().default(null),
  /** Units per case, when case-pack pricing inference (or a human's
   *  correction via PATCH .../lines/:lineId) determined the printed qty
   *  was actually a case count. Null when parsed in units as printed (the
   *  common case) — 2026-09-02 KeHE case-pack pricing fix. `.default(null)`
   *  covers deploy skew; added fix pass I7 (2026-09-03) so the Review
   *  popover can prefill it. */
  unitsPerCase: z.number().int().nullable().default(null),
});
export type PurchaseOrderLine = z.infer<typeof PurchaseOrderLine>;

/** R2 item 9: PATCH /api/orders/:id/lines/:lineId — clears a review hold,
 *  with optional corrections (lineTotalCents is deliberately never
 *  editable here — it's what the invoice actually billed, the
 *  reconciliation anchor, same convention as the case-priced-line fix). */
export const UpdatePurchaseOrderLineRequest = z.object({
  clearReview: z.literal(true),
  qty: z.number().int().positive().optional(),
  unitCostCents: z.number().int().positive().optional(),
  unitsPerCase: z.number().int().positive().nullable().optional(),
});
export type UpdatePurchaseOrderLineRequest = z.infer<typeof UpdatePurchaseOrderLineRequest>;

export const UpdatePurchaseOrderLineResponse = z.object({
  id: Uuid,
  reviewReason: z.null(),
  qty: z.number().int(),
  unitCostCents: z.number().int(),
  unitsPerCase: z.number().int().nullable(),
  /** Null when clearing the hold did NOT create a receipt (see
   *  `receiptSkippedReason`) — fix pass C2, 2026-09-03: a fully-received
   *  line or an already-closed PO clears without receiving anything. */
  receiptId: Uuid.nullable(),
  /** Non-null explains why clearing the hold created no receipt; null when
   *  it did (the normal case). `.default(null)` covers deploy skew. */
  receiptSkippedReason: z.enum(['fully_received', 'po_closed']).nullable().default(null),
});
export type UpdatePurchaseOrderLineResponse = z.infer<typeof UpdatePurchaseOrderLineResponse>;

// Vendor charges & credits (R1 item 5, 2026-09-01 design doc) — a landed-cost
// ledger attached to a PO: fuel/card surcharges, freight, other fees
// (positive) and credit memos (negative). Money is integer CENTS.

export const PoChargeKind = z.enum([
  'fuel_surcharge',
  'card_surcharge',
  'freight',
  'other_fee',
  'credit_memo',
]);
export type PoChargeKind = z.infer<typeof PoChargeKind>;

export const PoChargeTreatment = z.enum(['landed_cost', 'cash_only']);
export type PoChargeTreatment = z.infer<typeof PoChargeTreatment>;

export const PoChargeSource = z.enum(['invoice_parse', 'manual', 'rule']);
export type PoChargeSource = z.infer<typeof PoChargeSource>;

export const PoCharge = z.object({
  id: Uuid,
  poId: Uuid,
  kind: PoChargeKind,
  treatment: PoChargeTreatment,
  source: PoChargeSource,
  amountCents: z.number().int(),
  currency: z.string(),
  externalId: z.string().nullable(),
  chargedAt: Iso,
  /** Credit memos only: the invoice whose payment the credit reduced. */
  appliedToPoId: Uuid.nullable(),
  note: z.string().nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type PoCharge = z.infer<typeof PoCharge>;

export const PoLandedSummary = z.object({
  subtotalCents: z.number().int(),
  chargesCents: z.number().int(),
  creditsCents: z.number().int(),
  landedCents: z.number().int(),
  unitsReceived: z.number().int(),
  landedUpliftBps: z.number().int(),
  /** R2 item 5 (returned to vendor): units returned across this PO's lots.
   *  `.default(0)` covers deploy skew — additive only, does not change
   *  landedCents/landedUpliftBps math (that's item 4's territory). */
  unitsReturned: z.number().int().default(0),
});
export type PoLandedSummary = z.infer<typeof PoLandedSummary>;

// R2 item 5 (returned to vendor, 2026-09-03 design doc) — lot-level "goods
// returned to vendor" events on a PO's Charges & credits section.

export const PoLot = z.object({
  id: Uuid,
  poLineId: Uuid,
  itemNo: z.string().nullable(),
  qtyReceived: z.number().int(),
  qtyReturned: z.number().int(),
  qtyAllocated: z.number().int(),
  /** received - returned - allocated, floored at 0. */
  availableToReturn: z.number().int(),
});
export type PoLot = z.infer<typeof PoLot>;

export const LotReturn = z.object({
  id: Uuid,
  lotId: Uuid,
  qty: z.number().int(),
  returnedAt: Iso,
  poChargeId: Uuid.nullable(),
  note: z.string().nullable(),
  createdAt: Iso,
});
export type LotReturn = z.infer<typeof LotReturn>;

/** POST /api/orders/:id/returns */
export const CreateLotReturnRequest = z
  .object({
    lotId: Uuid,
    qty: z.number().int().positive(),
    returnedAt: z.union([z.string().date(), z.string().datetime()]),
    poChargeId: Uuid.optional(),
    note: z.string().max(500).optional(),
  })
  .strict();
export type CreateLotReturnRequest = z.infer<typeof CreateLotReturnRequest>;

export const PurchaseOrderDetail = PurchaseOrderSummary.extend({
  subtotalCents: z.number().int(),
  feesCents: z.number().int(),
  /** Sum of lines' allowanceCents; null when no line carries an allowance.
   *  `.default(null)` covers deploy skew. */
  allowanceTotalCents: z.number().int().nullable().default(null),
  notes: z.string().nullable(),
  supplierId: Uuid.nullable(),
  lines: z.array(PurchaseOrderLine),
  /** Optional so older responses (pre charges-&-credits) still parse. */
  charges: z.array(PoCharge).optional(),
  landedSummary: PoLandedSummary.optional(),
  /** R2 item 5: this PO's active returns and lots, for the Charges & credits
   *  "Mark goods returned" dialog. Optional so older responses still parse. */
  returns: z.array(LotReturn).optional(),
  lots: z.array(PoLot).optional(),
});
export type PurchaseOrderDetail = z.infer<typeof PurchaseOrderDetail>;

export const ListPurchaseOrdersResponse = z.array(PurchaseOrderSummary);
export type ListPurchaseOrdersResponse = z.infer<typeof ListPurchaseOrdersResponse>;

export const CreatePurchaseOrderRequest = z.object({
  vendor: z.string().min(1).max(120),
  orderedAt: z.string().datetime().optional(),
  expectedAt: z.string().datetime().optional(),
  notes: z.string().max(4000).optional(),
  supplierId: Uuid.optional(),
  lines: z
    .array(
      z.object({
        description: z.string().min(1).max(500),
        qty: z.number().int().positive(),
        // .nonnegative() closes the long-open "should this be negative"
        // question: a hand-typed purchase is never negative. Credit-memo
        // lines (which legitimately are) arrive via upsertFromKeheDoc, the
        // ingest path — it never goes through this request schema.
        unitCostCents: z.number().int().nonnegative(),
        itemNo: z.string().max(40).optional(),
        upc: z.string().max(20).optional(),
      })
    )
    .min(1)
    .max(200),
});
export type CreatePurchaseOrderRequest = z.infer<typeof CreatePurchaseOrderRequest>;

export const CreatePurchaseOrderResponse = z.object({ id: Uuid, status: PoStatus });
export type CreatePurchaseOrderResponse = z.infer<typeof CreatePurchaseOrderResponse>;

export const UpdatePoStatusRequest = z.object({ status: PoStatus });
export type UpdatePoStatusRequest = z.infer<typeof UpdatePoStatusRequest>;

export const UpdatePoStatusResponse = z.object({ id: Uuid, status: PoStatus });
export type UpdatePoStatusResponse = z.infer<typeof UpdatePoStatusResponse>;

/** POST /api/orders/:id/charges */
export const CreatePoChargeRequest = z
  .object({
    kind: PoChargeKind,
    treatment: PoChargeTreatment,
    amountCents: z
      .number()
      .int()
      .refine((v) => v !== 0, 'amountCents must not be zero'),
    chargedAt: z.union([z.string().date(), z.string().datetime()]),
    externalId: z.string().max(64).optional(),
    appliedToPoId: Uuid.optional(),
    note: z.string().max(500).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const isCreditMemo = value.kind === 'credit_memo';
    if (isCreditMemo !== value.amountCents < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Credit memos are negative; charges are positive.',
        path: ['amountCents'],
      });
    }
    if (value.appliedToPoId !== undefined && !isCreditMemo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'appliedToPoId is only allowed for credit memos',
        path: ['appliedToPoId'],
      });
    }
  });
export type CreatePoChargeRequest = z.infer<typeof CreatePoChargeRequest>;

/** PATCH /api/orders/:id/charges/:chargeId — kind is immutable. */
export const UpdatePoChargeRequest = z
  .object({
    treatment: PoChargeTreatment.optional(),
    amountCents: z
      .number()
      .int()
      .refine((v) => v !== 0, 'amountCents must not be zero')
      .optional(),
    chargedAt: z.union([z.string().date(), z.string().datetime()]).optional(),
    externalId: z.string().max(64).nullable().optional(),
    appliedToPoId: Uuid.nullable().optional(),
    note: z.string().max(500).nullable().optional(),
  })
  .strict()
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field is required',
  });
export type UpdatePoChargeRequest = z.infer<typeof UpdatePoChargeRequest>;

/** GET /api/orders/:id/charges */
export const PoChargesResponse = z.object({
  charges: z.array(PoCharge),
  landedSummary: PoLandedSummary,
});
export type PoChargesResponse = z.infer<typeof PoChargesResponse>;

/** POST /api/orders/charges/apply-rule — preview when apply=false, creates
 *  charges when true (D6: idempotent card-surcharge rule). */
export const ApplyChargeRuleRequest = z
  .object({
    vendor: z.string().min(1),
    kind: z.literal('card_surcharge'),
    ratePercent: z.number().gt(0).lte(10),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    apply: z.boolean(),
  })
  .strict();
export type ApplyChargeRuleRequest = z.infer<typeof ApplyChargeRuleRequest>;

export const ApplyChargeRuleRow = z.object({
  poId: Uuid,
  externalId: z.string().nullable(),
  orderedAt: z.string().nullable(),
  totalCents: z.number().int(),
  creditsAppliedCents: z.number().int(),
  baseCents: z.number().int(),
  amountCents: z.number().int(),
  status: z.enum(['created', 'exists', 'skipped_no_base']),
});
export type ApplyChargeRuleRow = z.infer<typeof ApplyChargeRuleRow>;

export const ApplyChargeRuleResponse = z.object({
  rows: z.array(ApplyChargeRuleRow),
  createdCount: z.number().int(),
  applied: z.boolean(),
});
export type ApplyChargeRuleResponse = z.infer<typeof ApplyChargeRuleResponse>;

/** GET /api/orders/allowance-trend (2026-08-24 allowance-drop alarm).
 *  KeHE-only for now — the only vendor whose invoices carry allowance
 *  data. `allowancePct` values are percentages (e.g. 22.5 means 22.5%),
 *  not fractions. */
export const AllowanceTrendMonth = z.object({
  month: z.string(), // 'YYYY-MM'
  /** allowance / (subtotal + allowance) for invoices ordered that month;
   *  0 for a month with no invoices (zero-filled, not omitted). */
  allowancePct: z.number(),
});
export type AllowanceTrendMonth = z.infer<typeof AllowanceTrendMonth>;

export const AllowanceTrendResponse = z.object({
  vendor: z.string(),
  /** Trailing 6 calendar months, oldest first, zero-filled. */
  months: z.array(AllowanceTrendMonth),
  latestInvoiceExternalId: z.string().nullable(),
  latestAllowancePct: z.number().nullable(),
  /** Average allowancePct over up to 6 invoices immediately preceding the
   *  latest one (not including it). Null when there's no prior history. */
  trailingAvgPct: z.number().nullable(),
  /** True when the latest invoice's rate is less than half the trailing
   *  average, and that average is itself non-trivial (> 0.5%). */
  dropped: z.boolean(),
});
export type AllowanceTrendResponse = z.infer<typeof AllowanceTrendResponse>;

// ─────────────────────────────────────────────────────────
// Warehouse receiving (M2, 2026-08-22). Warehouse-facing payloads carry NO
// money — 3PL staff count units; costs stay tenant-side.
// ─────────────────────────────────────────────────────────

export const ReceivablePoLine = z.object({
  id: Uuid,
  itemNo: z.string().nullable(),
  upc: z.string().nullable(),
  description: z.string(),
  qtyExpected: z.number().int(),
  qtyReceived: z.number().int(),
  qtyDamaged: z.number().int(),
  /** Product identity for the floor (Line-2 adjacent fix B), via the
   *  normalized item-no -> listing_map -> amazon_inventory join.
   *  `.default(null)` is skew-safe for callers on an older build.
   *  MONEY-FREE: no cost/pricing field is ever added here — warehouse
   *  payloads carry no money, full stop. */
  imageUrl: z.string().nullable().default(null),
  productName: z.string().nullable().default(null),
  /** ASIN from the same join — powers the clickable Amazon-listing link on
   *  the product name. `.default(null)` covers deploy skew. */
  asin: z.string().nullable().default(null),
});
export type ReceivablePoLine = z.infer<typeof ReceivablePoLine>;

export const ReceivablePo = z.object({
  id: Uuid,
  vendor: z.string(),
  externalId: z.string().nullable(),
  status: PoStatus,
  orderedAt: z.string().nullable(),
  lines: z.array(ReceivablePoLine),
});
export type ReceivablePo = z.infer<typeof ReceivablePo>;

export const ListReceivablePosResponse = z.array(ReceivablePo);
export type ListReceivablePosResponse = z.infer<typeof ListReceivablePosResponse>;

export const CreateReceiptRequest = z.object({
  poId: Uuid,
  /** Required for the live receive command; prevents duplicate stock facts on retry. */
  idempotencyKey: Uuid,
  /** Tenant operators select a facility; warehouse staff are resolved server-side. */
  warehouseId: Uuid.optional(),
  notes: z.string().max(2000).optional(),
  /** True = this delivery is complete; unaccounted units become a short
   *  claim and the PO closes (KeHE ships once — gaps are credits). */
  deliveryComplete: z.boolean().default(false),
  lines: z
    .array(
      z.object({
        poLineId: Uuid,
        qtyCounted: z.number().int().nonnegative(),
        qtyDamaged: z.number().int().nonnegative().default(0),
        note: z.string().max(500).optional(),
      })
    )
    .min(1)
    .max(500),
});
export type CreateReceiptRequest = z.infer<typeof CreateReceiptRequest>;

export const CreateReceiptResponse = z.object({
  id: Uuid,
  poStatus: PoStatus,
  /** M2.5: credit-claim drafts generated by this receipt (0 when clean). */
  claimsCreated: z.number().int().default(0),
  prepBatch: z
    .object({
      id: Uuid,
      reference: z.string().regex(/^PB-[0-9A-HJKMNP-TV-Z]{6}$/),
      status: z.enum(['ready_to_prep', 'in_prep', 'awaiting_amazon', 'label_ready', 'dispatched']),
    })
    .strict(),
  replayed: z.boolean(),
  /** R2 item 9 (2026-09-03): true when this receipt is NOT a physical
   *  count (inferred-receiving backfill or a review-hold clearance).
   *  `.default(false)` covers deploy skew. */
  inferred: z.boolean().default(false),
});
export type CreateReceiptResponse = z.infer<typeof CreateReceiptResponse>;

/** R2 item 9: POST /api/warehouse/receipts/:id/confirm — a human affirms an
 *  inferred receipt is accurate. */
export const ConfirmReceiptResponse = z.object({
  id: Uuid,
  inferred: z.boolean(),
  confirmedBy: Uuid.nullable(),
  confirmedAt: z.string().nullable(),
});
export type ConfirmReceiptResponse = z.infer<typeof ConfirmReceiptResponse>;

// ─────────────────────────────────────────────────────────
// Receipt history (2026-08-24): "if there is no current receiving, there
// should be a way to find the previous/old one, and searchable" (Faraz).
// Same money-free discipline as ReceivablePo* — the warehouse role reads
// this too, so no cost/price field is ever added here.
// ─────────────────────────────────────────────────────────

export const ReceiptHistoryLine = z.object({
  id: Uuid,
  description: z.string(),
  itemNo: z.string().nullable(),
  upc: z.string().nullable(),
  qtyCounted: z.number().int(),
  qtyDamaged: z.number().int(),
  /** Product identity via imagesByNormalizedItemNo, same join as
   *  ReceivablePoLine. `.default(null)` is skew-safe. MONEY-FREE. */
  imageUrl: z.string().nullable().default(null),
  productName: z.string().nullable().default(null),
  /** ASIN from the same join — powers the clickable Amazon-listing link on
   *  the product name. `.default(null)` is skew-safe. */
  asin: z.string().nullable().default(null),
});
export type ReceiptHistoryLine = z.infer<typeof ReceiptHistoryLine>;

export const ReceiptHistoryPoRef = z.object({
  id: Uuid,
  vendor: z.string(),
  externalId: z.string().nullable(),
  status: PoStatus,
});
export type ReceiptHistoryPoRef = z.infer<typeof ReceiptHistoryPoRef>;

export const ReceiptHistoryItem = z.object({
  id: Uuid,
  receivedAt: z.string(),
  /** User's display name, or their email's local-part when unnamed — never
   *  the raw email (avoids leaking a domain the warehouse role has no
   *  business seeing). Null when the receiver's user row was deleted. */
  receivedBy: z.string().nullable(),
  /** Carries INFERRED-backfill provenance text verbatim when present. */
  notes: z.string().nullable(),
  /** `.default(false)` is skew-safe for receipts recorded before this
   *  column existed. */
  deliveryComplete: z.boolean().default(false),
  /** R2 item 9 (2026-09-03): true until a human confirms this receipt —
   *  not money, safe on this money-free/warehouse-visible payload same as
   *  deliveryComplete. `.default(false)` covers deploy skew. */
  inferred: z.boolean().default(false),
  po: ReceiptHistoryPoRef,
  lines: z.array(ReceiptHistoryLine),
});
export type ReceiptHistoryItem = z.infer<typeof ReceiptHistoryItem>;

export const ListReceiptHistoryResponse = z.array(ReceiptHistoryItem);
export type ListReceiptHistoryResponse = z.infer<typeof ListReceiptHistoryResponse>;

// ─────────────────────────────────────────────────────────
// Credit claims (M2.5) — tenant-side money derived from receiving.
// ─────────────────────────────────────────────────────────

export const CreditClaimKind = z.enum(['short', 'damage']);
export type CreditClaimKind = z.infer<typeof CreditClaimKind>;

export const CreditClaimStatus = z.enum(['draft', 'filed', 'credited', 'dismissed']);
export type CreditClaimStatus = z.infer<typeof CreditClaimStatus>;

export const CreditClaimLine = z.object({
  poLineId: Uuid,
  description: z.string(),
  itemNo: z.string().nullable(),
  upc: z.string().nullable(),
  qty: z.number().int(),
  unitCostCents: z.number().int(),
  amountCents: z.number().int(),
});
export type CreditClaimLine = z.infer<typeof CreditClaimLine>;

export const CreditClaim = z.object({
  id: Uuid,
  poId: Uuid,
  poExternalId: z.string().nullable(),
  vendor: z.string(),
  kind: CreditClaimKind,
  status: CreditClaimStatus,
  amountCents: z.number().int(),
  createdAt: z.string(),
  /** Vendor filing deadline (Phase D); null on pre-deadline rows. Defaults
   *  so responses from an older API build still parse (deploy-order skew). */
  deadlineAt: z.string().nullable().default(null),
  filedAt: z.string().nullable(),
  lines: z.array(CreditClaimLine),
  draftBody: z.string(),
});
export type CreditClaim = z.infer<typeof CreditClaim>;

export const ListCreditClaimsResponse = z.array(CreditClaim);
export type ListCreditClaimsResponse = z.infer<typeof ListCreditClaimsResponse>;

export const UpdateCreditClaimStatusRequest = z.object({
  status: z.enum(['filed', 'credited', 'dismissed']),
});
export type UpdateCreditClaimStatusRequest = z.infer<typeof UpdateCreditClaimStatusRequest>;

// ─────────────────────────────────────────────────────────
// Dashboard (Today, 2026-08-24) — one call powering the home page.
// Section = null ⇒ that module is disabled for the tenant; an empty array
// or inner null means the module is on but there is nothing to show.
// ─────────────────────────────────────────────────────────

export const DashboardClaim = z.object({
  id: Uuid,
  kind: CreditClaimKind,
  vendor: z.string(),
  poExternalId: z.string().nullable(),
  amountCents: z.number().int(),
  deadlineAt: z.string().nullable(),
});
export type DashboardClaim = z.infer<typeof DashboardClaim>;

/** "Make Today real" phase 1 (2026-09-01): one merged, re-sorted row from
 * the receipts/shipments/claims/sync-run activity feed. `href` is
 * reserved for a future deep link — phase 1 does not populate it (see
 * docs/plans/2026-09-01-make-today-real-phase1.md), so it stays optional
 * for forward compatibility rather than a schema change later. */
export const ActivityItem = z.object({
  at: z.string(),
  kind: z.enum(['receipt', 'shipment', 'claim', 'sync']),
  label: z.string(),
  href: z.string().optional(),
});
export type ActivityItem = z.infer<typeof ActivityItem>;

/** "Make Today real" phase 2 (2026-09-01): one of the 12 UTC-week buckets
 * behind the "Cash & margin flow" card. weekStart is the Monday 00:00 UTC
 * ISO string; netProceedsCents/cogsCents/marginCents are signed integer
 * cents. cogsCoveredUnits/totalUnits let the client render "COGS covers N
 * of M units" honestly when landed cost isn't known for every unit sold
 * that week. */
export const MarginFlowWeek = z.object({
  weekStart: z.string(),
  netProceedsCents: z.number().int(),
  cogsCents: z.number().int(),
  marginCents: z.number().int(),
  cogsCoveredUnits: z.number().int(),
  totalUnits: z.number().int(),
  /** R2 item 3 (D1, docs/plans/2026-09-03-graded-kernel.md): 'settlement'
   *  only when every covered unit's landed cost came from lot lineage
   *  (lib/finance/landed-cost.ts's landedUnitCostBySku) AND every unit sold
   *  that week was covered; 'estimate' otherwise (unit-flow's weighted-
   *  average landed cost, a partial week, or a mix of both). marginGrade
   *  mirrors cogsGrade — netProceedsCents is always settlement-grade (real
   *  settlement lines), so margin's grade is exactly COGS's grade.
   *  `.default('estimate')` covers responses from an API build that
   *  predates this field — fail-closed rather than implying proof. */
  cogsGrade: FinanceGrade.default('estimate'),
  marginGrade: FinanceGrade.default('estimate'),
});
export type MarginFlowWeek = z.infer<typeof MarginFlowWeek>;

/** "Make Today real" phase 2: one rule-based Insights card row. valueCents
 * is a dollar-impact estimate for ranking (order of magnitude, not a
 * ledger figure) — null when a rule can't estimate impact, which ranks
 * last. href is an app-relative path, not a full URL. */
export const InsightItem = z.object({
  kind: z.string(),
  label: z.string(),
  valueCents: z.number().int().nullable(),
  href: z.string(),
});
export type InsightItem = z.infer<typeof InsightItem>;

export const DashboardResponse = z.object({
  /** Draft claims (the ones still needing filing), warehouse module. */
  claims: z.array(DashboardClaim).nullable(),
  /** Amazon module: latest sync run + when the hourly cron fires next. */
  sync: z
    .object({
      lastRun: z
        .object({
          status: z.enum(['running', 'ok', 'error']),
          startedAt: z.string(),
          finishedAt: z.string().nullable(),
          inventoryCount: z.number().int(),
          error: z.string().nullable(),
        })
        .nullable(),
      nextSyncAt: z.string(),
    })
    .nullable(),
  /** Orders module: the most recent mail-ingested PO (the ingest heartbeat). */
  ingest: z
    .object({
      lastPoAt: z.string().nullable(),
      lastPoVendor: z.string().nullable(),
    })
    .nullable(),
  /** Shipments module: open (shipped/receiving) shipments. */
  shipments: z
    .array(
      z.object({
        id: Uuid,
        shipmentId: z.string(),
        name: z.string().nullable(),
        status: z.string(),
        shippedAt: z.string().nullable(),
      })
    )
    .nullable(),
  /** One-line numeric position; each figure null when its module is off. */
  positions: z.object({
    fulfillableUnits: z.number().int().nullable(),
    openPoCount: z.number().int().nullable(),
    openPoCents: z.number().int().nullable(),
    recoverableCents: z.number().int().nullable(),
    /** Credited claims to date — the wound closing, in dollars. */
    recoveredCents: z.number().int().nullable(),
    /** True count — the shipments array itself is capped at 20 rows. */
    openShipmentCount: z.number().int().nullable(),
    /** Phase B3: Σ over shipments of max(0, lot-costed shortfall − matched
     *  reimbursements), floored per shipment before summing — tenant-wide,
     *  shipments module. Null when the module is off. `.default(null)`
     *  covers deploy skew (a web deploy can land before the api deploy). */
    netUnrecoveredCents: z.number().int().nullable().default(null),
    /** Total absolute unit-flow residual exposure; null when Amazon is off,
     * valuation is incomplete, or an older API has not shipped this field. */
    residualCents: z.number().int().nonnegative().nullable().default(null),
    /** Unit-flow valuation state; null when Amazon is disabled. */
    residualStatus: z.enum(['source_backfill', 'missing_cost', 'ready']).nullable().default(null),
    /** Latest dated Amazon settlement header; this is not a bank-confirmed
     * deposit and remains null when no dated settlement exists. */
    lastPayoutCents: z.number().int().nullable().default(null),
    lastPayoutCurrency: z.string().nullable().default(null),
    lastPayoutAt: z.string().nullable().default(null),
    lastPayoutStatus: z.enum(['reconciled', 'partial', 'unknown']).nullable().default(null),
    /** "Make Today real" phase 1 (2026-09-01): warehouse-lot value at cost
     * plus FBA fulfillable units at canonical landed cost (SKUs with no
     * known landed cost contribute 0, see valuedSkuCount/totalSkuCount).
     * Null only when both the warehouse and Amazon modules are off — the
     * two sources this figure draws from. `.default(null)` covers deploy
     * skew (an older API build hasn't shipped this field yet). */
    inventoryValueCents: z.number().int().nullable().default(null),
    /** Of the amazon_inventory SKU set, how many contributed a costed FBA
     * value (see inventoryValueCents) — "across N of M SKUs" captioning. */
    inventoryValuedSkuCount: z.number().int().nullable().default(null),
    inventoryTotalSkuCount: z.number().int().nullable().default(null),
    /** Σ purchase_orders effective total (total_cents, falling back to
     * subtotal_cents when total is 0) for non-deleted POs ordered in the
     * current UTC calendar month. Null when the orders module is off. */
    purchaseSpendMonthCents: z.number().int().nullable().default(null),
    /** Σ amazon_sales.item_price_cents for the current UTC calendar month,
     * excluding cancelled orders — gross product sales (order-report
     * revenue, not settlement-net; phase 2's margin instrument owns
     * settlement truth). Null when the Amazon module is off. */
    revenueMonthCents: z.number().int().nullable().default(null),
  }),
  /** "Make Today real" phase 1: most recent 8 events across receipts,
   * shipments, credit claims, and completed Amazon syncs, newest first.
   * Always an array (empty, not null) — each source contributes nothing
   * when its module is off rather than nulling the whole feed.
   * `.default([])` covers deploy skew. */
  activity: z.array(ActivityItem).default([]),
  /** "Make Today real" phase 2 (2026-09-01): 12 weekly settlement-grade
   * margin buckets for the "Cash & margin flow" card. Null when the Amazon
   * module is off (settlements/sales don't exist without it) or an older
   * API build hasn't shipped this field yet (`.default(null)` covers
   * deploy skew). Never null-with-empty-weeks — when present, `weeks`
   * always has exactly 12 zero-filled entries. */
  marginFlow: z
    .object({
      weeks: z.array(MarginFlowWeek),
    })
    .nullable()
    .default(null),
  /** "Make Today real" phase 2: rule-based Insights, ranked by dollar
   * impact and capped at 3. Null when none of the rule-owning modules
   * (amazon, warehouse) are enabled for this tenant — an empty array means
   * the modules are on but no rule currently fires ("Nothing urgent — the
   * machine is quiet" empty state). `.default(null)` covers deploy skew. */
  insights: z.array(InsightItem).nullable().default(null),
});
export type DashboardResponse = z.infer<typeof DashboardResponse>;

// ─────────────────────────────────────────────────────────
// Stranded inventory tracking (2026-08-31) — GET /api/inventory/stranded.
// Two read-only conditions computed live (no new tables): unlisted lots
// with idle stock, and PO lines billed by the vendor but never received.
// docs/plans/2026-08-31-stranded-inventory-tracking.md
// ─────────────────────────────────────────────────────────

export const StrandedInventoryUnlistedLot = z.object({
  itemNo: z.string(),
  description: z.string(),
  unitsIdle: z.number().int(),
  unitCostCents: z.number().int(),
  valueCents: z.number().int(),
  receivedAt: z.string(),
  daysIdle: z.number().int(),
});
export type StrandedInventoryUnlistedLot = z.infer<typeof StrandedInventoryUnlistedLot>;

export const StrandedInventoryUnreceivedLine = z.object({
  itemNo: z.string(),
  description: z.string(),
  qty: z.number().int(),
  unitCostCents: z.number().int(),
  valueCents: z.number().int(),
  poExternalId: z.string().nullable(),
  orderedAt: z.string(),
  daysOutstanding: z.number().int(),
});
export type StrandedInventoryUnreceivedLine = z.infer<typeof StrandedInventoryUnreceivedLine>;

export const StrandedInventoryResponse = z.object({
  unlistedLots: z.array(StrandedInventoryUnlistedLot),
  unreceivedLines: z.array(StrandedInventoryUnreceivedLine),
  totals: z.object({
    unlistedUnits: z.number().int(),
    unlistedValueCents: z.number().int(),
    unreceivedUnits: z.number().int(),
    unreceivedValueCents: z.number().int(),
  }),
});
export type StrandedInventoryResponse = z.infer<typeof StrandedInventoryResponse>;

// ─────────────────────────────────────────────────────────
// FBA shipments (M3). Lot-costed: every shipped unit traces to its vendor
// invoice + true unit cost. Module-gated on 'shipments'.
// ─────────────────────────────────────────────────────────

export const FbaShipmentStatus = z.enum(['preparing', 'shipped', 'receiving', 'closed']);
export type FbaShipmentStatus = z.infer<typeof FbaShipmentStatus>;

export const FbaShipmentSummary = z.object({
  id: Uuid,
  shipmentId: z.string(),
  name: z.string().nullable(),
  destinationFc: z.string().nullable(),
  status: FbaShipmentStatus,
  shippedAt: z.string().nullable(),
  createdAt: z.string(),
  lineCount: z.number().int(),
  totalQty: z.number().int(),
  totalReceived: z.number().int(),
  anyUnreceived: z.boolean(),
  unallocatedUnits: z.number().int(),
  /** Top 3 lines by qty desc (sellerSku asc tie-break) — enough for the
   *  collapsed list row to say what a shipment contains without expanding
   *  it (Faraz, 2026-08-24). `.default([])` covers deploy skew (web can
   *  ship before api). */
  linePreview: z
    .array(
      z.object({ sellerSku: z.string(), productName: z.string().nullable(), qty: z.number().int() })
    )
    .default([]),
});
export type FbaShipmentSummary = z.infer<typeof FbaShipmentSummary>;

export const ListFbaShipmentsResponse = z.array(FbaShipmentSummary);
export type ListFbaShipmentsResponse = z.infer<typeof ListFbaShipmentsResponse>;

export const FbaShipmentAllocation = z.object({
  qtyUnits: z.number().int(),
  unitCostCents: z.number().int(),
  poExternalId: z.string().nullable(),
});
export type FbaShipmentAllocation = z.infer<typeof FbaShipmentAllocation>;

export const FbaShipmentLineDetail = z.object({
  id: Uuid,
  sellerSku: z.string(),
  qty: z.number().int(),
  unallocatedUnits: z.number().int(),
  fbaReceivedQty: z.number().int().nullable(),
  allocations: z.array(FbaShipmentAllocation),
  landedCostCents: z.number().int(),
  /** Catalog Items MAIN image link, joined from amazon_inventory by seller
   *  SKU (Revision 3). `.default(null)` covers deploy skew. */
  imageUrl: z.string().nullable().default(null),
  /** Amazon product name from the same amazon_inventory join — shipment
   *  lines otherwise show only the seller SKU. */
  productName: z.string().nullable().default(null),
  /** ASIN from the same amazon_inventory join — powers the clickable
   *  Amazon-listing link on the product name. `.default(null)` covers
   *  deploy skew. */
  asin: z.string().nullable().default(null),
});
export type FbaShipmentLineDetail = z.infer<typeof FbaShipmentLineDetail>;

// ── Shipment cases + recovery (Phase B3, 2026-08-24) ────────────────────────
// A Seller Central case number the tenant hand-attaches to a shipment (no
// automated Amazon writes — cases are opened in Seller Central, this just
// tracks them). `recovery` is computed from amazon_reimbursements matched
// either by case_id (when a case is attached) or by seller_sku within a
// 120-day window from ship date, against the shipment's lot-costed shortfall.

export const ShipmentCaseStatus = z.enum(['open', 'resolved', 'denied']);
export type ShipmentCaseStatus = z.infer<typeof ShipmentCaseStatus>;

export const ShipmentCase = z.object({
  id: Uuid,
  caseId: z.string(),
  filedAt: z.string().nullable(),
  status: ShipmentCaseStatus,
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ShipmentCase = z.infer<typeof ShipmentCase>;

export const CreateShipmentCaseRequest = z.object({
  caseId: z.string().min(1).max(64),
  filedAt: z.string().datetime().optional(),
  notes: z.string().max(2000).optional(),
});
export type CreateShipmentCaseRequest = z.infer<typeof CreateShipmentCaseRequest>;

export const UpdateShipmentCaseRequest = z.object({
  status: ShipmentCaseStatus.optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type UpdateShipmentCaseRequest = z.infer<typeof UpdateShipmentCaseRequest>;

export const MatchedReimbursement = z.object({
  reimbursementId: z.string(),
  caseId: z.string().nullable(),
  sellerSku: z.string(),
  reason: z.string(),
  amountCents: z.number().int(),
  approvalDate: z.string(),
});
export type MatchedReimbursement = z.infer<typeof MatchedReimbursement>;

export const ShipmentRecovery = z.object({
  matchedReimbursements: z.array(MatchedReimbursement),
  reimbursedCents: z.number().int(),
  shortfallCents: z.number().int(),
  /** True when some short units have no (or only partial) lot cost —
   *  shortfallCents is a floor, not a complete figure. */
  shortfallIncomplete: z.boolean(),
  /** shortfallCents − reimbursedCents, NOT floored at 0 — the web UI floors
   *  this for display. */
  netUnrecoveredCents: z.number().int(),
});
export type ShipmentRecovery = z.infer<typeof ShipmentRecovery>;

export const FbaShipmentDetail = FbaShipmentSummary.omit({
  lineCount: true,
  totalQty: true,
  totalReceived: true,
  anyUnreceived: true,
  unallocatedUnits: true,
}).extend({
  notes: z.string().nullable(),
  lines: z.array(FbaShipmentLineDetail),
  files: z.array(
    z.object({ id: Uuid, filename: z.string(), sizeBytes: z.number().int(), createdAt: z.string() })
  ),
  /** `.default([])` covers deploy skew — a web deploy can land before the
   *  api deploy that started returning this field. */
  cases: z.array(ShipmentCase).default([]),
  /** `.nullable().default(null)` covers deploy skew the same way. */
  recovery: ShipmentRecovery.nullable().default(null),
});
export type FbaShipmentDetail = z.infer<typeof FbaShipmentDetail>;

export const CreateFbaShipmentRequest = z.object({
  shipmentId: z.string().min(4).max(40),
  name: z.string().max(200).optional(),
  destinationFc: z.string().max(20).optional(),
  notes: z.string().max(2000).optional(),
  lines: z
    .array(z.object({ sellerSku: z.string().min(1).max(60), qty: z.number().int().positive() }))
    .min(1)
    .max(500),
});
export type CreateFbaShipmentRequest = z.infer<typeof CreateFbaShipmentRequest>;

export const CreateFbaShipmentResponse = z.object({
  id: Uuid,
  totalUnallocated: z.number().int(),
});
export type CreateFbaShipmentResponse = z.infer<typeof CreateFbaShipmentResponse>;

export const RecordFbaReceivedRequest = z.object({
  lines: z
    .array(
      z.object({ sellerSku: z.string().min(1), fbaReceivedQty: z.number().int().nonnegative() })
    )
    .min(1),
});
export type RecordFbaReceivedRequest = z.infer<typeof RecordFbaReceivedRequest>;

// ─────────────────────────────────────────────────────────
// Amazon tracking (M4). Read-only SP-API pulls; module-gated on 'amazon'.
// ─────────────────────────────────────────────────────────

// ─── Amazon-retail exclusion (Faraz 2026-09-10 design: "We need to skip or
// mark if Amazon is selling or getting the BB on any item. No way we can
// compete with them.") ──────────────────────────────────────────────────
//
// Shared verdict shape (lib/sourcing/amazon-retail.ts's deriveAmazonRetail
// on the API side) used by both the Amazon inventory row below (flagged
// only — a stocked SKU is never auto-excluded, Faraz decides on stock) and
// PlanningResearchRow (excluded from buyable). Declared once here since
// api.ts loads before planning-run.ts in the package barrel (index.ts) —
// planning-run.ts imports this rather than duplicating it, same rationale
// as PlanningResearchTier's own doc comment there.
export const AmazonRetailStatus = z.enum(['amazon', 'clear', 'unknown']);
export type AmazonRetailStatus = z.infer<typeof AmazonRetailStatus>;
export const AmazonRetailVerdict = z.object({
  status: AmazonRetailStatus,
  reasons: z.array(z.string()),
});
export type AmazonRetailVerdict = z.infer<typeof AmazonRetailVerdict>;
const UNKNOWN_AMAZON_RETAIL: AmazonRetailVerdict = { status: 'unknown', reasons: [] };

export const AmazonInventoryRow = z.object({
  sellerSku: z.string(),
  fnsku: z.string().nullable(),
  asin: z.string().nullable(),
  productName: z.string().nullable(),
  fulfillable: z.number().int(),
  inboundWorking: z.number().int(),
  inboundShipped: z.number().int(),
  inboundReceiving: z.number().int(),
  reserved: z.number().int(),
  updatedAt: z.string(),
  /** Catalog Items MAIN image link (Revision 3). `.default(null)` covers
   *  deploy skew — an older API build that doesn't send this field yet. */
  imageUrl: z.string().nullable().default(null),
  /**
   * Make Today real phase 2, Instrument 3 (2026-09-01) — straight column
   * selects from amazon_inventory, the same columns
   * PriceCalibrationRow already reads (written by lib/amazon/
   * pricing-sync.ts / fees-sync.ts on the hourly sync, never a user
   * click). All `.default(null)` for deploy skew.
   */
  yourPriceCents: z.number().int().nullable().default(null),
  buyboxPriceCents: z.number().int().nullable().default(null),
  buyboxIsYours: z.boolean().nullable().default(null),
  priceCheckedAt: z.string().nullable().default(null),
  feesAtPriceCents: z.number().int().nullable().default(null),
  feesCheckedAt: z.string().nullable().default(null),
  /**
   * Observed Amazon sales velocity — lib/amazon/sales-velocity.ts's shared
   * grouped read (the SAME reader/filter PriceCalibrationRow's context
   * uses), not a second interpretation. Null means velocity is unknown
   * (no/insufficient sales in the last 30 days) — never a bare zero, same
   * convention as PriceCalibrationVelocity.velocityKnown.
   */
  unitsSold7d: z.number().int().nullable().default(null),
  unitsSold30d: z.number().int().nullable().default(null),
  /**
   * COGS column (Faraz 2026-09-07: I need a column in OPS-Amazon for
   * COGS; follow-up: prep cost too, $1.00 or $1.50 for glass) —
   * lib/amazon/inventory-cogs.ts's grouped reader.
   *
   * Faraz 2026-09-07 (one-unit-cost design): cogsUnitCents is the ONE
   * number — the same lib/finance/landed-cost.ts figure Price Calibration
   * and the Dashboard show for this SKU. cogsBreakdown gives its
   * settlement/accrued components (all null for the estimate grade, which
   * has no lots to break down); cogsGrade widened to 'settlement' (every
   * unit covered by a warehouse cost layer) / 'accrued' (some units still
   * on the flat prep accrual) / 'estimate' (no received lots at all — the
   * latest invoice line × units per listing, plus flat prep). All
   * `.default(null)`/`.default(false)` for deploy skew.
   */
  cogsBreakdown: z
    .object({
      baseCents: z.number().int().nullable(),
      poChargeCents: z.number().int().nullable(),
      warehouseCents: z.number().int().nullable(),
      prepAccrualCents: z.number().int().nullable(),
    })
    .nullable()
    .default(null),
  cogsUnitCents: z.number().int().nullable().default(null),
  cogsGrade: z.enum(['settlement', 'accrued', 'estimate']).nullable().default(null),
  /** listing_map.prep_requires_wrap for this seller SKU — the "glass"
   *  marker driving the accrual/estimate prep rate, editable via the
   *  Amazon page's wrap toggle (PATCH /api/amazon/listings/:sellerSku).
   *  This is the EFFECTIVE flag: the manual override when set, else the
   *  catalog glass hint below (glassHint === 'glass'). */
  prepRequiresWrap: z.boolean().default(false),
  /**
   * Glass hint (Faraz 2026-09-08 glass-hint design: "we discussed that,
   * right? find it from listing details") — the SP-API Catalog `attributes`
   * signal (lib/amazon/glass-hint.ts's deriveGlassHint), read from the
   * global catalog_asin_attributes table. 'unknown' when there's no ASIN or
   * no attributes row yet.
   */
  glassHint: z.enum(['glass', 'not_glass', 'unknown']).default('unknown'),
  /** Where the effective prepRequiresWrap above came from: 'manual' (a
   *  human set the listing_map flag in either direction), 'catalog' (flag
   *  unset, the glass hint is 'glass'), or 'default' (flag unset, hint not
   *  glass). */
  prepWrapSource: z.enum(['manual', 'catalog', 'default']).default('default'),
  /** Amazon-retail signal (Faraz 2026-09-10 design) — computed live from
   *  the Keepa demand probe and SP-API offers for this ASIN (see
   *  AmazonRetailVerdict's doc above). Flagged only: a stocked SKU keeps
   *  selling regardless of this status, Faraz decides whether to keep
   *  reordering it. `.default(...)` covers deploy skew. */
  amazonRetail: AmazonRetailVerdict.default(UNKNOWN_AMAZON_RETAIL),
});
export type AmazonInventoryRow = z.infer<typeof AmazonInventoryRow>;

// ─── Amazon listing prep-wrap toggle (Faraz 2026-09-07) ────────────────────
// Nothing sets listing_map.prep_requires_wrap yet (no route, no UI, zero
// flagged SKUs) — this is that one control, scoped to the Amazon page's
// COGS column.

export const SetAmazonListingPrepWrapRequest = z.object({
  prepRequiresWrap: z.boolean(),
});
export type SetAmazonListingPrepWrapRequest = z.infer<typeof SetAmazonListingPrepWrapRequest>;

export const SetAmazonListingPrepWrapResponse = z.object({
  sellerSku: z.string(),
  prepRequiresWrap: z.boolean(),
});
export type SetAmazonListingPrepWrapResponse = z.infer<typeof SetAmazonListingPrepWrapResponse>;

export const AmazonSyncRun = z.object({
  id: Uuid,
  status: z.enum(['running', 'ok', 'error']),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  inventoryCount: z.number().int(),
  shipmentsChecked: z.number().int(),
  checkinsApplied: z.number().int(),
  mappingsUpdated: z.number().int(),
  /** Phase B1 (2026-08-23). `.default(0)` covers rows written before this
   *  column existed (deploy-skew-safe). */
  salesRowsUpserted: z.number().int().default(0),
  error: z.string().nullable(),
});
export type AmazonSyncRun = z.infer<typeof AmazonSyncRun>;

export const AmazonStateResponse = z.object({
  inventory: z.array(AmazonInventoryRow),
  lastRun: AmazonSyncRun.nullable(),
});
export type AmazonStateResponse = z.infer<typeof AmazonStateResponse>;

export const AmazonSyncResponse = z.object({
  runId: Uuid,
  inventoryCount: z.number().int(),
  shipmentsChecked: z.number().int(),
  checkinsApplied: z.number().int(),
  mappingsUpdated: z.number().int(),
  salesRowsUpserted: z.number().int().default(0),
});
export type AmazonSyncResponse = z.infer<typeof AmazonSyncResponse>;

// ─────────────────────────────────────────────────────────
// Price calibration (COCKPIT-VISION.md build-order item 7, 2026-08-25; prices
// re-sourced from SP-API off Keepa the same day — Faraz's decision: "Your
// price" (yourPriceCents, our own SP-API offer) anchors verdicts/margin;
// "Buy box" (buyboxPriceCents, Amazon's featured offer) is context only,
// with buyboxIsYours marking when we hold it. Both refresh automatically on
// the hourly Amazon sync — no more user-triggered Keepa refresh). Module-
// gated on 'amazon'. Pure band-solve/verdict math lives in
// apps/api/src/lib/price-calibration/calc.ts; see that file's header doc
// for the margin-fraction and fee-model conventions this response mirrors.
// ─────────────────────────────────────────────────────────

/**
 * `no-price` — never fetched a Buy Box price yet (neutral, "No price yet").
 * `no-cost-basis` — a price exists but no invoice cost yet (band + margin
 * stay null rather than a fake zero-cost number). The other four require
 * both a price AND a cost.
 */
export const PriceCalibrationVerdict = z.enum([
  'underwater',
  'below-floor',
  'below-target',
  'optimal',
  'no-price',
  'no-cost-basis',
]);
export type PriceCalibrationVerdict = z.infer<typeof PriceCalibrationVerdict>;

/**
 * Amazon Listings Restrictions authorization state (pricing-truth arc Task
 * A, 2026-08-25) — lib/adapters/sp-api.ts's getListingsAuthorization, shared
 * by the price-calibration row badge and the planner line badge (same
 * vocabulary both surfaces, per the arc spec). `.catch('unknown')` so an
 * older deployed frontend build never hard-fails parsing a future status
 * value — same skew-safety convention as RecommendationVelocitySource.
 */
export const AmazonAuthorizationStatus = z
  .enum(['authorized', 'approval_required', 'restricted', 'unknown'])
  .catch('unknown');
export type AmazonAuthorizationStatus = z.infer<typeof AmazonAuthorizationStatus>;

/**
 * Competitor stock and clearance price (2026-10-05,
 * docs/plans/2026-10-05-competitor-stock-clearance-design.md). Keepa is back on
 * this page for competitor stock: the latest dated snapshot of each live offer
 * (amazon_competitor_snapshots), plus a clearance recommendation computed from
 * it by lib/price-calibration/clearance.ts. Read-only advice; nothing is
 * written to Amazon (hard rule 5).
 */
export const CompetitorOffer = z.object({
  sellerName: z.string().nullable(),
  isAmazon: z.boolean(),
  isFba: z.boolean(),
  /** The offer is the tenant's own (seller id matches the SP-API seller id). */
  isOurs: z.boolean(),
  /** Landed price (price plus shipping). Null when Keepa has no price. */
  landedCents: Cents.nullable(),
  /** Units in stock. Keepa refreshes stock only for the cheapest offers. */
  stock: z.number().int().nonnegative().nullable(),
  /** Minutes since Keepa last read this offer's stock. */
  stockAgeMinutes: z.number().int().nonnegative().nullable(),
  /** This offer's seller holds the Buy Box now. */
  holdsBuyBox: z.boolean(),
  /** Share of the last 30 days this seller held the Buy Box (percent). */
  sharePct30: z.number().nullable(),
});
export type CompetitorOffer = z.infer<typeof CompetitorOffer>;

export const PriceCalibrationCompetition = z.object({
  /** When the latest snapshot was taken (ISO). */
  capturedAt: z.string().datetime(),
  /** Keepa monthlySold, a floor. */
  marketMonthlySold: z.number().int().nullable(),
  buyBoxPriceCents: Cents.nullable(),
  buyBoxSellerName: z.string().nullable(),
  buyBoxIsAmazon: z.boolean(),
  /** Share of the last 30 days Amazon held the Buy Box (percent). */
  amazonSharePct30: z.number(),
  /** Share of the last 90 days Amazon was out of stock (percent). */
  amazonOosPct90: z.number().nullable(),
  /** Live offers that are not ours and price below ours. */
  cheaperSellerCount: z.number().int(),
  /** Units held by third-party FBA offers priced below ours. */
  cheaperFbaUnits: z.number().int(),
  /** The same figure on the snapshot before the latest, against our current price. */
  previousCheaperFbaUnits: z.number().int().nullable(),
  previousCapturedAt: z.string().nullable(),
  /** The tenant's newest sale is more than 3 days old, so sales evidence was not used. */
  salesDataStale: z.boolean(),
  offers: z.array(CompetitorOffer),
});
export type PriceCalibrationCompetition = z.infer<typeof PriceCalibrationCompetition>;

export const PriceCalibrationClearanceAction = z.enum([
  'hold',
  'match_tier',
  'wait_for_amazon',
  'undercut_amazon',
  'restore_buy_box',
  'exit',
  'fix_listing',
]);
export type PriceCalibrationClearanceAction = z.infer<typeof PriceCalibrationClearanceAction>;

export const PriceCalibrationClearance = z.object({
  action: PriceCalibrationClearanceAction,
  priceCents: Cents.nullable(),
  reason: z.string(),
  /** The fee floor: the price where Amazon's payout is zero. */
  floorCents: Cents.nullable(),
  /** Per unit at priceCents, after referral and FBA fee. Can be negative. */
  payoutCents: z.number().int().nullable(),
  /** payoutCents times our units. Can be negative. */
  cashBackCents: z.number().int().nullable(),
  /** Cost times our units: what we paid, reported but never the floor. */
  costAtStakeCents: z.number().int().nullable(),
  /** Third-party FBA units more than 1% cheaper than priceCents. */
  unitsAheadAtPrice: z.number().int().nullable(),
  /** unitsAheadAtPrice divided by market sales per day, one decimal. */
  daysAhead: z.number().nullable(),
  horizonDays: z.number().int(),
});
export type PriceCalibrationClearance = z.infer<typeof PriceCalibrationClearance>;

export const PriceCalibrationRow = z.object({
  sellerSku: z.string(),
  asin: z.string().nullable(),
  productName: z.string().nullable(),
  imageUrl: z.string().nullable(),
  /**
   * LISTING-level cost basis feeding margin/verdict math (multipack fix,
   * 2026-08-31): the canonical landed cost when one exists (`landedUnitCostCents`
   * below, already unitsPerListing-adjusted), else the latest supplier
   * invoice unit cost x listing_map.unitsPerListing (see `costBasis` for
   * which). Never the bare per-vendor-unit invoice cost when unitsPerListing
   * > 1 — a 2-pack's true cost is 2x one vendor unit's invoice price. Null =
   * no cost basis at all (no invoice, no mapping).
   */
  costCents: Cents.nullable(),
  /**
   * Provenance for `costCents` above (multipack fix, 2026-08-31):
   * 'landed' = the canonical landed cost (context.landedUnitCostCents);
   * 'supplier_x_pack' = latest supplier invoice unit cost x unitsPerListing
   * (no costed lot yet); null = no cost basis at all. `.default(null)`
   * covers responses from an API build that predates this field.
   */
  costBasis: z.enum(['landed', 'supplier_x_pack']).nullable().default(null),
  /**
   * R2 item 3 (D1, docs/plans/2026-09-03-graded-kernel.md) — grade/source
   * for the SAME cost basis `costBasis`/`costCents` describe, in the ONE
   * kernel grade vocabulary (FinanceGrade) shared with Finance/Fee
   * Center/the dashboard margin chart. `costBasisSource: 'lot_landed'`
   * means `lib/finance/landed-cost.ts`'s landedUnitCostBySku found received
   * lots for this SKU (base cost + landed-cost charge allocations — true
   * settlement-grade); `'supplier_pack'` means the supplier-invoice x
   * unitsPerListing fallback was used (estimate-grade); null means no cost
   * basis at all. `.default(null)` covers responses from an API build that
   * predates these fields.
   */
  costBasisGrade: FinanceGrade.nullable().default(null),
  costBasisSource: z.enum(['lot_landed', 'supplier_pack']).nullable().default(null),
  /**
   * listing_map.unitsPerListing for this seller SKU (1 when unmapped) —
   * lets the client render "supplier cost x N per listing" for the
   * supplier_x_pack case. `.default(1)` covers pre-field responses.
   */
  unitsPerListing: z.number().int().positive().default(1),
  /** Our own current SP-API offer price (amazon_inventory.your_price_cents)
   *  — the verdict/margin ANCHOR. Null = no active offer / not yet synced. */
  yourPriceCents: Cents.nullable(),
  /** Amazon's featured offer (New Buy Box). Context only, never used in
   *  verdict/margin math. Null when suppressed or never synced. */
  buyboxPriceCents: Cents.nullable(),
  /** true when we hold the Buy Box (SP-API belongsToRequester). Null when
   *  there's no Buy Box to evaluate (mirrors buyboxPriceCents's null cases). */
  buyboxIsYours: z.boolean().nullable(),
  /** Both price columns above are refreshed together on the hourly Amazon
   *  sync — one freshness stamp for both, not per-field. */
  priceCheckedAt: z.string().nullable(),
  feesAtCurrentPriceCents: Cents.nullable(),
  minPriceCents: Cents.nullable(),
  targetPriceCents: Cents.nullable(),
  /** Can be negative (underwater) — NOT the nonnegative Cents primitive. */
  currentMarginCents: z.number().int().nullable(),
  /** Fraction, can be negative — same convention as tenants.min_margin_pct. */
  currentMarginPct: z.number().nullable(),
  verdict: PriceCalibrationVerdict,
  /** amazon_inventory fulfillable + inboundWorking + inboundShipped +
   *  inboundReceiving — all units committed to FBA for this SKU. */
  unitsOnHand: z.number().int().nonnegative(),
  /** feeComponents() decomposition (lib/price-calibration/calc.ts), null
   *  only when there's no cost basis. Lets the client compute fees at ANY
   *  price — e.g. the min/target sell-through projection — exactly as the
   *  server would: Math.round(referralPct * price + fixedFeeCents). */
  referralPct: z.number().min(0).max(1).nullable(),
  fixedFeeCents: Cents.nullable(),
  /**
   * 'amazon' when feesAtCurrentPriceCents/the band came from a real SP-API
   * My Fees Estimate (amazon_inventory.fees_at_price_cents, fresh); 'no-fee-
   * data' when there's no cost basis at all (fees fields are already null
   * in that case). `.default('estimated')` covers responses from an older
   * API build that predates this field — additive, old web ignores it.
   */
  feesSource: z.enum(['amazon', 'estimated']).default('estimated'),
  /** Listings Restrictions authorization for this ASIN (condition NEW).
   *  `.default('unknown')` covers rows from before this field existed. */
  authorization: AmazonAuthorizationStatus.default('unknown'),
  /**
   * Competition intelligence (pricing-truth arc Task D, 2026-08-25) —
   * amazon_inventory.offer_count/lowest_competitor_cents/
   * offers_within_5pct/offers_checked_at, written by the competition sync
   * leg (lib/amazon/competition-sync.ts, 6h staleness gate). `.default()`
   * on each covers rows from an API build that predates these fields —
   * additive, old web ignores them.
   */
  offerCount: z.number().int().nonnegative().nullable().default(null),
  lowestCompetitorCents: Cents.nullable().default(null),
  offersWithin5Pct: z.number().int().nonnegative().nullable().default(null),
  offersCheckedAt: z.string().nullable().default(null),
  /**
   * Freshness stamp for feesAtCurrentPriceCents (amazon_inventory.fees_
   * checked_at) — SEPARATE from priceCheckedAt (existing-inventory velocity/
   * scenario plan, 2026-08-26/31 build, Task 2). `.default(null)` covers
   * responses from an API build that predates this field.
   */
  feesCheckedAt: z.string().nullable().default(null),
  /**
   * Exact scoped inventory buckets (2026-08-26/31 existing-inventory
   * velocity/scenario plan, Task 2 — apps/api/src/lib/price-calibration/
   * context.ts). `unitsOnHand` above is UNCHANGED and stays populated
   * during deploy skew; these are ADDITIVE fields the web moves to once
   * both sides deploy. `.default(0)` covers responses from an older API
   * build that predates these fields — an old field, not "no units."
   *
   *   sellableNowUnits = fulfillable only (design doc default scope).
   *   allOwnedUnits    = fulfillable + reserved + the three inbound
   *                       buckets + warehouseReadyUnits (unambiguously
   *                       mapped only — see context.ts's module doc for
   *                       exactly which mapping decision "unambiguous"
   *                       means here).
   */
  sellableNowUnits: z.number().int().nonnegative().default(0),
  allOwnedUnits: z.number().int().nonnegative().default(0),
  reservedUnits: z.number().int().nonnegative().default(0),
  inboundWorkingUnits: z.number().int().nonnegative().default(0),
  inboundShippedUnits: z.number().int().nonnegative().default(0),
  inboundReceivingUnits: z.number().int().nonnegative().default(0),
  warehouseReadyUnits: z.number().int().nonnegative().default(0),
  /**
   * Canonical landed Amazon-listing-unit cost (weighted-average vendor
   * cost x unitsPerListing, applied exactly once — apps/api/src/lib/
   * amazon/unit-flow.ts). This is the SAME value `costCents` above uses
   * when `costBasis === 'landed'` — surfaced separately here because the
   * scenario/velocity math (scenarioRowInputForRow) always requires the
   * canonical figure and never accepts the supplier_x_pack fallback. Null =
   * no costed lot for this SKU yet.
   */
  landedUnitCostCents: Cents.nullable().default(null),
  /**
   * Observed non-cancelled Amazon (AFN/null-channel) sales velocity —
   * NEVER the Planning page's shipment/check-in proxy. `velocityKnown:
   * false` (with `recentUnitsPerDay: null`) means insufficient/absent
   * 30-day sales history, reported as unknown rather than a misleading
   * zero-days/infinite-profit projection.
   */
  unitsSold7d: z.number().int().nonnegative().default(0),
  unitsSold30d: z.number().int().nonnegative().default(0),
  unitsSold90d: z.number().int().nonnegative().default(0),
  unitsSoldPrevious30d: z.number().int().nonnegative().default(0),
  recentUnitsPerDay: z.number().nonnegative().nullable().default(null),
  velocityKnown: z.boolean().default(false),
  /**
   * Sales rank on Price Calibration, step one (2026-09-03,
   * docs/plans/2026-09-03-sales-rank-on-calibration.md) — latest observed
   * Amazon sales rank (Catalog Items includedData=salesRanks), a demand
   * signal shown next to velocity. Feeds NO calculation (margin, verdict,
   * scenario, runway are untouched). ASIN-level, not per seller SKU.
   * `rank`/`title` are the primary rank: the latest display_group row, or
   * (when the ASIN has none) the latest classification row with the lowest
   * rank number. `rank7dAgo`/`rank30dAgo` are the latest same-key
   * observation on/before today-7/-30, null when there is no such row yet.
   * `subRanks` are the latest classification rows, ascending rank. Null =
   * no observation yet for this SKU's ASIN — also what an API build that
   * predates this field produces. `.nullable().default(null)` covers that
   * deploy skew.
   */
  salesRank: z
    .object({
      rank: z.number().int().positive(),
      title: z.string(),
      observedAt: z.string(),
      rank7dAgo: z.number().int().positive().nullable(),
      rank30dAgo: z.number().int().positive().nullable(),
      subRanks: z.array(z.object({ title: z.string(), rank: z.number().int().positive() })),
    })
    .nullable()
    .default(null),
  /**
   * Competitor stock from the latest Keepa snapshot (at most 3 days old).
   * Null when the ASIN has no recent snapshot. `.nullable().default(null)`
   * covers responses from an API build that predates this field.
   */
  competition: PriceCalibrationCompetition.nullable().default(null),
  /**
   * Clearance recommendation. Null when there is no recent snapshot or when we
   * already hold the Buy Box. `.nullable().default(null)` as above.
   */
  clearance: PriceCalibrationClearance.nullable().default(null),
});
export type PriceCalibrationRow = z.infer<typeof PriceCalibrationRow>;

export const PriceCalibrationResponse = z.object({
  rows: z.array(PriceCalibrationRow),
  /**
   * Vendor units sitting at the warehouse whose prep-line mapping is
   * ambiguous or absent (existing-inventory velocity/scenario plan,
   * 2026-08-26/31, Task 2) — excluded from every row's warehouseReadyUnits/
   * allOwnedUnits rather than silently dropped; "called out" per the design
   * doc's shared-mapping requirement. `.default(0)` covers responses from
   * an API build that predates this field.
   */
  excludedWarehouseVendorUnits: z.number().int().nonnegative().default(0),
  /**
   * True only for an API build that actually ran the Task 2 calibration
   * context read (apps/api/src/lib/price-calibration/context.ts) — i.e.
   * every row's sellableNowUnits/allOwnedUnits/velocity fields are real
   * numbers, not schema defaults standing in for an absent field.
   * `.default(false)` on an OLDER API response (deploy skew: new web +
   * old API) lets the page tell "an empty portfolio" apart from "this
   * server hasn't shipped inventory/velocity data yet" — without it every
   * row's `.default(0)` bucket reads as a real zero and the scenario
   * totals would silently zero out instead of staying honestly unknown.
   */
  inventoryContextAvailable: z.boolean().default(false),
});
export type PriceCalibrationResponse = z.infer<typeof PriceCalibrationResponse>;

/**
 * POST /api/price-calibration/competitors/refresh (owner/operator). Runs one
 * batch of up to 5 ASINs; the web calls it until `remaining` is 0. `force`
 * lowers the staleness bar from 20 hours to 1 hour.
 */
export const CompetitorRefreshRequest = z.object({
  force: z.boolean().optional(),
});
export type CompetitorRefreshRequest = z.infer<typeof CompetitorRefreshRequest>;

export const CompetitorRefreshResponse = z.object({
  refreshed: z.number().int().nonnegative(),
  /** Stale targets left after this batch. Forced to 0 when nothing was refreshed. */
  remaining: z.number().int().nonnegative(),
  /** Keepa tokens left after the call, null when unknown. */
  tokensLeft: z.number().int().nullable(),
});
export type CompetitorRefreshResponse = z.infer<typeof CompetitorRefreshResponse>;

// POST /refresh + its Keepa-budget stop-reason schema (PriceCalibrationRefreshStopReason
// / RefreshPriceCalibrationResponse) were removed 2026-08-25 — prices are now
// re-sourced from the tenant's own SP-API connection on the hourly Amazon
// sync, not a user-triggered Keepa lookup. See lib/amazon/pricing-sync.ts.

// ─────────────────────────────────────────────────────────
// Price calibration — pricing strategies, Ask AI (2026-09-03,
// docs/plans/2026-09-03-pricing-strategies-design.md §3) —
// POST /api/price-calibration/strategy. One-shot LLM call (hard rule 4 — no
// loop): the server re-reads the calibration rows for the requested SKUs,
// asks the tenant's own OpenRouter model (BYOK) to propose prices for one
// objective, and validates the result before responding. Rule-based
// strategies (§1) are pure web-side functions — this is the on-demand AI
// path only.
// ─────────────────────────────────────────────────────────

/** Mirrors apps/web/src/lib/profit-scenario.ts's `InventoryScope` — which
 *  rows-in-view bucket ("Sellable now" vs "All owned") the request was made
 *  against. Duplicated here (schemas can't import from apps/web) rather than
 *  re-derived; keep the two literal unions in sync by hand. */
export const InventoryScope = z.enum(['sellable-now', 'all-owned']);
export type InventoryScope = z.infer<typeof InventoryScope>;

export const PriceStrategyObjective = z.enum([
  'max-profit',
  'sell-fast',
  'cut-losses',
  'sensible',
  'open',
]);
export type PriceStrategyObjective = z.infer<typeof PriceStrategyObjective>;

export const PriceStrategyRequest = z.object({
  objective: PriceStrategyObjective,
  scope: InventoryScope,
  sellerSkus: z.array(z.string().trim().min(1)).min(1).max(500),
});
export type PriceStrategyRequest = z.infer<typeof PriceStrategyRequest>;

export const PriceStrategyProposal = z.object({
  sellerSku: z.string(),
  priceCents: z.number().int().positive(),
  reason: z.string(),
  /** true when the model's proposed price was floored up to the
   *  server-computed price (§3/§4) rather than used as-is — normally
   *  break-even, or the §1 exit-rule price (I1/I2, 2026-09-03 code review)
   *  on objective `cut-losses`, or `sensible`/`open` on an `underwater`
   *  row, where an exit never prices above break-even but can still be
   *  floored below the model's number. */
  clamped: z.boolean(),
});
export type PriceStrategyProposal = z.infer<typeof PriceStrategyProposal>;

export const PriceStrategyResponse = z.object({
  proposals: z.array(PriceStrategyProposal),
  rationale: z.string(),
  /** Count of proposals the server dropped: unknown SKU; non-positive/NaN
   *  price; or a row the prompt was told to never price (verdict
   *  `no-price`/`no-cost-basis`, or zero units in the requested scope) —
   *  never silently discarded, always surfaced. */
  dropped: z.number().int().nonnegative(),
  model: z.string(),
  promptTokens: z.number().int().nonnegative(),
  completionTokens: z.number().int().nonnegative(),
});
export type PriceStrategyResponse = z.infer<typeof PriceStrategyResponse>;

/** Warehouse-facing shipment view (Line-2 A4): labels for the floor, NO money. */
export const WarehouseFacility = z.object({ id: Uuid, name: z.string() });
export type WarehouseFacility = z.infer<typeof WarehouseFacility>;

export const WarehouseShipmentLine = z.object({
  id: Uuid,
  sellerSku: z.string().trim().min(1),
  listingUnits: z.number().int().positive().safe(),
  unitsPerListingSnapshot: z.number().int().positive().safe().nullable(),
});
export type WarehouseShipmentLine = z.infer<typeof WarehouseShipmentLine>;

export const WarehouseShipment = z.object({
  id: Uuid,
  warehouseId: Uuid.nullable().default(null),
  shipmentId: z.string(),
  name: z.string().nullable(),
  destinationFc: z.string().nullable(),
  status: FbaShipmentStatus,
  createdAt: z.string(),
  files: z.array(z.object({ id: Uuid, filename: z.string() })),
  lines: z.array(WarehouseShipmentLine).default([]),
});
export type WarehouseShipment = z.infer<typeof WarehouseShipment>;

export const ListWarehouseShipmentsResponse = z.array(WarehouseShipment);
export type ListWarehouseShipmentsResponse = z.infer<typeof ListWarehouseShipmentsResponse>;

export const WarehouseShipmentBoxStatus = z.enum(['open', 'finalized']);
export type WarehouseShipmentBoxStatus = z.infer<typeof WarehouseShipmentBoxStatus>;

export const WarehouseShipmentBoxContent = z.object({
  id: Uuid,
  shipmentLineId: Uuid,
  sellerSku: z.string().trim().min(1),
  listingUnits: z.number().int().positive().safe(),
  unitsPerListingSnapshot: z.number().int().positive().safe().nullable(),
});
export type WarehouseShipmentBoxContent = z.infer<typeof WarehouseShipmentBoxContent>;

export const WarehouseShipmentBox = z.object({
  id: Uuid,
  shipmentId: Uuid,
  warehouseId: Uuid,
  boxNumber: z.string().trim().min(1),
  status: WarehouseShipmentBoxStatus,
  finalizedAt: Iso.nullable(),
  contents: z.array(WarehouseShipmentBoxContent),
});
export type WarehouseShipmentBox = z.infer<typeof WarehouseShipmentBox>;

export const ListWarehouseShipmentBoxesResponse = z.array(WarehouseShipmentBox);
export type ListWarehouseShipmentBoxesResponse = z.infer<typeof ListWarehouseShipmentBoxesResponse>;

export const CreateWarehouseShipmentBoxRequest = z.object({
  boxNumber: z.string().trim().min(1),
});
export type CreateWarehouseShipmentBoxRequest = z.infer<typeof CreateWarehouseShipmentBoxRequest>;

export const ReplaceWarehouseShipmentBoxContentsRequest = z
  .object({
    contents: z.array(
      z.object({
        shipmentLineId: Uuid,
        listingUnits: z.number().int().positive().safe(),
      })
    ),
  })
  .refine(
    (value) =>
      new Set(value.contents.map((content) => content.shipmentLineId)).size ===
      value.contents.length,
    { message: 'contents must contain each shipment line at most once', path: ['contents'] }
  );
export type ReplaceWarehouseShipmentBoxContentsRequest = z.infer<
  typeof ReplaceWarehouseShipmentBoxContentsRequest
>;

// ─────────────────────────────────────────────────────────
// Receipt-owned warehouse prep (M3 foundation, 2026-08-29). These payloads
// are intentionally money-free: warehouse actors need physical identity and
// quantities, never acquisition cost, revenue, COGS, or seller margin.
// ─────────────────────────────────────────────────────────

/** Human-safe reference copied into Seller Central shipment/plan names. */
export const PrepReference = z
  .string()
  .regex(/^PB-[0-9A-HJKMNP-TV-Z]{6}$/, 'invalid prep reference');
export type PrepReference = z.infer<typeof PrepReference>;

export const PrepStatus = z.enum([
  'ready_to_prep',
  'in_prep',
  'awaiting_amazon',
  'label_ready',
  'dispatched',
]);
export type PrepStatus = z.infer<typeof PrepStatus>;

/** Human-safe box reference assigned by the server within a prep batch. */
export const PrepBoxReference = z
  .string()
  .regex(/^PB-[0-9A-HJKMNP-TV-Z]{6}-B[0-9]{2,}$/, 'invalid prep box reference');
export type PrepBoxReference = z.infer<typeof PrepBoxReference>;

/** A reservation of vendor units from one receipt-owned prep line. */
export const WarehousePrepBoxContent = z
  .object({
    id: Uuid,
    prepLineId: Uuid,
    lotId: Uuid,
    sellerSku: z.string().trim().min(1),
    vendorUnits: z.number().int().positive(),
    listingUnits: z.number().int().positive(),
    unitsPerListing: z.number().int().positive(),
  })
  .strict();
export type WarehousePrepBoxContent = z.infer<typeof WarehousePrepBoxContent>;

export const WarehousePrepBox = z
  .object({
    id: Uuid,
    prepBatchId: Uuid,
    warehouseId: Uuid,
    boxNumber: PrepBoxReference,
    status: WarehouseShipmentBoxStatus,
    version: z.number().int().positive(),
    contents: z.array(WarehousePrepBoxContent),
    finalization: z
      .object({
        cartonId: Uuid,
        cartonVersion: z.number().int().positive(),
        measurements: z
          .object({
            lengthUm: z.number().int().positive().safe(),
            widthUm: z.number().int().positive().safe(),
            heightUm: z.number().int().positive().safe(),
            weightMg: z.number().int().positive().safe(),
          })
          .strict(),
        packingStatus: z.enum(['confirmed', 'provisional']),
        finalizedAt: Iso,
      })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if ((value.status === 'finalized') !== (value.finalization !== null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'finalization must be populated exactly when status is finalized',
        path: ['finalization'],
      });
    }
  });
export type WarehousePrepBox = z.infer<typeof WarehousePrepBox>;

export const ListWarehousePrepBoxesResponse = z.array(WarehousePrepBox);
export type ListWarehousePrepBoxesResponse = z.infer<typeof ListWarehousePrepBoxesResponse>;

/** An empty command body; the server allocates the next safe box reference. */
export const CreateWarehousePrepBoxRequest = z.object({}).strict();
export type CreateWarehousePrepBoxRequest = z.infer<typeof CreateWarehousePrepBoxRequest>;

export const ReplaceWarehousePrepBoxContentsRequest = z
  .object({
    expectedVersion: z.number().int().positive(),
    contents: z
      .array(
        z
          .object({
            prepLineId: Uuid,
            lotId: Uuid,
            vendorUnits: z.number().int().positive(),
            listingUnits: z.number().int().positive(),
          })
          .strict()
      )
      .max(500),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.contents.map((content) => content.prepLineId)).size === value.contents.length,
    { message: 'contents must contain each prep line at most once', path: ['contents'] }
  );
export type ReplaceWarehousePrepBoxContentsRequest = z.infer<
  typeof ReplaceWarehousePrepBoxContentsRequest
>;

export const FinalizeWarehousePrepBoxRequest = z
  .object({
    expectedVersion: z.number().int().positive(),
    cartonId: Uuid,
    measurements: z
      .object({
        lengthUm: z.number().int().positive().safe(),
        widthUm: z.number().int().positive().safe(),
        heightUm: z.number().int().positive().safe(),
        weightMg: z.number().int().positive().safe(),
      })
      .strict(),
  })
  .strict();
export type FinalizeWarehousePrepBoxRequest = z.infer<typeof FinalizeWarehousePrepBoxRequest>;

export const ReopenWarehousePrepBoxRequest = z
  .object({
    expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(1),
  })
  .strict();
export type ReopenWarehousePrepBoxRequest = z.infer<typeof ReopenWarehousePrepBoxRequest>;

/** Reviewed product/listing identity safe for warehouse-facing responses. */
export const WarehousePrepProductIdentity = z
  .object({
    id: Uuid,
    vendorItemNo: z.string().trim().min(1).nullable(),
    productLabel: z.string().trim().min(1),
    sellerSku: z.string().trim().min(1).nullable(),
    asin: z.string().trim().min(1).nullable(),
    fnsku: z.string().trim().min(1).nullable(),
    unitsPerListing: z.number().int().positive().nullable(),
  })
  .strict();
export type WarehousePrepProductIdentity = z.infer<typeof WarehousePrepProductIdentity>;

export const WarehousePrepMappingStatus = z.enum(['mapped', 'mapping_required']);
export type WarehousePrepMappingStatus = z.infer<typeof WarehousePrepMappingStatus>;

export const WarehousePrepLine = z
  .object({
    id: Uuid,
    receiptLineId: Uuid,
    lotId: Uuid,
    product: WarehousePrepProductIdentity,
    vendorUnits: z.number().int().positive(),
    preparedVendorUnits: z.number().int().nonnegative(),
    remainingVendorUnits: z.number().int().nonnegative(),
    listingUnits: z.number().int().nonnegative().nullable(),
    mappingStatus: WarehousePrepMappingStatus,
  })
  .strict()
  .refine((value) => value.preparedVendorUnits <= value.vendorUnits, {
    message: 'preparedVendorUnits cannot exceed vendorUnits',
    path: ['preparedVendorUnits'],
  })
  .refine((value) => value.remainingVendorUnits === value.vendorUnits - value.preparedVendorUnits, {
    message: 'remainingVendorUnits must equal vendorUnits - preparedVendorUnits',
    path: ['remainingVendorUnits'],
  });
export type WarehousePrepLine = z.infer<typeof WarehousePrepLine>;

export const WarehousePrepActor = z
  .object({
    id: Uuid,
    displayName: z.string().trim().min(1),
  })
  .strict();
export type WarehousePrepActor = z.infer<typeof WarehousePrepActor>;

export const WarehousePrepBatchSummary = z
  .object({
    id: Uuid,
    receiptId: Uuid,
    warehouseId: Uuid,
    reference: PrepReference,
    status: PrepStatus,
    version: z.number().int().positive().default(1),
    facility: WarehouseFacility,
    lineCount: z.number().int().nonnegative(),
    remainingVendorUnits: z.number().int().nonnegative(),
    claimedBy: WarehousePrepActor.nullable(),
    createdAt: Iso,
    updatedAt: Iso,
  })
  .strict();
export type WarehousePrepBatchSummary = z.infer<typeof WarehousePrepBatchSummary>;

export const WarehousePrepBatchDetail = WarehousePrepBatchSummary.extend({
  lines: z.array(WarehousePrepLine),
}).strict();
export type WarehousePrepBatchDetail = z.infer<typeof WarehousePrepBatchDetail>;

export const ListWarehousePrepBatchesResponse = z.array(WarehousePrepBatchSummary);
export type ListWarehousePrepBatchesResponse = z.infer<typeof ListWarehousePrepBatchesResponse>;

export const WarehousePrepBatchResponse = WarehousePrepBatchDetail;
export type WarehousePrepBatchResponse = z.infer<typeof WarehousePrepBatchResponse>;

/** Claiming is an optimistic-concurrency command; an empty body is valid. */
export const ClaimWarehousePrepBatchRequest = z
  .object({ expectedVersion: z.number().int().positive().optional() })
  .strict();
export type ClaimWarehousePrepBatchRequest = z.infer<typeof ClaimWarehousePrepBatchRequest>;

/** Explicitly reviewed Amazon identity used to unblock a prep line. */
export const MapWarehousePrepLineRequest = z
  .object({
    sellerSku: z.string().trim().min(1).nullable(),
    asin: z.string().trim().min(1).nullable(),
    fnsku: z.string().trim().min(1).nullable(),
    unitsPerListing: z.number().int().positive().nullable(),
    expectedVersion: z.number().int().positive(),
  })
  .strict()
  .refine((value) => value.sellerSku !== null, 'sellerSku is required for a reviewed mapping')
  .refine(
    (value) => value.unitsPerListing !== null,
    'unitsPerListing is required for a reviewed mapping'
  );
export type MapWarehousePrepLineRequest = z.infer<typeof MapWarehousePrepLineRequest>;

export const WarehousePrepErrorCode = z.enum([
  'PREP_BATCH_NOT_FOUND',
  'PREP_LINE_NOT_FOUND',
  'PREP_FACILITY_REQUIRED',
  'PREP_FACILITY_NOT_FOUND',
  'PREP_FACILITY_FORBIDDEN',
  'PREP_INVALID_TRANSITION',
  'PREP_ALREADY_CLAIMED',
  'PREP_MAPPING_REQUIRED',
  'PREP_CONFLICT',
  'PREP_BOX_NOT_FOUND',
  'PREP_BOX_CONFLICT',
  'PREP_BOX_FINALIZED',
  'PREP_CAPACITY_EXCEEDED',
  'PREP_CONTENT_INVALID',
  'PREP_CARTON_INVALID',
  'PREP_FINALIZATION_INVALID',
  'PREP_REOPEN_REASON_REQUIRED',
  'PREP_BOX_NOT_FINALIZED',
  'PREP_REOPEN_BLOCKED',
  'PREP_DISPATCH_CONFLICT',
  'PREP_DISPATCH_INVALID',
  'PREP_DISPATCH_ALREADY_EXISTS',
]);
export type WarehousePrepErrorCode = z.infer<typeof WarehousePrepErrorCode>;

export const WarehousePrepError = z
  .object({
    code: WarehousePrepErrorCode,
    message: z.string().min(1),
  })
  .strict();
export type WarehousePrepError = z.infer<typeof WarehousePrepError>;

export const WarehousePrepErrorResponse = z.object({ error: WarehousePrepError }).strict();
export type WarehousePrepErrorResponse = z.infer<typeof WarehousePrepErrorResponse>;

// ─────────────────────────────────────────────────────────
// Read-only inbound reconciliation (Task 7). These are presenter contracts:
// opaque tokens deliberately keep Amazon and internal database identifiers out
// of the warehouse-facing workflow.
// ─────────────────────────────────────────────────────────

export const InboundReconciliationReasonCode = z.enum([
  'aggregate_mismatch',
  'carton_count_mismatch',
  'unmatchable_carton',
  'physical_mismatch',
  'duplicate_amazon_box',
  'already_linked',
  'conflicting_link',
  'facility_mismatch',
  'invalid_shipment_lines',
  'stale_local_version',
  'incomplete_pagination',
  'unknown_unit',
  'prep_reference_missing_or_mistyped',
  'multiple_plans',
  'multiple_bijections',
  'missing_carton_evidence',
  'multiple_split_placements',
  'invalid_local_evidence',
]);
export type InboundReconciliationReasonCode = z.infer<typeof InboundReconciliationReasonCode>;

const InboundReconciliationToken = z.string().regex(/^recon_[a-z0-9]+$/, 'invalid token');

const InboundReconciliationPlacementPresentation = z
  .object({
    prepBoxReference: PrepBoxReference,
    shipmentOrdinal: z.number().int().positive(),
    cartonOrdinal: z.number().int().positive(),
    destination: z.discriminatedUnion('status', [
      z.object({ status: z.literal('assigned'), ordinal: z.number().int().positive() }).strict(),
      z.object({ status: z.literal('pending') }).strict(),
    ]),
    products: z.array(
      z
        .object({
          sellerSku: z.string().min(1),
          amazonUnits: z.number().int().positive(),
        })
        .strict()
    ),
    dimensionsUm: z
      .tuple([z.string().regex(/^\d+$/), z.string().regex(/^\d+$/), z.string().regex(/^\d+$/)])
      .nullable(),
    weightMg: z.string().regex(/^\d+$/).nullable(),
  })
  .strict();

export const InboundReconciliationProposal = z
  .object({
    status: z.enum(['automatic', 'review_required', 'blocked']),
    choiceToken: InboundReconciliationToken,
    fingerprints: z
      .object({
        local: InboundReconciliationToken,
        amazon: InboundReconciliationToken,
        combined: InboundReconciliationToken,
      })
      .strict(),
    reasonCodes: z.array(InboundReconciliationReasonCode),
    placements: z.array(
      z
        .object({
          shipmentToken: InboundReconciliationToken,
          localBoxToken: InboundReconciliationToken,
          amazonBoxToken: InboundReconciliationToken,
          presentation: InboundReconciliationPlacementPresentation,
        })
        .strict()
    ),
  })
  .strict();
export type InboundReconciliationProposal = z.infer<typeof InboundReconciliationProposal>;

/** POST /prep-batches/:id/amazon-handoff/confirm — the choice itself stays server-side. */
export const ConfirmPrepAmazonHandoffRequest = z
  .object({
    expectedVersion: z.number().int().positive(),
    choiceToken: InboundReconciliationToken,
  })
  .strict();
export type ConfirmPrepAmazonHandoffRequest = z.infer<typeof ConfirmPrepAmazonHandoffRequest>;

/** Retry every eligible label, or one human-visible prep-box reference. */
export const RetryPrepAmazonLabelsRequest = z
  .object({
    pageType: z.string().trim().min(1).max(100),
    boxReference: PrepBoxReference.optional(),
  })
  .strict();
export type RetryPrepAmazonLabelsRequest = z.infer<typeof RetryPrepAmazonLabelsRequest>;

/** Human-only path component for a batch-bound Amazon label download. */
export const PrepAmazonLabelReferenceParams = z.object({ boxReference: PrepBoxReference }).strict();
export type PrepAmazonLabelReferenceParams = z.infer<typeof PrepAmazonLabelReferenceParams>;

const PrepAmazonHandoffLabel = z
  .object({
    state: z.enum(['not_requested', 'pending', 'fetching', 'ready', 'retryable_error']),
    retryable: z.boolean(),
    /** True only when the server has a printable Amazon box identity; the ID itself never leaves it. */
    fetchable: z.boolean(),
    /** A package-only link can be checked again for a later printable box identity. */
    needsIdentityCheck: z.boolean(),
    downloadReady: z.boolean(),
    error: z.string().nullable(),
  })
  .strict();

/** Human-only reconciliation evidence. Internal Amazon/database identity never crosses this boundary. */
export const PrepAmazonHandoffProposal = z
  .object({
    status: z.enum(['automatic', 'review_required', 'blocked']),
    choiceToken: InboundReconciliationToken,
    reasons: z.array(z.string().min(1)),
    placements: z.array(
      z
        .object({
          prepBoxReference: PrepBoxReference,
          shipmentOrdinal: z.number().int().positive(),
          cartonOrdinal: z.number().int().positive(),
          destinationSummary: z.string().min(1),
          destinationOrdinal: z.number().int().positive(),
          products: z.array(
            z
              .object({
                sellerSku: z.string().min(1),
                amazonUnits: z.number().int().positive(),
              })
              .strict()
          ),
          dimensionsUm: z
            .tuple([
              z.string().regex(/^\d+$/),
              z.string().regex(/^\d+$/),
              z.string().regex(/^\d+$/),
            ])
            .nullable(),
          weightMg: z.string().regex(/^\d+$/).nullable(),
        })
        .strict()
    ),
  })
  .strict();
export type PrepAmazonHandoffProposal = z.infer<typeof PrepAmazonHandoffProposal>;

export const PrepAmazonHandoffResponse = z
  .object({
    prepId: PrepReference,
    statusSummary: z.string().min(1),
    destinationSummary: z.string().nullable(),
    blocker: z.string().nullable(),
    reconciliation: z
      .object({
        state: z.enum(['awaiting_sync', 'needs_confirmation', 'linked', 'error']),
        version: z.number().int().positive().nullable(),
        proposal: PrepAmazonHandoffProposal.nullable(),
      })
      .strict(),
    products: z.array(
      z
        .object({
          productLabel: z.string().min(1),
          sellerSku: z.string().nullable(),
          vendorUnits: z.number().int().positive(),
          listingUnits: z.number().int().nonnegative().nullable(),
        })
        .strict()
    ),
    boxes: z.array(
      z
        .object({
          reference: PrepBoxReference,
          status: WarehouseShipmentBoxStatus,
          products: z.array(
            z
              .object({ sellerSku: z.string().min(1), listingUnits: z.number().int().positive() })
              .strict()
          ),
          label: PrepAmazonHandoffLabel,
        })
        .strict()
    ),
  })
  .strict();
export type PrepAmazonHandoffResponse = z.infer<typeof PrepAmazonHandoffResponse>;

// Short aliases keep the shared vocabulary convenient for API consumers.
export const PrepProductIdentity = WarehousePrepProductIdentity;
export type PrepProductIdentity = z.infer<typeof PrepProductIdentity>;
export const PrepLine = WarehousePrepLine;
export type PrepLine = z.infer<typeof PrepLine>;
export const PrepBatchSummary = WarehousePrepBatchSummary;
export type PrepBatchSummary = z.infer<typeof PrepBatchSummary>;
export const PrepBatchDetail = WarehousePrepBatchDetail;
export type PrepBatchDetail = z.infer<typeof PrepBatchDetail>;
export const ClaimPrepBatchRequest = ClaimWarehousePrepBatchRequest;
export type ClaimPrepBatchRequest = z.infer<typeof ClaimPrepBatchRequest>;
export const MapPrepLineRequest = MapWarehousePrepLineRequest;
export type MapPrepLineRequest = z.infer<typeof MapPrepLineRequest>;
export const PrepErrorCode = WarehousePrepErrorCode;
export type PrepErrorCode = z.infer<typeof PrepErrorCode>;
export const PrepError = WarehousePrepError;
export type PrepError = z.infer<typeof PrepError>;

// ─────────────────────────────────────────────────────────
// Order planning (2026-08-24) — reorder recommendations (computed, not
// stored) + the VA-curated plan itself (persisted). Module-gated on
// 'amazon' for recommendations; plans/lines are reachable by tenant-side
// roles or a 'va' role granted the 'planning' permission (middleware/auth.ts).
// ─────────────────────────────────────────────────────────

export const OrderPlanStatus = z.enum(['draft', 'ordered', 'archived']);
export type OrderPlanStatus = z.infer<typeof OrderPlanStatus>;

export const OrderPlanLineSource = z.enum([
  'reorder_rec',
  'manual',
  'sourcing_rec',
  // Order-import endpoint (Task 6, 2026-09-21 design) — a line created by
  // POST /plans/import from a supplier order file. See migration 0113.
  'supplier_order',
]);
export type OrderPlanLineSource = z.infer<typeof OrderPlanLineSource>;

export const OrderPlanLineStatus = z.enum(['proposed', 'approved', 'rejected']);
export type OrderPlanLineStatus = z.infer<typeof OrderPlanLineStatus>;

/**
 * Legacy 4-token wire verdict — KEPT UNCHANGED (pricing-truth arc Task A,
 * 2026-08-25). The currently-deployed web UI's bundled zod schema only
 * accepts these 4 tokens; it isn't redeployed until Task B ships, so the
 * `verdict` field on the wire must keep emitting values from this set
 * (down-mapped from the richer OrderPlanLineVerdictTier below — see
 * lib/order-planning/verdict.ts's legacyVerdictFromTier). Do not add values
 * here without confirming old web is gone.
 */
export const OrderPlanLineVerdict = z.enum(['ok', 'thin', 'underwater', 'unknown']);
export type OrderPlanLineVerdict = z.infer<typeof OrderPlanLineVerdict>;

/**
 * Rich 5-tier verdict, aligned with PriceCalibrationVerdict's core 4 tiers
 * (underwater/below-floor/below-target/optimal) plus 'unknown' (planner's
 * single catch-all for "no anchor / no cost / fees pending", vs.
 * calibration's split no-price/no-cost-basis — planner doesn't need that
 * split since research is one-shot, not a standing table). Task B/C's math
 * card reads this field instead of the legacy `verdict`.
 */
export const OrderPlanLineVerdictTier = z.enum([
  'underwater',
  'below-floor',
  'below-target',
  'optimal',
  'unknown',
]);
export type OrderPlanLineVerdictTier = z.infer<typeof OrderPlanLineVerdictTier>;

/** Migration-free read shim: a stored research row written before this arc
 *  has no verdictTier at all — derive one from the legacy `verdict` it DOES
 *  have. 'thin' is ambiguous (covers both below-floor and below-target in
 *  the old 4-token enum) — 'below-target' is the conservative middle
 *  reading (a floor breach would usually also show up as an explicit note
 *  on those older rows). */
const LEGACY_VERDICT_TO_TIER: Record<OrderPlanLineVerdict, OrderPlanLineVerdictTier> = {
  ok: 'optimal',
  thin: 'below-target',
  underwater: 'underwater',
  unknown: 'unknown',
};

export const OrderPlanLineTrend = z.object({
  pctChange: z.number(),
  /** Whole days between the first and last history points used.
   *  `.default(90)` covers rows written before this field existed
   *  (deploy-skew-safe — old rows implied a ~90d window). */
  spanDays: z.number().int().nonnegative().default(90),
  direction: z.enum(['rising', 'flat', 'declining']),
});
export type OrderPlanLineTrend = z.infer<typeof OrderPlanLineTrend>;

export const OrderPlanLineHistoryPoint = z.object({
  /** Whole days since the Unix epoch (compact series storage). */
  t: z.number().int(),
  priceCents: z.number().int(),
});
export type OrderPlanLineHistoryPoint = z.infer<typeof OrderPlanLineHistoryPoint>;

/** Top of the anchor hierarchy that produced anchorPriceCents.
 *
 *  REVERSED (Faraz, 2026-09-06): the original arc spec said buy box never
 *  anchors. Verified on live staging data that this was wrong two ways: (1)
 *  a reorder priced itself off our own $11.57 listing while the Buy Box sat
 *  at $9.52 (21.5% below) and we weren't winning it — the planner
 *  recommended 96 units at a price we don't actually transact at; (2) a
 *  sourcing candidate has no `intended`/`listed` price at all (we don't
 *  list it yet), so every one of 83 researched candidates came back with a
 *  null anchor — the acquisition half of the tool produced nothing. Faraz's
 *  call: always anchor on the Buy Box price when one is known, falling back
 *  to intended/listed only when there is no Buy Box anywhere.
 *
 *  EXTENDED (Faraz, 2026-09-06, same day): 'lowest_offer' added — "if BB is
 *  suppressed, use the lowest offer as base but make sure to count shipping
 *  if any." On the first full sourcing run, 23 researched products had live
 *  offers (1-11) but no featured/Buy-Box offer (a suppressed box) and came
 *  back with a null anchor — three of them clear the margin floor when
 *  re-priced at their lowest offer, invisible under the old rule. Ranks
 *  strictly below the Buy Box (a suppressed box still means "we don't know
 *  the real clearing price," so a lowest offer is the best available
 *  substitute, not a replacement) and above intended/listed (a real,
 *  independently-observed competitor price beats our own unverified
 *  numbers). See OrderPlanLineResearch's `lowestOffer` field.
 *
 *  EXTENDED (Faraz, 2026-09-11 anchor-spread design): 'lowest_fba_offer'
 *  added — a Buy Box more than ANCHOR_SPREAD_FRAC (15%) above the lowest
 *  live FBA (not-ours) competitor offer is a rotating-box artifact, not the
 *  real clearing price ("not sure how it is getting the prices" — a listing
 *  priced at $24.91 while a $13.25 FBA offer sat unconsidered). Computed by
 *  lib/order-planning/anchor.ts's pickAnchor; ranks alongside 'buy_box' at
 *  the TOP of the hierarchy (it only ever replaces a 'buy_box' anchor, never
 *  competes with lowest_offer/intended/listed). See OrderPlanLineAnchorSpread. */
export const OrderPlanLineAnchorSource = z.enum([
  'buy_box',
  'lowest_fba_offer',
  'lowest_offer',
  'intended',
  'listed',
  /** Keepa's observed Amazon price history (2026-09-20 zero-price defect
   *  fix) — the v2 provider-queues merge path (merge-provider-evidence.ts)
   *  never re-runs the SP-API buy-box/lowest-offer/intended/listed
   *  hierarchy above; when it has no anchor on file at all and fresh Keepa
   *  evidence lands, that's the only price it actually knows. None of the
   *  other five values honestly describe that provenance (each implies
   *  SP-API-shaped data — a buy box, a structured offer breakdown, the
   *  tenant's own listed price — this pipeline never has). */
  'keepa_price',
]);
export type OrderPlanLineAnchorSource = z.infer<typeof OrderPlanLineAnchorSource>;

/** The Buy-Box-vs-lowest-FBA-offer spread behind an anchorSource of
 *  'lowest_fba_offer' (2026-09-11 anchor-spread design) — pct is
 *  round((buyBoxCents / lowestFbaCents − 1) × 100), e.g. 88 for $24.91 vs
 *  $13.25. Set exactly when anchorSource is 'lowest_fba_offer'; null
 *  otherwise, including for research stored before this field existed. */
export const OrderPlanLineAnchorSpread = z.object({
  buyBoxCents: z.number().int(),
  lowestFbaCents: z.number().int(),
  pct: z.number(),
});
export type OrderPlanLineAnchorSpread = z.infer<typeof OrderPlanLineAnchorSpread>;

/** The lowest-landed-competitor-offer breakdown behind an anchorSource of
 *  'lowest_offer' (2026-09-06) — landedCents is ALREADY listingCents +
 *  shippingCents (never the bare listing price) and already excludes our
 *  own offer (adapters/sp-api.ts's CompetitionRow.lowestCompetitorOffer).
 *  listingCents/shippingCents/isFulfilledByAmazon are null when the fallback
 *  came from a cached amazon_inventory row (landed total only, no per-offer
 *  breakdown stored) rather than a live getItemOffersBatch call. `.default
 *  (null)` covers research JSON stored before this field existed — a
 *  required field in a zod union bit us earlier this week, so this whole
 *  object (not just its inner fields) is nullable/defaulted. */
export const OrderPlanLineLowestOffer = z.object({
  listingCents: z.number().int().nullable(),
  shippingCents: z.number().int().nullable(),
  landedCents: z.number().int(),
  offerCount: z.number().int().nonnegative().nullable(),
  isFulfilledByAmazon: z.boolean().nullable(),
});
export type OrderPlanLineLowestOffer = z.infer<typeof OrderPlanLineLowestOffer>;

export const OrderPlanLineFeesBreakdown = z.object({
  referral: z.number().int().nullable(),
  fixed: z.number().int().nullable(),
  /** Set only when feesSource is 'keepa_estimate' (2026-09-25 Task C) — the
   *  ISO timestamp Keepa reported for its fbaFees.pickAndPackFee snapshot,
   *  so the UI can label an estimated figure with its own staleness rather
   *  than implying it's as fresh as a real Amazon fee quote. Null/absent
   *  for every other feesSource, including rows stored before this field
   *  existed. */
  keepaFeeLastUpdate: z.string().nullable().optional(),
  /** Set only when feesSource is 'keepa_estimate' (MINOR review fix,
   *  2026-09-25) — the resolved referral rate (0-1) and the anchor price it
   *  was rated at, so a later merge pass can re-rate the referral component
   *  if the price moves instead of the estimate silently going stale. See
   *  merge-provider-evidence.ts's carry-forward re-rate block. Null/absent
   *  for every other feesSource, or for a 'keepa_estimate' row stored
   *  before this fix (that shape is dropped entirely on the next merge —
   *  it cannot be re-rated honestly without these parts). */
  referralFeePct: z.number().min(0).max(1).nullable().optional(),
  priceCentsAtEstimate: z.number().int().nullable().optional(),
});
export type OrderPlanLineFeesBreakdown = z.infer<typeof OrderPlanLineFeesBreakdown>;

/** Cost provenance for the math card's "cost" line (latest-unit-cost.ts's
 *  latestUnitCostDetailByItemNo). Both fields independently nullable — a
 *  cost basis can exist with no external_id, or (in principle) no
 *  ordered_at. */
export const OrderPlanLineCostSource = z.object({
  poExternalId: z.string().nullable(),
  invoiceDate: z.string().nullable(),
});
export type OrderPlanLineCostSource = z.infer<typeof OrderPlanLineCostSource>;

export const OrderPlanLineThresholds = z.object({
  minFrac: z.number(),
  targetFrac: z.number(),
});
export type OrderPlanLineThresholds = z.infer<typeof OrderPlanLineThresholds>;

/**
 * pricing-truth arc Task A (2026-08-25) rework — see verdict.ts + research.ts
 * module docs. New provenance fields are everything the math card renders:
 * anchorSource/anchorPriceCents (the resolved verdict anchor — the Buy Box
 * price when one is known, else the intended price on the plan line, else
 * the tenant's own SP-API listed price; buy-box-first per Faraz's
 * 2026-09-06 reversal of the original "buy box never anchors" call — see
 * OrderPlanLineAnchorSource's doc comment), feesSource/feesBreakdown
 * (real Amazon fees vs. the estimated-fallback placeholder, plus the
 * referral/fixed split), costSource (which invoice the cost came from),
 * authorization (Listings Restrictions), thresholds (the tenant margin
 * rules used for this verdict), trendWarning (Keepa decline annotation,
 * tier-independent), and verdictTier (the rich 5-tier verdict — see
 * OrderPlanLineVerdictTier doc).
 *
 * Every new field is nullable/defaulted so a row stored BEFORE this rework
 * (missing all of them) still parses — deploy-skew safety, same convention
 * `history`/`trend` already used. verdictTier additionally back-derives
 * from the legacy `verdict` via .transform() when absent (the "migration-
 * free read shim" the arc spec calls for), rather than a blind default.
 */
export const OrderPlanLineResearch = z
  .object({
    checkedAt: z.string(),
    asin: z.string().nullable(),
    amazonPriceCents: z.number().int().nullable(),
    estFeesCents: z.number().int().nullable(),
    estMarginCents: z.number().int().nullable(),
    marginPct: z.number().nullable(),
    verdict: OrderPlanLineVerdict,
    risks: z.array(z.string()).default([]),
    note: z.string().nullable(),
    /** ≤90d, downsampled to ≤30 points. `.default([])` covers deploy skew. */
    history: z.array(OrderPlanLineHistoryPoint).default([]),
    trend: OrderPlanLineTrend.nullable().default(null),
    verdictTier: OrderPlanLineVerdictTier.optional(),
    anchorSource: OrderPlanLineAnchorSource.nullable().default(null),
    anchorPriceCents: z.number().int().nullable().default(null),
    /** amazon_inventory.buybox_is_yours, carried through when anchorSource
     *  is 'buy_box' and the ASIN is one we stock — context for "we own the
     *  box" vs "we'd have to take it from someone", never used to CHOOSE
     *  the anchor (Faraz: anchor on the Buy Box unconditionally either way).
     *  null when unknown (no inventory row, or the anchor came from the
     *  live-offers path, which doesn't report this). `.default(null)`
     *  covers research JSON stored before this field existed. */
    buyboxIsYours: z.boolean().nullable().default(null),
    /** Set exactly when anchorSource is 'lowest_offer' — the fallback that
     *  actually produced anchorPriceCents (2026-09-06). Null otherwise,
     *  including for every other anchorSource and for research stored
     *  before this field existed. See OrderPlanLineLowestOffer's doc. */
    lowestOffer: OrderPlanLineLowestOffer.nullable().default(null),
    /** Set exactly when anchorSource is 'lowest_fba_offer' (2026-09-11
     *  anchor-spread design) — null otherwise, including for research
     *  stored before this field existed. See OrderPlanLineAnchorSpread. */
    anchorSpread: OrderPlanLineAnchorSpread.nullable().default(null),
    /** 'keepa_estimate' (2026-09-25 Task C) is a THIRD source, distinct from
     *  the legacy 'estimated' fallback: it's Keepa's own reported
     *  fbaFees.pickAndPackFee + a referral-rate estimate, computed only
     *  when Amazon's My Fees Estimate terminally failed with
     *  'no_catalog_dimensions'. Never fed into the verdict as a normal
     *  Amazon-equivalent figure — see verdict-policy notes on
     *  merge-provider-evidence.ts's `feesCentsForVerdict`: it may confirm a
     *  reject (underwater/below-floor) but never a Buy. */
    feesSource: z
      .enum(['amazon', 'estimated', 'keepa_estimate', 'floor_estimate', 'manual'])
      .nullable()
      .default(null),
    feesBreakdown: OrderPlanLineFeesBreakdown.nullable().default(null),
    costSource: OrderPlanLineCostSource.nullable().default(null),
    /** Multipack fix (2026-08-31, downstream twin of price-calibration's
     *  dd5533e) — the LISTING-level cost actually fed into the margin
     *  verdict: expectedUnitCostCents (a vendor-unit cost) x
     *  unitsPerListing. `.default(null)` covers research JSON stored
     *  before this field existed (deploy skew) — the web falls back to the
     *  raw order_plan_lines.expectedUnitCostCents for those rows, same
     *  display as before this fix. */
    costCents: z.number().int().nullable().default(null),
    /** 'supplier_x_pack' when costCents above was computed as vendor-unit
     *  cost x unitsPerListing — the only cost basis this path uses (no
     *  landed-cost concept here, unlike PriceCalibrationRow.costBasis's
     *  richer 'landed' | 'supplier_x_pack'). null when no cost is on file
     *  at all. */
    costBasis: z.enum(['supplier_x_pack']).nullable().default(null),
    /** The resolved listing_map.unitsPerListing used above (1 when the
     *  mapping is missing/unset) — only meaningful alongside costBasis. */
    unitsPerListing: z.number().int().nullable().default(null),
    authorization: AmazonAuthorizationStatus.nullable().default(null),
    thresholds: OrderPlanLineThresholds.nullable().default(null),
    trendWarning: z.boolean().default(false),
    /** Competitive-price economics per listing, before assembled-order freight.
     * Optional so previously stored research remains readable. */
    supplierListSource: z
      .object({
        rowId: Uuid,
        sheetId: Uuid,
        supplierId: Uuid,
        parsedAt: Iso.nullable(),
        listUnitCostCents: z.number().int().nullable(),
        casePack: z.number().int().positive().nullable(),
        orderIncrement: z.number().int().positive().nullable(),
        basis: z.literal('supplier_list_per_vendor_unit'),
      })
      .nullable()
      .optional(),
    marketSource: z
      .object({
        basis: z.enum(['amazon_inventory', 'amazon_live_offers']),
        checkedAt: Iso.nullable(),
        offersWithin5Pct: z.number().int().nonnegative().nullable(),
        offerCount: z.number().int().nonnegative().nullable(),
      })
      .nullable()
      .optional(),
    marketCheck: z
      .object({
        marketPriceCents: z.number().int(),
        liveFeePerListingCents: z.number().int().nullable(),
        feesSource: z.literal('amazon').nullable(),
        unitsPerListing: z.number().int().positive(),
        prepPerListingCents: z.number().int().nullable(),
        costScenarios: z.array(
          z.object({
            discountPct: z.number(),
            vendorUnitCostCents: z.number().int().nullable(),
            profitBeforeFreightPerListingCents: z.number().int().nullable(),
            marginBeforeFreightPct: z.number().nullable(),
          })
        ),
      })
      .nullable()
      .optional(),
  })
  .transform((r) => ({
    ...r,
    verdictTier: r.verdictTier ?? LEGACY_VERDICT_TO_TIER[r.verdict],
  }));
export type OrderPlanLineResearch = z.infer<typeof OrderPlanLineResearch>;

export const OrderPlanLine = z.object({
  id: Uuid,
  planId: Uuid,
  source: OrderPlanLineSource,
  sellerSku: z.string().nullable(),
  asin: z.string().nullable(),
  upc: z.string().nullable(),
  itemNo: z.string().nullable(),
  description: z.string(),
  qtyPlanned: z.number().int(),
  expectedUnitCostCents: z.number().int().nullable(),
  /** pricing-truth arc Task A (2026-08-25) — top of the anchor hierarchy;
   *  null = no override, verdict falls back to the tenant's SP-API listed
   *  price. `.default(null)` covers rows from before this field existed. */
  intendedPriceCents: z.number().int().nullable().default(null),
  currentSellingPriceCents: z.number().int().nullable().optional(),
  currentPriceCheckedAt: Iso.nullable().optional(),
  status: OrderPlanLineStatus,
  research: OrderPlanLineResearch.nullable(),
  buying: PlanningBuyingEvidence.nullable().default(null),
  supplierId: Uuid.nullable().default(null),
  createdAt: Iso,
  updatedAt: Iso,
  /** amazon_inventory.image_url joined by ASIN (2026-09-05) — the letter
   *  avatar fallback in the UI otherwise renders even when a real product
   *  photo is on file. `.default(null)` covers historical payloads and any
   *  other producer that doesn't resolve it. */
  imageUrl: z.string().nullable().default(null),
  /** Per-SKU inventory hold (2026-09-10 design doc, migration 0100). Set by
   *  publish-run.ts when this line's sellerSku/itemNo matched an
   *  `existing_inventory_estimate` lineHold — the line was still written,
   *  but is held individually rather than the whole plan. `.default(null)`
   *  covers rows written before this field existed. */
  holdReason: z.string().nullable().default(null),
  holdDetail: z
    .object({
      units: z.number().int().nonnegative(),
      sellerSku: z.string().optional(),
      vendorItemNo: z.string().optional(),
    })
    .nullable()
    .default(null),
  /** Sales rank/velocity (Faraz 2026-09-16: "Please add sales rank/sales
   *  into planning table too") — batch-loaded from the tenant's newest
   *  order_planning_research_results row for this line's resolved ASIN
   *  (line.asin ?? research.asin, uppercased), same source the Research
   *  page reads (see PlanningResearchRow's rank/velocity fields). Null when
   *  the line has no resolved ASIN, or no research row matches it. */
  demand: z
    .object({
      salesRank: z.number().int().nullable(),
      monthlySales: z.number().nullable(),
      velocityTier: PlanningVelocityTier,
      velocitySource: z.enum(['badge', 'rank_drops']).nullable(),
    })
    .nullable()
    .default(null),
  /** Pack-quantity design follow-up (2026-09-16, I4): the live
   *  sourcing_pack_overrides value for this line's resolved ASIN (already
   *  loaded by GET /plans/:id to apply it) — surfaced directly so the
   *  Planning table's Pack field can show `packOverride ?? research?.
   *  unitsPerListing ?? null` and never look empty on a research-null or
   *  cost-null line, even though `research.unitsPerListing` alone already
   *  reflects it for a normal (research-and-cost-known) line. `.default
   *  (null)` covers rows from before this field existed and non-draft
   *  plans (GET never loads overrides for those). */
  packOverride: z.number().int().positive().nullable().default(null),
  /** listing_map.unitsPerListing for this line's mapped seller SKU, when it
   *  DIFFERS from packOverride (I8) — the Pack field shows a "Stock
   *  mapping: N" note so a stocked SKU's own pack mapping is never silently
   *  shadowed by an override without the user seeing both numbers. null
   *  when there's no sellerSku, no listing_map row, or the two already
   *  agree. `.default(null)` covers rows from before this field existed. */
  stockMappingUnitsPerListing: z.number().int().positive().nullable().default(null),
  /** Manual sellable override (2026-09-17 design): the live
   *  sourcing_sellable_overrides row for this line's resolved ASIN
   *  (line.asin ?? research.asin), same live-lookup-by-ASIN convention as
   *  `packOverride` above. `.default(null)` covers rows from before this
   *  field existed and a line with no resolved ASIN. */
  sellableOverride: z
    .object({ note: z.string().nullable(), updatedAt: Iso })
    .nullable()
    .default(null),
});
export type OrderPlanLine = z.infer<typeof OrderPlanLine>;

export const OrderPlan = z.object({
  id: Uuid,
  name: z.string(),
  status: OrderPlanStatus,
  notes: z.string().nullable(),
  createdAt: Iso,
  updatedAt: Iso,
  /** Present on the single-plan detail fetch; omitted from the list view. */
  lines: z.array(OrderPlanLine).optional(),
  /** Count of the plan's (non-deleted — lines are hard-deleted, so this is
   *  just a row count) lines. Present on the plans-list fetch (planning
   *  page's picker, Phase A item 1) so the UI can show "N items" without an
   *  extra per-plan detail fetch; omitted from the single-plan detail fetch,
   *  where `lines.length` already gives the same number. */
  lineCount: z.number().int().nonnegative().optional(),
});
export type OrderPlan = z.infer<typeof OrderPlan>;

export const ListOrderPlansResponse = z.array(OrderPlan);
export type ListOrderPlansResponse = z.infer<typeof ListOrderPlansResponse>;

export const CreateOrderPlanRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  notes: z.string().max(2000).optional(),
});
export type CreateOrderPlanRequest = z.infer<typeof CreateOrderPlanRequest>;

export const CreateOrderPlanResponse = z.object({ id: Uuid });
export type CreateOrderPlanResponse = z.infer<typeof CreateOrderPlanResponse>;

// draft -> ordered -> archived, forward-only (credit-claims-style CAS).
export const UpdateOrderPlanRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  notes: z.string().max(2000).nullable().optional(),
  status: OrderPlanStatus.optional(),
});
export type UpdateOrderPlanRequest = z.infer<typeof UpdateOrderPlanRequest>;

export const CreateOrderPlanLineRequest = z.object({
  researchCandidateId: Uuid.optional(),
  intendedPriceCents: z.number().int().positive().optional(),
  source: OrderPlanLineSource.default('manual'),
  sellerSku: z.string().max(60).optional(),
  asin: z.string().max(10).optional(),
  upc: z.string().max(20).optional(),
  itemNo: z.string().max(40).optional(),
  description: z.string().min(1).max(500),
  qtyPlanned: z.number().int().positive(),
  expectedUnitCostCents: z.number().int().nonnegative().optional(),
});
export type CreateOrderPlanLineRequest = z.infer<typeof CreateOrderPlanLineRequest>;

export const CreateOrderPlanLineResponse = z.object({ id: Uuid });
export type CreateOrderPlanLineResponse = z.infer<typeof CreateOrderPlanLineResponse>;

export const UpdateOrderPlanLineRequest = z.object({
  qtyPlanned: z.number().int().positive().optional(),
  expectedUnitCostCents: z.number().int().nonnegative().nullable().optional(),
  /** pricing-truth arc Task A — validated positive when set; null clears
   *  the override (same optional/nullable shape as expectedUnitCostCents). */
  intendedPriceCents: z.number().int().positive().nullable().optional(),
  status: OrderPlanLineStatus.optional(),
});
export type UpdateOrderPlanLineRequest = z.infer<typeof UpdateOrderPlanLineRequest>;

/** One step of the research orchestration's progress trail (pricing-truth
 *  arc Task A) — price → fees → authorization → verdict. True streaming is
 *  out of scope; this is a single response the UI renders as an
 *  already-completed stepper (Task B). */
export const OrderPlanLineResearchStep = z.object({
  step: z.enum(['price', 'fees', 'authorization', 'verdict']),
  ok: z.boolean(),
  ms: z.number().int().nonnegative(),
  /** Set on a failed 'price' step only, when the failure was specifically
   *  Keepa running out of tokens (Phase A.1, 2026-08-31
   *  planning-page-usability design, "Keepa token honesty") — lets the web
   *  show a distinct "Keepa out of tokens — refills ~1/min" label instead
   *  of the generic step-failure copy. Optional/absent for every other
   *  failure reason and for older stored responses (n/a — steps are never
   *  persisted, but keeps the shape consistent with the rest of this file's
   *  additive-field convention). */
  code: z.enum(['keepa_no_tokens']).optional(),
});
export type OrderPlanLineResearchStep = z.infer<typeof OrderPlanLineResearchStep>;

export const ResearchOrderPlanLineResponse = z.object({
  line: OrderPlanLine,
  /** `.default([])` covers an older API build's response shape during
   *  deploy skew — additive field, old web ignores it entirely. */
  steps: z.array(OrderPlanLineResearchStep).default([]),
  /** Best-effort Keepa token-bucket estimate for the key research just
   *  used, AFTER this run (Phase A.1). Prefers the live response's
   *  tokensLeft; falls back to the ledger-extrapolation estimate when the
   *  live call didn't happen/report one. null when unknown — including
   *  every BYOK-tenant run, since the shared ledger only tracks
   *  platform-key spends (see keepa/token-bucket.ts's module doc).
   *  `.default(null)` covers deploy skew (an older API build's response). */
  keepaTokensLeft: z.number().int().nullable().default(null),
});
export type ResearchOrderPlanLineResponse = z.infer<typeof ResearchOrderPlanLineResponse>;

/** GET /api/order-planning/keepa-budget (Phase A.1) — a no-spend readout of
 *  the platform Keepa key's estimated bucket level, so the web can show
 *  "~N Keepa tokens" near "Check margin" (and disable it proactively) even
 *  before a research click. Pure ledger-extrapolation, same estimate
 *  research responses fall back to — never calls Keepa. null for a
 *  BYOK-configured tenant (unknown budget) or a tenant with no Keepa key
 *  configured at all. */
export const OrderPlanKeepaBudgetResponse = z.object({
  keepaTokensLeft: z.number().int().nullable(),
});
export type OrderPlanKeepaBudgetResponse = z.infer<typeof OrderPlanKeepaBudgetResponse>;

/** GET /api/order-planning/product-search?q= row (Phase A item 4, planning
 *  page usability design 2026-08-31; extended 2026-09-16 to also match
 *  saved Research by ASIN/UPC) — an as-you-type match against the tenant's
 *  own amazon_inventory AND its exact-match researched candidates, for
 *  AddLineForm's manual-add autocomplete. `sellerSku` is nullable because a
 *  research-only match (researched, never stocked) has none. No `upc` here:
 *  amazon_inventory doesn't carry one (Phase B's separate upc-lookup
 *  endpoint fills that from Catalog Items/Keepa instead). */
export const ProductSearchResult = z.object({
  sellerSku: z.string().nullable(),
  asin: z.string().nullable(),
  productName: z.string().nullable(),
  /** Set when this row has saved Research (either an inventory row whose
   *  ASIN was researched, or a research-only match) — POST .../lines
   *  attaches the saved cost/price/research evidence when this is sent
   *  back as researchCandidateId. */
  researchCandidateId: Uuid.nullable(),
});
export type ProductSearchResult = z.infer<typeof ProductSearchResult>;

export const ProductSearchResponse = z.array(ProductSearchResult);
export type ProductSearchResponse = z.infer<typeof ProductSearchResponse>;

export const ReorderRecommendation = z.enum([
  'reorder_now',
  'watch',
  'ok',
  'overstocked',
  'excluded',
  'unknown',
]);
export type ReorderRecommendation = z.infer<typeof ReorderRecommendation>;

/** GET /api/order-planning/recommendations row. Computed on the fly — never
 *  stored. `velocitySource` labels which signal produced unitsPerDay: real
 *  sales (Phase B1, once a tenant has ≥14d of amazon_sales history) or the
 *  check-in replenishment proxy (everyone before that, or in the meantime).
 *  `typicalOrderQty` is the median historical PO line quantity for the
 *  mapped vendor item — null when there's no PO history to derive it from.
 *  `atWarehouse` is open lot units on hand at the warehouse for the mapped
 *  vendor item (Line-2 adjacent fix A) — supply not yet shipped to FBA but
 *  already owned; `.default(0)` is skew-safe for callers on an older build.
 *  `noReorderReason` is carried through from listing_map whenever
 *  `recommendation === 'excluded'`; null otherwise. */
// Review fix 7: `.catch('checkin_proxy')` so an older deployed frontend
// build never hard-fails parsing a velocitySource value it doesn't know
// about yet (a future source added after that frontend shipped) — it falls
// back to the safe, most-conservative label instead of throwing.
export const RecommendationVelocitySource = z
  .enum(['sales', 'checkin_proxy'])
  .catch('checkin_proxy');
export type RecommendationVelocitySource = z.infer<typeof RecommendationVelocitySource>;

export const OrderPlanRecommendation = z.object({
  sellerSku: z.string(),
  asin: z.string().nullable(),
  upc: z.string().nullable(),
  imageUrl: z.string().nullable(),
  productName: z.string().nullable(),
  stock: z.number().int(),
  inbound: z.number().int(),
  atWarehouse: z.number().int().default(0),
  unitsPerDay: z.number(),
  daysOfCover: z.number().nullable(),
  recommendation: ReorderRecommendation,
  suggestedQty: z.number().int().nullable(),
  typicalOrderQty: z.number().int().nullable(),
  expectedUnitCostCents: z.number().int().nullable(),
  velocitySource: RecommendationVelocitySource,
  noReorderReason: z.string().nullable(),
});
export type OrderPlanRecommendation = z.infer<typeof OrderPlanRecommendation>;

export const ListOrderPlanRecommendationsResponse = z.array(OrderPlanRecommendation);
export type ListOrderPlanRecommendationsResponse = z.infer<
  typeof ListOrderPlanRecommendationsResponse
>;

// ─── Reorder exclusions (listing_map.no_reorder) ───────────────────────────

export const SetReorderExclusionRequest = z.object({
  noReorder: z.boolean(),
  reason: z.string().max(500).optional(),
});
export type SetReorderExclusionRequest = z.infer<typeof SetReorderExclusionRequest>;

export const SetReorderExclusionResponse = z.object({
  sellerSku: z.string(),
  noReorder: z.boolean(),
  noReorderReason: z.string().nullable(),
});
export type SetReorderExclusionResponse = z.infer<typeof SetReorderExclusionResponse>;

// ─── Pack override (sourcing_pack_overrides, 2026-09-08 design) ────────────
// Faraz: "There should be way in UI to solve these kind of listings. May be
// a text box to enter right pack quantity and solve it permanently."
// PUT upserts, DELETE removes — owner/operator only (routes/order-
// planning.ts). candidate-inputs.ts applies the override ahead of the
// detected pack for the NEXT run; it never recomputes an already-published
// run's economics.

export const PutPackOverrideRequest = z.object({
  vendorUnitsPerListing: z.number().int().min(1).max(999),
  note: z.string().max(500).optional(),
});
export type PutPackOverrideRequest = z.infer<typeof PutPackOverrideRequest>;

export const PutPackOverrideResponse = z.object({
  asin: z.string(),
  vendorUnitsPerListing: z.number().int(),
  note: z.string().nullable(),
});
export type PutPackOverrideResponse = z.infer<typeof PutPackOverrideResponse>;

export const DeletePackOverrideResponse = z.object({
  asin: z.string(),
});
export type DeletePackOverrideResponse = z.infer<typeof DeletePackOverrideResponse>;

// ─── Verified fee override (sourcing_fee_overrides, 2026-09-30 design) ─────
// Faraz: some products have an Amazon price but Amazon refuses a fee quote
// (no catalog dimensions); the VA looks the fee up in Amazon's Revenue
// Calculator and types it in. A verified fee is treated as REAL (full
// verdict), but a genuine Amazon fee quote always wins over it.
// referralFeePct is a FRACTION (0.15 = 15%).

export const PutFeeOverrideRequest = z.object({
  fulfillmentFeeCents: z.number().int().min(1).max(100000),
  referralFeePct: z.number().min(0).max(0.5),
  note: z.string().max(500).optional(),
});
export type PutFeeOverrideRequest = z.infer<typeof PutFeeOverrideRequest>;

export const PutFeeOverrideResponse = z.object({
  asin: z.string(),
  fulfillmentFeeCents: z.number().int(),
  referralFeePct: z.number(),
  note: z.string().nullable(),
});
export type PutFeeOverrideResponse = z.infer<typeof PutFeeOverrideResponse>;

export const DeleteFeeOverrideResponse = z.object({
  asin: z.string(),
});
export type DeleteFeeOverrideResponse = z.infer<typeof DeleteFeeOverrideResponse>;

// ─── Manual sellable override (sourcing_sellable_overrides, 2026-09-17
// design) ────────────────────────────────────────────────────────────────
// Faraz: "I need a way to mark a product sellable in planning or research
// page. Once I check manually, I should be able to remove the
// restrictions." PUT upserts, DELETE removes — owner/operator only
// (routes/order-planning.ts), same shape as the pack-override routes
// above. A row's existence means sellable; research-verdict.ts applies it
// whatever Amazon's own stored gating status is.

export const PutSellableOverrideRequest = z.object({
  note: z.string().max(500).optional(),
});
export type PutSellableOverrideRequest = z.infer<typeof PutSellableOverrideRequest>;

export const PutSellableOverrideResponse = z.object({
  asin: z.string(),
  note: z.string().nullable(),
  updatedAt: Iso,
});
export type PutSellableOverrideResponse = z.infer<typeof PutSellableOverrideResponse>;

export const DeleteSellableOverrideResponse = z.object({
  asin: z.string(),
});
export type DeleteSellableOverrideResponse = z.infer<typeof DeleteSellableOverrideResponse>;

// ─── Identity overrides (sourcing_identity_overrides, 2026-09-21 design) ───
// A supplier order file gives UPCs, not ASINs — on live data only 53% of
// resolved UPCs match one ASIN cleanly (28% ambiguous, 18% match nothing).
// This records a human's settled "this UPC is that ASIN" decision, keyed
// by (tenant, upc) — same shape as the pack/sellable override schemas
// above. PUT upserts, DELETE removes (204, no body), GET lists all of the
// tenant's overrides, most-recently-updated first. Owner/operator only for
// writes (routes/order-planning.ts), same as pack/sellable overrides.

export const IdentityOverride = z.object({
  upc: z.string(),
  asin: z.string(),
  note: z.string().nullable(),
  updatedAt: Iso,
});
export type IdentityOverride = z.infer<typeof IdentityOverride>;

export const PutIdentityOverrideRequest = z.object({
  asin: z.string().min(1),
  note: z.string().max(500).optional(),
});
export type PutIdentityOverrideRequest = z.infer<typeof PutIdentityOverrideRequest>;

export const PutIdentityOverrideResponse = IdentityOverride;
export type PutIdentityOverrideResponse = z.infer<typeof PutIdentityOverrideResponse>;

export const ListIdentityOverridesResponse = z.array(IdentityOverride);
export type ListIdentityOverridesResponse = z.infer<typeof ListIdentityOverridesResponse>;

// ─── Order-import preview (2026-09-21 design, Task 5) ──────────────────────
// POST /api/order-planning/plans/import/preview — parses a supplier order
// file (parseOrderFile, apps/api Task 3) and resolves each line's UPC to an
// ASIN (resolveOrderIdentities, Task 4), returning one resolution per line
// plus roll-up totals so a human can fix identity problems BEFORE Task 6's
// import endpoint creates a plan. Pure read/compute — the route writes
// nothing (no order_plans/order_plan_lines row, no R2 upload). This
// package doesn't depend on apps/api, so apps/api's ParsedOrderLine
// (parse-order-file.ts) and ResolvedLine (resolve-identities.ts) shapes are
// re-declared here structurally rather than imported.

export const OrderImportCandidateSummary = z.object({
  asin: z.string(),
  title: z.string().nullable(),
  brand: z.string().nullable(),
  packageQuantity: z.number().int().nullable(),
});
export type OrderImportCandidateSummary = z.infer<typeof OrderImportCandidateSummary>;

export const OrderImportResolvedLine = z.discriminatedUnion('state', [
  z.object({
    rowIndex: z.number().int().nonnegative(),
    state: z.literal('confirmed'),
    asin: z.string(),
    source: z.enum(['override', 'auto']),
    summary: OrderImportCandidateSummary,
  }),
  z.object({
    rowIndex: z.number().int().nonnegative(),
    state: z.literal('ambiguous'),
    candidates: z.array(OrderImportCandidateSummary),
  }),
  z.object({
    rowIndex: z.number().int().nonnegative(),
    state: z.literal('unresolved'),
    reason: z.string(),
  }),
]);
export type OrderImportResolvedLine = z.infer<typeof OrderImportResolvedLine>;

export const OrderImportPreviewLine = z.object({
  rowIndex: z.number().int().nonnegative(),
  itemNo: z.string(),
  upc: z.string(),
  brand: z.string(),
  description: z.string(),
  qty: z.number().int().positive(),
  netEachCents: Cents,
  srpCents: Cents.nullable(),
  resolution: OrderImportResolvedLine,
});
export type OrderImportPreviewLine = z.infer<typeof OrderImportPreviewLine>;

export const OrderImportPreviewResponse = z.object({
  orderNumber: z.string().nullable(),
  supplierId: Uuid,
  lines: z.array(OrderImportPreviewLine),
  totals: z.object({
    lineCount: z.number().int().nonnegative(),
    totalCents: Cents,
    confirmed: z.number().int().nonnegative(),
    ambiguous: z.number().int().nonnegative(),
    unresolved: z.number().int().nonnegative(),
  }),
});
export type OrderImportPreviewResponse = z.infer<typeof OrderImportPreviewResponse>;

// ─── Order import (2026-09-21 design, Task 6) — THE GATE ───────────────────
// POST /api/order-planning/plans/import. The file is NOT re-parsed here —
// the client has already previewed (the endpoint above) and resolved every
// line, whether the human picked a candidate or typed an ASIN; this takes
// that SETTLED result. `asin` is required on every line, but its shape is
// enforced by the route (not this schema) so a missing/empty/malformed
// value is refused as one 400 UNRESOLVED_LINES naming every offending row
// index, rather than a generic zod validation error — see
// routes/order-planning.ts's ASIN_PATTERN gate. Faraz, verbatim: "if we are
// buying a product, it has to be on Amazon. We're not going to buy
// something which is not."

export const OrderImportLine = z.object({
  itemNo: z.string(),
  upc: z.string(),
  asin: z.string(),
  description: z.string().min(1),
  qty: z.number().int().positive(),
  netEachCents: Cents,
});
export type OrderImportLine = z.infer<typeof OrderImportLine>;

// Target draft order (2026-09-28 design: "when importing an order, it
// should ask to add into existing order or create a new order" — several
// draft orders per tenant are now allowed, migration 0115). Absent
// `target` keeps the pre-2026-09-28 behaviour: append to the tenant's most
// recently updated draft, creating one if none exists.
export const OrderImportTarget = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('existing'), planId: Uuid }),
  z.object({ mode: z.literal('new'), name: z.string().trim().min(1).max(120) }),
]);
export type OrderImportTarget = z.infer<typeof OrderImportTarget>;

export const OrderImportRequest = z.object({
  supplierId: Uuid,
  orderNumber: z.string().nullable(),
  lines: z.array(OrderImportLine).min(1),
  target: OrderImportTarget.optional(),
});
export type OrderImportRequest = z.infer<typeof OrderImportRequest>;

export const OrderImportResponse = z.object({
  planId: Uuid,
  lineCount: z.number().int().nonnegative(),
  createdPlan: z.boolean(),
});
export type OrderImportResponse = z.infer<typeof OrderImportResponse>;

// ─── Bulk research pass (Task 7, 2026-09-21 design) ─────────────────────────
// POST /api/order-planning/plans/:id/research — runs the SAME per-line
// research chain (routes/order-planning.ts's runLineResearchAndPersist,
// wrapping research-service.ts's researchPlanningItem) over every line in a
// plan, sequentially, reusing already-fresh evidence instead of paying for
// a Keepa/SP-API round trip on every line. See the route's module doc for
// the reuse-vs-research decision — the ASIN comparison is load-bearing: a
// line whose ASIN was corrected after its stored research was gathered is
// ALWAYS re-researched, never reused, or a UPC->ASIN fix would silently
// keep describing a different product.

export const BulkResearchPlanRequest = z.object({
  /** Ignore freshness/ASIN-match entirely and re-research every line with
   *  an ASIN. Default false. */
  force: z.boolean().default(false),
});
export type BulkResearchPlanRequest = z.infer<typeof BulkResearchPlanRequest>;

export const BulkResearchLineOutcome = z.enum(['researched', 'reused', 'failed']);
export type BulkResearchLineOutcome = z.infer<typeof BulkResearchLineOutcome>;

export const BulkResearchPlanResponse = z.object({
  researched: z.number().int().nonnegative(),
  reused: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  lines: z.array(
    z.object({
      lineId: Uuid,
      outcome: BulkResearchLineOutcome,
      /** Set on a 'failed' outcome only — human-readable, not an error code. */
      reason: z.string().optional(),
    })
  ),
});
export type BulkResearchPlanResponse = z.infer<typeof BulkResearchPlanResponse>;

// ─────────────────────────────────────────────────────────
// Reports (Phase B1, 2026-08-23) — the first live surface reading
// amazon_sales. requireAuth + module 'amazon'.
// ─────────────────────────────────────────────────────────

export const SalesSummaryQuery = z.object({
  /** Trailing calendar months to return, current month inclusive. */
  months: z.coerce.number().int().min(1).max(24).default(3),
});
export type SalesSummaryQuery = z.infer<typeof SalesSummaryQuery>;

export const SalesSummaryMonth = z.object({
  /** 'YYYY-MM', UTC calendar month. */
  month: z.string(),
  units: z.number().int(),
  revenueCents: z.number().int(),
});
export type SalesSummaryMonth = z.infer<typeof SalesSummaryMonth>;

export const SalesSummaryResponse = z.array(SalesSummaryMonth);
export type SalesSummaryResponse = z.infer<typeof SalesSummaryResponse>;

// ─────────────────────────────────────────────────────────
// Finance summary (B2 Component E1, 2026-08-27)
// ─────────────────────────────────────────────────────────

const CompleteMonthDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

function validUtcDate(value: string): boolean {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month! - 1 && date.getUTCDate() === day
  );
}

/** Finance periods are whole UTC calendar months and at most 366 days. */
export const FinanceSummaryQuery = z
  .object({
    from: CompleteMonthDate,
    to: CompleteMonthDate,
  })
  .superRefine((value, ctx) => {
    if (!validUtcDate(value.from)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['from'], message: 'Invalid UTC date' });
      return;
    }
    if (!validUtcDate(value.to)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Invalid UTC date' });
      return;
    }
    const from = new Date(`${value.from}T00:00:00.000Z`);
    const to = new Date(`${value.to}T23:59:59.999Z`);
    if (from.getUTCDate() !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['from'],
        message: 'from must be the first day of a UTC month',
      });
    }
    const monthEnd = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() + 1, 0)).getUTCDate();
    if (to.getUTCDate() !== monthEnd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'to must be the last day of a UTC month',
      });
    }
    if (to.getTime() < from.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'to must not precede from',
      });
    }
    if (to.getTime() - from.getTime() + 1 > 366 * 24 * 60 * 60 * 1000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'period cannot exceed 366 days',
      });
    }
  });
export type FinanceSummaryQuery = z.infer<typeof FinanceSummaryQuery>;

export const FinanceSummaryCoverage = z
  .object({
    status: z.enum(['complete', 'backfilling', 'pending', 'unknown']),
    complete: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (value.complete !== (value.status === 'complete')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['complete'],
        message: "complete must be true exactly when status is 'complete'",
      });
    }
  });
export type FinanceSummaryCoverage = z.infer<typeof FinanceSummaryCoverage>;

export const FinanceSummaryProvenance = z.object({
  source: z.string(),
  freshness: z.enum(['fresh', 'stale', 'unknown']),
  coverage: z.enum(['complete', 'backfilling', 'pending', 'unknown']),
  dateBasis: z.enum(['posted_date_utc', 'deposit_date_utc', 'purchase_date_utc']),
  periodSemantics: z.string(),
  periodStart: Iso,
  periodEnd: Iso,
});
export type FinanceSummaryProvenance = z.infer<typeof FinanceSummaryProvenance>;

export const FinanceSummaryTask10Coverage = z
  .object({
    status: z.enum(['complete', 'partial', 'backfilling', 'pending', 'unknown']),
    complete: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (value.complete !== (value.status === 'complete')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['complete'],
        message: "complete must be true exactly when status is 'complete'",
      });
    }
  });
export type FinanceSummaryTask10Coverage = z.infer<typeof FinanceSummaryTask10Coverage>;

export const FinanceSummaryTask10Provenance = FinanceSummaryProvenance.extend({
  coverage: z.enum(['complete', 'partial', 'backfilling', 'pending', 'unknown']),
});
export type FinanceSummaryTask10Provenance = z.infer<typeof FinanceSummaryTask10Provenance>;

/** Warehouse expense provenance is recognized by the fact's UTC recognition date. */
export const FinanceSummaryWarehouseExpenseProvenance = FinanceSummaryTask10Provenance.extend({
  dateBasis: z.literal('recognized_at_utc'),
});
export type FinanceSummaryWarehouseExpenseProvenance = z.infer<
  typeof FinanceSummaryWarehouseExpenseProvenance
>;

export const FinanceSummarySource = z.object({
  saleId: Uuid,
  consumptionId: Uuid,
  shipmentId: Uuid,
  shipmentLineId: Uuid,
  fbaAllocationId: Uuid,
  lotId: Uuid,
  costLayerId: Uuid.nullable(),
  warehouseAllocationId: Uuid.nullable(),
  chargeId: Uuid.nullable(),
  invoiceId: Uuid.nullable(),
  invoiceLineId: Uuid.nullable(),
  prepReference: PrepReference.nullable().optional(),
  prepBoxReference: PrepBoxReference.nullable().optional(),
  receiptId: Uuid.nullable().optional(),
  poReference: z.string().trim().min(1).nullable().optional(),
  vendorUnits: z.number().int(),
  vendorCostCents: z.number().int(),
  event: z.enum(['consume', 'reverse']),
  purchaseDate: Iso,
  recognizedAt: Iso,
});
export type FinanceSummarySource = z.infer<typeof FinanceSummarySource>;

export const FinanceSummaryIncomeStatus = z.enum([
  'complete',
  'partial',
  'blocked_missing_revenue',
  'blocked_missing_sales',
  'blocked_missing_landed_cogs',
  'blocked_missing_warehouse_expense',
  'blocked_missing_operating_expense',
]);
export type FinanceSummaryIncomeStatus = z.infer<typeof FinanceSummaryIncomeStatus>;

export const FinanceSummaryCategory = z.object({
  month: z.string(),
  // Fee Center's category enum is declared below this block; lazy resolution
  // keeps one canonical category vocabulary without an initialization cycle.
  category: z.lazy(() => FeeCenterActivityCategory),
  netChargeCents: z.number().int(),
});
export type FinanceSummaryCategory = z.infer<typeof FinanceSummaryCategory>;

export const FinanceSummaryMonth = z.object({
  month: z.string(),
  settlementRevenueCents: z.number().int().nullable(),
  amazonNetChargeCents: z.number().int().nullable(),
  netProceedsBeforeCogsCents: z.number().int().nullable(),
  landedCogsCents: z.number().int().nullable().optional(),
  warehousePeriodExpenseCents: z.number().int().nullable().optional(),
  operatingExpenseCents: z.number().int().nullable().optional(),
  netIncomeCents: z.number().int().nullable().optional(),
  incomeStatus: FinanceSummaryIncomeStatus.optional(),
  incomeBlockedReason: z.string().nullable().optional(),
  /** R2 item 3 (D1): documents the promise — every number on this row is
   *  settlement-grade, never estimated. `.optional()` covers responses from
   *  an API build that predates this field; a client should treat a missing
   *  value the same as 'settlement' (Finance has always been fail-closed). */
  grade: FinanceGrade.optional(),
});
export type FinanceSummaryMonth = z.infer<typeof FinanceSummaryMonth>;

export const FinanceSummarySettlementActivity = z.object({
  currency: z.string(),
  settlementRevenueCents: z.number().int().nullable(),
  amazonNetChargeCents: z.number().int().nullable(),
  netProceedsBeforeCogsCents: z.number().int().nullable(),
  landedCogsCents: z.number().int().nullable(),
  warehousePeriodExpenseCents: z.number().int().nullable(),
  operatingExpenseCents: z.number().int().nullable(),
  netIncomeCents: z.number().int().nullable(),
  incomeStatus: FinanceSummaryIncomeStatus,
  incomeBlockedReason: z.string().nullable(),
  categories: z.array(FinanceSummaryCategory),
  monthly: z.array(FinanceSummaryMonth),
  sources: z.array(FinanceSummarySource).optional(),
  /** R2 item 3 (D1) — same promise as FinanceSummaryMonth.grade, at the
   *  currency-total level. `.optional()` covers pre-field responses. */
  grade: FinanceGrade.optional(),
});
export type FinanceSummarySettlementActivity = z.infer<typeof FinanceSummarySettlementActivity>;

export const FinanceSummaryPayout = z.object({
  settlementId: z.string(),
  periodStart: Iso.nullable(),
  periodEnd: Iso.nullable(),
  depositDate: Iso.nullable(),
  currency: z.string(),
  settlementTotalCents: z.number().int().nullable(),
  explainedLedgerNetCents: z.number().int().nullable(),
  unexplainedDeltaCents: z.number().int().nullable(),
  reconciliationStatus: z.enum(['reconciled', 'partial', 'unknown']),
});
export type FinanceSummaryPayout = z.infer<typeof FinanceSummaryPayout>;

export const FinanceSummaryPayoutTotal = z.object({
  currency: z.string(),
  totalCents: z.number().int().nullable(),
});
export type FinanceSummaryPayoutTotal = z.infer<typeof FinanceSummaryPayoutTotal>;

export const FinanceSummarySalesMonth = z.object({
  month: z.string(),
  units: z.number().int().nullable(),
  revenueCents: z.number().int().nullable(),
});
export type FinanceSummarySalesMonth = z.infer<typeof FinanceSummarySalesMonth>;

export const FinanceSummarySalesCurrency = z.object({
  currency: z.string(),
  months: z.array(FinanceSummarySalesMonth),
});
export type FinanceSummarySalesCurrency = z.infer<typeof FinanceSummarySalesCurrency>;

/** R2 item 1 (docs/plans/2026-09-02-nightly-cogs-replan.md): per-tenant
 *  "COGS replan pending" state. Approvals and PO charge writes mark it; the
 *  nightly queue job (or "Recompute now") clears it. */
const CogsReplanPhaseResult = z.object({
  inserted: z.number().int().nonnegative(),
  reversed: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
});
/** R2 item 1, task 2 (docs/plans/2026-09-03-incremental-replan.md D1):
 *  counts-only view of the merged `pending_scope` — never the raw ids
 *  (those can run into the hundreds; the banner only needs "how much"). */
const CogsReplanPendingScope = z.object({
  full: z.boolean(),
  lotCount: z.number().int().nonnegative(),
  skuCount: z.number().int().nonnegative(),
});
/** R2 item 1, task 3 (D3): the chunked-run summary — filled in once the
 *  queue consumer lands; task 2 always reports `run: null`. */
export const CogsReplanRunSummary = z.object({
  id: z.string(),
  chunksTotal: z.number().int().nonnegative(),
  chunksDone: z.number().int().nonnegative(),
  chunksFailed: z.number().int().nonnegative(),
  /** R2 item 1 chunked-replan perf follow-up (2026-09-03): the in-flight
   *  chunk's own heartbeat, filled from its `tenant_cogs_replan_chunks` row
   *  while status is 'running' — `null` when no chunk is currently running
   *  (between chunks, or before the first one's heartbeat lands). Lets the
   *  web banner show "chunk 3/12 — 80/123 keys" instead of a bare spinner
   *  during a long chunk (e.g. a large lots-scope re-post). */
  current: z
    .object({
      chunkIndex: z.number().int().nonnegative(),
      keysDone: z.number().int().nonnegative(),
      keysTotal: z.number().int().nonnegative(),
      updatedAt: Iso,
    })
    .nullable()
    .optional(),
});
export type CogsReplanRunSummary = z.infer<typeof CogsReplanRunSummary>;
export const CogsReplanState = z.object({
  pendingSince: Iso.nullable(),
  pendingCount: z.number().int().nonnegative(),
  pendingReasons: z.array(z.string()),
  pendingScope: CogsReplanPendingScope.nullable(),
  runningSince: Iso.nullable(),
  runningRunId: z.string().nullable(),
  lastReconciledAt: Iso.nullable(),
  lastResult: z
    .object({
      cogs: CogsReplanPhaseResult,
      writeOffs: CogsReplanPhaseResult,
      seconds: z.number(),
      // R2 item 1, task 3 (D3): inserted+reversed across cogs and
      // write-offs, present only for a completed `full` (nightly drift
      // check) run.
      drift: z.number().int().nonnegative().optional(),
    })
    .nullable(),
  lastError: z.string().nullable(),
  run: CogsReplanRunSummary.nullable(),
});
export type CogsReplanState = z.infer<typeof CogsReplanState>;
export const CogsReplanStateResponse = z.object({ state: CogsReplanState });
export type CogsReplanStateResponse = z.infer<typeof CogsReplanStateResponse>;

export const FinanceSummaryResponse = z.object({
  period: z.object({ from: Iso, to: Iso }),
  cogsReplan: CogsReplanState.optional(),
  provenance: z.object({
    settlements: FinanceSummaryProvenance,
    payouts: FinanceSummaryProvenance,
    sales: FinanceSummaryProvenance,
    cogs: FinanceSummaryTask10Provenance.optional(),
    warehouseExpenses: FinanceSummaryWarehouseExpenseProvenance.optional(),
    operatingExpenses: FinanceSummaryWarehouseExpenseProvenance.optional(),
  }),
  coverage: z.object({
    settlements: FinanceSummaryCoverage,
    sales: FinanceSummaryCoverage,
    cogs: FinanceSummaryTask10Coverage.optional(),
    warehouseExpenses: FinanceSummaryTask10Coverage.optional(),
    operatingExpenses: FinanceSummaryTask10Coverage.optional(),
  }),
  settlementActivity: z.array(FinanceSummarySettlementActivity),
  payouts: z.array(FinanceSummaryPayout),
  payoutTotals: z.array(FinanceSummaryPayoutTotal),
  sales: z.array(FinanceSummarySalesCurrency),
});
export type FinanceSummaryResponse = z.infer<typeof FinanceSummaryResponse>;

// ─── R2 item 4 (2026-09-03 business-position design doc) — the "every cent
// I invested" panel. `grade` is a literal union defined locally (not
// imported from item 3's kernel facade, per that item's coordination note)
// — 'settlement' for a direct ledger fact, 'estimate' when a value fell
// back to an assumption (only Still-in-inventory can do this, when an
// on-hand unit has no lot-level landed cost). ─────────────────────────────

export const BusinessPositionGrade = z.enum(['settlement', 'estimate']);
export type BusinessPositionGrade = z.infer<typeof BusinessPositionGrade>;

const BusinessPositionLine = z.object({
  cents: z.number().int(),
  grade: BusinessPositionGrade,
});

export const BusinessPositionInvestedBreakdown = z.object({
  vendorCents: z.number().int(),
  chargesCents: z.number().int(),
  /** Negative — the sum of po_charges.kind='credit_memo' rows, which are
   *  stored negative already (DB check constraint). Invested's breakdown
   *  fields sum directly (vendor + charges + credits + warehouse + overhead
   *  + amazonFees); credits needs no separate subtraction. */
  creditsCents: z.number().int(),
  warehouseCents: z.number().int(),
  overheadCents: z.number().int(),
  amazonFeesCents: z.number().int(),
});
export type BusinessPositionInvestedBreakdown = z.infer<typeof BusinessPositionInvestedBreakdown>;

export const BusinessPositionInvestedLine = BusinessPositionLine.extend({
  breakdown: BusinessPositionInvestedBreakdown,
});
export type BusinessPositionInvestedLine = z.infer<typeof BusinessPositionInvestedLine>;

export const BusinessPositionInventoryLine = BusinessPositionLine.extend({
  units: z.number().int().nonnegative(),
  /** On-hand units whose lot lacks a computable landed cost — "none
   *  expected" per the design doc; a nonzero value flips this line's grade
   *  to 'estimate' and those units are valued at 0 cents (no fallback price
   *  source is in scope for this item). */
  unitsWithoutLineage: z.number().int().nonnegative(),
  /** Fix pass I6: sold units the COGS kernel would flag as
   *  INCOMPLETE_SALES_COVERAGE (an eligible AFN sale whose consumption
   *  lineage hasn't caught up with its qty) — still counted as on-hand
   *  stock above since nothing has decremented them. A nonzero value also
   *  flips this line's grade to 'estimate'. */
  unitsSoldWithoutLineage: z.number().int().nonnegative(),
});
export type BusinessPositionInventoryLine = z.infer<typeof BusinessPositionInventoryLine>;

export const BusinessPositionResponse = z.object({
  currency: z.string(),
  /** First vendor invoice's ordered date. */
  since: Iso,
  /** Latest settlement period end (or `since`/now when the tenant has no
   *  settlements yet). */
  asOf: Iso,
  /** Fix pass I3: true when Returned/Amazon fees include a settlement line
   *  from the still-open current calendar month, or one with no
   *  posted_date at all — this panel is lifetime-to-date and never
   *  excludes them, unlike the income statement (complete months only). */
  includesOpenMonth: z.boolean(),
  lines: z.object({
    invested: BusinessPositionInvestedLine,
    returned: BusinessPositionLine,
    inventory: BusinessPositionInventoryLine,
    realizedMargin: BusinessPositionLine,
    position: BusinessPositionLine,
  }),
});
export type BusinessPositionResponse = z.infer<typeof BusinessPositionResponse>;

/** R2 item 2 (2026-09-03 design doc, A1): lazy COGS/warehouse-expense source
 *  evidence — the "How these numbers are built" drill-down the finance
 *  summary used to build eagerly on every request. `warehouseExpenses` is
 *  always `[]` today (no warehouse-expense provenance chain exists yet); the
 *  field exists so the contract doesn't need to change again when one does. */
export const FinanceSummarySourcesResponse = z.object({
  cogs: z.array(FinanceSummarySource),
  warehouseExpenses: z.array(FinanceSummarySource),
});
export type FinanceSummarySourcesResponse = z.infer<typeof FinanceSummarySourcesResponse>;

// ─────────────────────────────────────────────────────────
// Operating expenses ledger (R1 item 6, 2026-09-01 design doc) — overhead
// that is not COGS and not a warehouse-invoice cost (VA pay, software,
// marketing, card fees). Mounted at /api/operating-expenses, owner-only
// writes. USD only, positive amounts (D5); month strings are the same
// 'YYYY-MM' UTC format used by the warehouse expense attestation.
// ─────────────────────────────────────────────────────────

const OperatingExpenseMonth = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be in YYYY-MM format');

export const OperatingExpenseCategory = z.enum([
  'contractor_labor',
  'software',
  'marketing',
  'bank_card_fees',
  'professional_services',
  'other',
]);
export type OperatingExpenseCategory = z.infer<typeof OperatingExpenseCategory>;

export const OperatingExpense = z.object({
  id: Uuid,
  category: OperatingExpenseCategory,
  amountCents: z.number().int(),
  currency: z.string(),
  month: OperatingExpenseMonth,
  description: z.string(),
  recurrenceId: Uuid.nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type OperatingExpense = z.infer<typeof OperatingExpense>;

export const OperatingExpenseRecurrence = z.object({
  id: Uuid,
  category: OperatingExpenseCategory,
  amountCents: z.number().int(),
  currency: z.string(),
  description: z.string(),
  startMonth: OperatingExpenseMonth,
  endMonth: OperatingExpenseMonth.nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type OperatingExpenseRecurrence = z.infer<typeof OperatingExpenseRecurrence>;

/** POST /api/operating-expenses */
export const CreateOperatingExpenseRequest = z
  .object({
    category: OperatingExpenseCategory,
    amountCents: z.number().int().positive(),
    month: OperatingExpenseMonth,
    description: z.string().trim().min(1).max(200),
  })
  .strict();
export type CreateOperatingExpenseRequest = z.infer<typeof CreateOperatingExpenseRequest>;

/** PATCH /api/operating-expenses/:id */
export const UpdateOperatingExpenseRequest = z
  .object({
    category: OperatingExpenseCategory.optional(),
    amountCents: z.number().int().positive().optional(),
    month: OperatingExpenseMonth.optional(),
    description: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field is required',
  });
export type UpdateOperatingExpenseRequest = z.infer<typeof UpdateOperatingExpenseRequest>;

/** POST /api/operating-expenses/recurrences */
export const CreateOperatingExpenseRecurrenceRequest = z
  .object({
    category: OperatingExpenseCategory,
    amountCents: z.number().int().positive(),
    description: z.string().trim().min(1).max(200),
    startMonth: OperatingExpenseMonth,
    endMonth: OperatingExpenseMonth.nullable().optional(),
  })
  .strict();
export type CreateOperatingExpenseRecurrenceRequest = z.infer<
  typeof CreateOperatingExpenseRecurrenceRequest
>;

/** PATCH /api/operating-expenses/recurrences/:id — D8: startMonth is not editable. */
export const UpdateOperatingExpenseRecurrenceRequest = z
  .object({
    category: OperatingExpenseCategory.optional(),
    amountCents: z.number().int().positive().optional(),
    description: z.string().trim().min(1).max(200).optional(),
    endMonth: OperatingExpenseMonth.nullable().optional(),
  })
  .strict()
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field is required',
  });
export type UpdateOperatingExpenseRecurrenceRequest = z.infer<
  typeof UpdateOperatingExpenseRecurrenceRequest
>;

/** GET /api/operating-expenses?from=YYYY-MM&to=YYYY-MM */
export const ListOperatingExpensesResponse = z.object({
  expenses: z.array(OperatingExpense),
  recurrences: z.array(OperatingExpenseRecurrence),
});
export type ListOperatingExpensesResponse = z.infer<typeof ListOperatingExpensesResponse>;

export const OperatingExpenseResponse = z.object({
  expense: OperatingExpense,
});
export type OperatingExpenseResponse = z.infer<typeof OperatingExpenseResponse>;

export const OperatingExpenseRecurrenceResponse = z.object({
  recurrence: OperatingExpenseRecurrence,
  materializedCount: z.number().int(),
});
export type OperatingExpenseRecurrenceResponse = z.infer<typeof OperatingExpenseRecurrenceResponse>;

// ─────────────────────────────────────────────────────────
// Sales ledger (2026-08-25) — GET /api/sales, module-gated on 'amazon'. The
// dense "all Amazon sales" table in OPS. amazon_sales.item_price_cents is
// the report's LINE TOTAL for the row's quantity, not a per-unit price (see
// apps/api/src/lib/amazon/sales-summary.ts's header comment — confirmed
// against the same source table amazon_sales.sales-summary already reads).
// rowTotalCents mirrors item_price_cents directly; unitPriceCents is
// DERIVED (rowTotalCents / qty, rounded) purely for display — never
// re-multiply it by qty, that double-counts revenue.
// ─────────────────────────────────────────────────────────

export const ListSalesQuery = z.object({
  /** ISO date/datetime; defaults to 90 days before now server-side. */
  from: z.string().optional(),
  /** ISO date/datetime; defaults to now server-side. */
  to: z.string().optional(),
  status: z.string().optional(),
});
export type ListSalesQuery = z.infer<typeof ListSalesQuery>;

export const SalesRow = z.object({
  id: z.string().uuid(),
  amazonOrderId: z.string(),
  purchaseDate: z.string(),
  orderStatus: z.string(),
  sellerSku: z.string(),
  asin: z.string().nullable(),
  productName: z.string().nullable(),
  imageUrl: z.string().nullable(),
  qty: z.number().int(),
  /** Derived: rowTotalCents / qty, rounded. Null when itemPriceCents is null. */
  unitPriceCents: z.number().int().nullable(),
  /** = amazon_sales.item_price_cents directly (already the line total). */
  rowTotalCents: z.number().int().nullable(),
  currency: z.string().nullable(),
  /** 'AFN' (Amazon-fulfilled/FBA) | 'MFN' (merchant-fulfilled/FBM) verbatim
   *  from the report, or null for rows ingested before migration 0053 added
   *  this column (FBM support, 2026-08-25). */
  fulfillmentChannel: z.string().nullable(),
});
export type SalesRow = z.infer<typeof SalesRow>;

export const ListSalesResponse = z.object({
  rows: z.array(SalesRow),
  meta: z.object({
    rowCount: z.number().int().nonnegative(),
    /** True when the server truncated to the 5,000-row hard cap — never a
     *  silent truncation, the UI must say so. */
    capped: z.boolean(),
  }),
});
export type ListSalesResponse = z.infer<typeof ListSalesResponse>;

// ─────────────────────────────────────────────────────────
// Amazon unit-flow ledger (2026-08-26) — read-only, source-aggregated math.
// ─────────────────────────────────────────────────────────

export const UNIT_FLOW_ATTENTION_THRESHOLD_CENTS = 1_000;

export const AmazonUnitFlowRow = z.object({
  sellerSku: z.string(),
  asin: z.string().nullable(),
  productName: z.string().nullable(),
  vendorItemNo: z.string().nullable(),
  unitsPerListing: z.number().int().positive(),
  mappingState: z.enum(['mapped', 'unmapped', 'shared']),
  cost: z.object({
    weightedVendorUnitCents: z.number().int().nonnegative().nullable(),
    listingUnitCents: z.number().int().nonnegative().nullable(),
    contributingLotUnits: z.number().int().nonnegative(),
    contributingLotCostCents: z.number().int().nonnegative().nullable(),
  }),
  warehouse: z
    .object({
      lotReceived: z.number().int(),
      warehouseOnHand: z.number().int(),
      allocatedToShipments: z.number().int(),
      /** R2 item 5: units returned to vendor — never on-hand, never
       *  shippable; tracked separately from warehouseOnHand (which already
       *  nets them out) so residualUnits can net to zero. */
      returnedUnits: z.number().int().default(0),
      residualUnits: z.number().int(),
    })
    .nullable(),
  amazon: z.object({
    fbaReceived: z.number().int(),
    soldUnits: z.number().int(),
    fbaOnHand: z.number().int(),
    adjustments: z.number().int(),
    removedUnits: z.number().int(),
    reimbursedUnits: z.number().int(),
    /** Reported reimbursement amount; separate from physical inventory variance units. */
    reimbursedCents: z.number().int().default(0),
    residualUnits: z.number().int(),
  }),
  warehouseResidualCents: z.number().int().nonnegative().nullable(),
  amazonResidualCents: z.number().int().nonnegative().nullable(),
  /** Absolute, combined dollar exposure; null during source backfill or when any nonzero leg is uncosted. */
  residualCents: z.number().int().nonnegative().nullable(),
});
export type AmazonUnitFlowRow = z.infer<typeof AmazonUnitFlowRow>;

export const AmazonUnitFlowCoverageLeg = z.object({
  status: z.enum(['unknown', 'backfilling', 'caught_up', 'pending']),
  nextWindowStart: z.string().nullable(),
  lastCompletedAt: z.string().nullable(),
});

export const AmazonUnitFlowCoverage = z.object({
  complete: z.boolean(),
  legs: z.object({
    sales: AmazonUnitFlowCoverageLeg,
    adjustments: AmazonUnitFlowCoverageLeg,
    removals: AmazonUnitFlowCoverageLeg,
    reimbursements: AmazonUnitFlowCoverageLeg,
  }),
});
export type AmazonUnitFlowCoverage = z.infer<typeof AmazonUnitFlowCoverage>;

export const AmazonUnitFlowResponse = z.object({
  rows: z.array(AmazonUnitFlowRow).default([]),
  /** Null during source backfill or when at least one nonzero residual has no contributing cost. */
  residualCents: z.number().int().nonnegative().nullable().default(0),
  uncostedResidualSkuCount: z.number().int().nonnegative().default(0),
  attentionThresholdCents: z
    .number()
    .int()
    .nonnegative()
    .default(UNIT_FLOW_ATTENTION_THRESHOLD_CENTS),
  /** Source completeness for all four Amazon report legs. */
  coverage: AmazonUnitFlowCoverage.nullable().default(null),
});
export type AmazonUnitFlowResponse = z.infer<typeof AmazonUnitFlowResponse>;

// ─────────────────────────────────────────────────────────
// Finance Fee Center (B2 Component D, 2026-08-26)
// ─────────────────────────────────────────────────────────

export const FeeCenterQuery = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  sellerSku: z.string().trim().min(1).optional(),
});
export type FeeCenterQuery = z.infer<typeof FeeCenterQuery>;

export const FeeCenterProvenance = z.object({
  source: z.string(),
  periodStart: Iso.nullable(),
  periodEnd: Iso.nullable(),
  freshness: z.enum(['fresh', 'stale', 'unknown']),
  coverage: z.enum(['complete', 'backfilling', 'pending', 'unknown']),
});
export type FeeCenterProvenance = z.infer<typeof FeeCenterProvenance>;

/** Stable, human-auditable buckets for non-revenue Amazon settlement activity. */
export const FeeCenterActivityCategory = z.enum([
  'selling_fees',
  'fba_fulfillment',
  'inbound_shipping',
  'storage_fees',
  'removals',
  'subscription',
  'reimbursements',
  'tax_withheld',
  'promotions_adjustments',
  'other_amazon_activity',
]);
export type FeeCenterActivityCategory = z.infer<typeof FeeCenterActivityCategory>;

export const FeeCenterActivityScope = z.enum(['product', 'account', 'mixed']);
export type FeeCenterActivityScope = z.infer<typeof FeeCenterActivityScope>;

/** Description-level detail behind one product/month actual-fee total. */
export const FeeCenterActualBreakdownRow = z.object({
  category: FeeCenterActivityCategory,
  label: z.string(),
  explanation: z.string(),
  netChargeCents: z.number().int(),
  lineCount: z.number().int().nonnegative(),
  orderCount: z.number().int().nonnegative(),
  skuCount: z.number().int().nonnegative(),
  scope: FeeCenterActivityScope,
  amountType: z.string(),
  amountDescription: z.string(),
  provenance: FeeCenterProvenance,
});
export type FeeCenterActualBreakdownRow = z.infer<typeof FeeCenterActualBreakdownRow>;

export const FeeCenterActualRow = z.object({
  sellerSku: z.string(),
  /** Nullable latest inventory metadata; SKU remains the durable join key. */
  productName: z.string().nullable().default(null),
  asin: z.string().nullable().default(null),
  periodStart: Iso,
  periodEnd: Iso,
  currency: z.string(),
  revenueCents: z.number().int(),
  actualFeesCents: z.number().int(),
  feePercent: z.number().nullable(),
  /** Sales-report units for this SKU/month/currency; null when coverage is incomplete. */
  unitsSold: z.number().int().nonnegative().nullable().default(null),
  /** Total signed Amazon fees divided by unitsSold; null without a safe denominator. */
  feePerUnitSoldCents: z.number().int().nullable().default(null),
  breakdown: z.array(FeeCenterActualBreakdownRow).default([]),
  provenance: FeeCenterProvenance,
});
export type FeeCenterActualRow = z.infer<typeof FeeCenterActualRow>;

/** One explainable, signed non-revenue movement grouped by month and raw label. */
export const FeeCenterAccountActivityRow = z.object({
  periodStart: Iso,
  periodEnd: Iso,
  currency: z.string(),
  category: FeeCenterActivityCategory,
  label: z.string(),
  explanation: z.string(),
  netChargeCents: z.number().int(),
  lineCount: z.number().int().nonnegative(),
  orderCount: z.number().int().nonnegative(),
  skuCount: z.number().int().nonnegative(),
  scope: FeeCenterActivityScope,
  amountType: z.string(),
  amountDescription: z.string(),
  provenance: FeeCenterProvenance,
});
export type FeeCenterAccountActivityRow = z.infer<typeof FeeCenterAccountActivityRow>;

export const FeeCenterExpectedStatus = z.enum([
  'available',
  'stale_stamp',
  'price_mismatch',
  'no_stamp',
]);
export type FeeCenterExpectedStatus = z.infer<typeof FeeCenterExpectedStatus>;

export const FeeCenterExpectedReason = z.enum([
  'fresh_same_price',
  'stale_stamp',
  'price_mismatch',
  'no_stamp',
]);
export type FeeCenterExpectedReason = z.infer<typeof FeeCenterExpectedReason>;

export const FeeCenterFulfillmentAuditRow = z.object({
  sellerSku: z.string(),
  periodStart: Iso,
  periodEnd: Iso,
  currency: z.string(),
  units: z.number().int().nonnegative(),
  chargedFeesCents: z.number().int(),
  expectedFeesCents: z.number().int().nullable(),
  /** Why the expected-fee estimate is available or withheld. */
  expectedStatus: FeeCenterExpectedStatus,
  expectedReason: FeeCenterExpectedReason,
  deltaCents: z.number().int().nullable(),
  chargedPerUnitCents: z.number().int().nullable(),
  expectedPerUnitCents: z.number().int().nullable(),
  provenance: FeeCenterProvenance,
});
export type FeeCenterFulfillmentAuditRow = z.infer<typeof FeeCenterFulfillmentAuditRow>;

export const FeeCenterStorageRow = z.object({
  sellerSku: z.string(),
  periodStart: Iso,
  periodEnd: Iso,
  /**
   * Amazon's estimated monthly storage fee, rounded once to cents after exact
   * currency-micro source rows are aggregated; null while report coverage is
   * incomplete. Settlement actuals are separate.
   */
  estimatedStorageFeeCents: z.number().int().nonnegative().nullable(),
  unitsSold: z.number().int().nonnegative().nullable(),
  unitsPerDay: z.number().nonnegative().nullable(),
  costPerUnitSoldCents: z.number().int().nonnegative().nullable(),
  /** Percentage of revenue consumed by estimated storage (31 means 31%). */
  storagePercentOfRevenue: z.number().nonnegative().nullable(),
  provenance: FeeCenterProvenance,
});
export type FeeCenterStorageRow = z.infer<typeof FeeCenterStorageRow>;

export const FeeCenterAgedRow = z.object({
  sellerSku: z.string(),
  snapshotDate: Iso,
  ageBucket: z.string(),
  units: z.number().int().nonnegative(),
  surchargeDate: Iso.nullable(),
  daysUntilSurcharge: z.number().int().nullable(),
  provenance: FeeCenterProvenance,
});
export type FeeCenterAgedRow = z.infer<typeof FeeCenterAgedRow>;

export const FeeCenterFeeChangeRow = z.object({
  sellerSku: z.string(),
  previousFeeCents: z.number().int(),
  newFeeCents: z.number().int(),
  previousPriceCents: z.number().int(),
  newPriceCents: z.number().int(),
  deltaCents: z.number().int(),
  material: z.boolean(),
  observedAt: Iso,
  provenance: FeeCenterProvenance,
});
export type FeeCenterFeeChangeRow = z.infer<typeof FeeCenterFeeChangeRow>;

export const FeeCenterCoverageLeg = z.object({
  status: z.enum(['unknown', 'backfilling', 'caught_up', 'pending']),
  nextWindowStart: Iso.nullable(),
  lastCompletedAt: Iso.nullable(),
});

export const FeeCenterCoverage = z.object({
  complete: z.boolean(),
  storageFees: FeeCenterCoverageLeg,
  inventoryPlanning: FeeCenterCoverageLeg,
});
export type FeeCenterCoverage = z.infer<typeof FeeCenterCoverage>;

/**
 * R2 item 3 (D1/D3, docs/plans/2026-09-03-graded-kernel.md; I1 fix pass) —
 * the Fee Center's headline total per currency for the requested period,
 * computed by the ONE kernel (`lib/finance/kernel.ts`'s
 * monthlySettlementActivity) over Finance's OWN settlement header+line
 * selection (`lib/finance/facts.ts`'s loadFinanceSettlementsForRange — the
 * exact query lib/finance/facts.ts's loadFinanceMonthRaw uses for the
 * finance-summary route: a LEFT JOIN admitting a posted-date line even when
 * its settlement header doesn't independently match
 * `settlementSelectionPredicate`, plus deposit-date-matched lines and
 * headerless settlements synthesized from proven zero-ledger sync
 * evidence — NOT the bare predicate alone), alongside `kernelDeltaCents` —
 * Fee Center's own line-table total (its existing postedDate-only line
 * read, independent of the selection above) minus the kernel total, now a
 * true "differs from Finance" signal rather than partly an artifact of the
 * two sides reading different lines. Proven to be zero on the
 * finance-summary fixture tenant (D3); reported, never hidden, when it
 * isn't.
 */
export const FeeCenterTotalsRow = z.object({
  currency: z.string(),
  periodStart: Iso,
  periodEnd: Iso,
  revenueCents: z.number().int(),
  chargeCents: z.number().int(),
  netProceedsCents: z.number().int(),
  grade: FinanceGrade,
  kernelDeltaCents: z.number().int(),
});
export type FeeCenterTotalsRow = z.infer<typeof FeeCenterTotalsRow>;

export const FeeCenterResponse = z.object({
  actual: z.array(FeeCenterActualRow).default([]),
  accountActivity: z.array(FeeCenterAccountActivityRow).default([]),
  fulfillment: z.array(FeeCenterFulfillmentAuditRow).default([]),
  storage: z.array(FeeCenterStorageRow).default([]),
  aged: z.array(FeeCenterAgedRow).default([]),
  feeChanges: z.array(FeeCenterFeeChangeRow).default([]),
  coverage: FeeCenterCoverage,
  /** `.default([])` covers responses from an API build that predates this
   *  field. One row per currency present in the requested period. */
  totals: z.array(FeeCenterTotalsRow).default([]),
});
export type FeeCenterResponse = z.infer<typeof FeeCenterResponse>;

// ─────────────────────────────────────────────────────────
// Warehouse billing (E2)
// ─────────────────────────────────────────────────────────

export const WarehouseBillingBasis = z.enum([
  'vendor_unit',
  'listing_unit',
  'box',
  'weight',
  'volume',
  'time',
  'fixed',
]);
export type WarehouseBillingBasis = z.infer<typeof WarehouseBillingBasis>;

export const WarehouseAccountingTreatment = z.enum(['capitalizable', 'period_expense']);
export type WarehouseAccountingTreatment = z.infer<typeof WarehouseAccountingTreatment>;

/** Explicit charge classification; null persists only on legacy catalog reads. */
export const WarehouseServiceCategory = z.enum(['material', 'service']);
export type WarehouseServiceCategory = z.infer<typeof WarehouseServiceCategory>;

export const WarehouseInvoiceStatus = z.enum([
  'draft',
  'submitted',
  'approved',
  'partially_paid',
  'paid',
  'disputed',
  'void',
]);
export type WarehouseInvoiceStatus = z.infer<typeof WarehouseInvoiceStatus>;

export const WarehouseAllocationStatus = z.enum(['ready', 'blocked']);
export type WarehouseAllocationStatus = z.infer<typeof WarehouseAllocationStatus>;

const WarehouseCurrency = z
  .string()
  .trim()
  .min(1)
  .transform((value) => value.toUpperCase())
  .refine((value) => /^[A-Z]{3}$/.test(value), 'currency must be a 3-letter ISO code');
const WarehouseDescription = z.string().trim().min(1);
const POSTGRES_INT_MIN = -2_147_483_648;
const POSTGRES_INT_MAX = 2_147_483_647;
const WarehouseInteger = z.number().int().min(POSTGRES_INT_MIN).max(POSTGRES_INT_MAX);
const WarehouseCents = WarehouseInteger;
const WarehouseNonnegativeCents = WarehouseInteger.nonnegative();
const WarehouseMillis = WarehouseInteger.nonnegative();

const sumWarehouseCents = (values: readonly number[]) =>
  values.reduce((sum, value) => sum + BigInt(value), 0n);
const WarehouseSourceIds = {
  sourceId: Uuid.nullable().optional(),
  sourceType: z
    .enum(['receipt', 'shipment', 'shipment_line', 'box', 'prep_box', 'prep_batch'])
    .nullable()
    .optional(),
  sourceReceiptId: Uuid.nullable().optional(),
  sourceShipmentId: Uuid.nullable().optional(),
  sourceShipmentLineId: Uuid.nullable().optional(),
  sourceBoxId: Uuid.nullable().optional(),
  sourcePrepBatchId: Uuid.nullable().optional(),
};
const WarehouseSourceRequestIds = {
  sourceId: Uuid.optional(),
  sourceType: z.enum(['receipt', 'shipment', 'shipment_line', 'box']).optional(),
  sourceReceiptId: Uuid.optional(),
  sourceShipmentId: Uuid.optional(),
  sourceShipmentLineId: Uuid.optional(),
  sourceBoxId: Uuid.optional(),
};
const warehouseSourceReferenceIsExclusive = (value: {
  sourceId?: string | null;
  sourceType?: 'receipt' | 'shipment' | 'shipment_line' | 'box' | 'prep_box' | 'prep_batch' | null;
  sourceReceiptId?: string | null;
  sourceShipmentId?: string | null;
  sourceShipmentLineId?: string | null;
  sourceBoxId?: string | null;
  sourcePrepBatchId?: string | null;
}): boolean => {
  if (value.sourceType === 'prep_batch') {
    return (
      value.sourceId === value.sourcePrepBatchId &&
      Boolean(value.sourcePrepBatchId) &&
      !value.sourceBoxId &&
      !value.sourceReceiptId &&
      !value.sourceShipmentId &&
      !value.sourceShipmentLineId
    );
  }
  if (value.sourceType === 'prep_box') {
    return (
      value.sourceId === value.sourceBoxId &&
      Boolean(value.sourcePrepBatchId) &&
      Boolean(value.sourceBoxId) &&
      !value.sourceReceiptId &&
      !value.sourceShipmentId &&
      !value.sourceShipmentLineId
    );
  }
  const typedSourceCount = [
    value.sourceReceiptId,
    value.sourceShipmentId,
    value.sourceShipmentLineId,
    value.sourceBoxId,
  ].filter(Boolean).length;
  const hasGenericSource = Boolean(value.sourceId || value.sourceType);
  const genericSourceIsComplete = Boolean(value.sourceId) === Boolean(value.sourceType);
  return genericSourceIsComplete && typedSourceCount + (hasGenericSource ? 1 : 0) === 1;
};

export const WarehouseServiceCatalogItem = z.object({
  id: Uuid,
  tenantId: Uuid,
  warehouseId: Uuid,
  code: z.string().trim().min(1),
  description: WarehouseDescription,
  defaultRateCents: WarehouseNonnegativeCents,
  currency: WarehouseCurrency,
  billingBasis: WarehouseBillingBasis,
  accountingTreatment: WarehouseAccountingTreatment,
  category: WarehouseServiceCategory.nullable(),
  active: z.boolean(),
  createdAt: Iso.optional(),
  updatedAt: Iso.optional(),
});
export type WarehouseServiceCatalogItem = z.infer<typeof WarehouseServiceCatalogItem>;

export const WarehouseWorkCharge = z
  .object({
    id: Uuid,
    tenantId: Uuid,
    warehouseId: Uuid,
    serviceId: Uuid,
    code: z.string().trim().min(1),
    description: WarehouseDescription,
    currency: WarehouseCurrency,
    quantityMillis: WarehouseMillis,
    rateCents: WarehouseNonnegativeCents,
    amountCents: WarehouseCents,
    billingBasis: WarehouseBillingBasis,
    accountingTreatment: WarehouseAccountingTreatment,
    allocationStatus: WarehouseAllocationStatus,
    blockReason: z.string().trim().min(1).nullable().optional(),
    idempotencyKey: z.string().trim().min(1).optional(),
    reversalOfChargeId: Uuid.nullable().optional(),
    ...WarehouseSourceIds,
  })
  .refine(warehouseSourceReferenceIsExclusive, {
    message: 'exactly one complete source reference is required',
  })
  .refine((value) => value.allocationStatus !== 'blocked' || Boolean(value.blockReason), {
    message: 'blocked charges require a block reason',
    path: ['blockReason'],
  });
export type WarehouseWorkCharge = z.infer<typeof WarehouseWorkCharge>;

export const WarehouseChargeAllocation = z
  .object({
    id: Uuid,
    tenantId: Uuid,
    warehouseId: Uuid,
    chargeId: Uuid,
    lotId: Uuid.nullable().optional(),
    shipmentAllocationId: Uuid.nullable().optional(),
    prepBatchId: Uuid.nullable().optional(),
    prepLineId: Uuid.nullable().optional(),
    boxContentId: Uuid.nullable().optional(),
    amountCents: WarehouseCents,
    vendorUnits: WarehouseMillis.optional(),
    billingBasis: WarehouseBillingBasis,
    accountingTreatment: WarehouseAccountingTreatment,
    status: WarehouseAllocationStatus,
    blockReason: z.string().trim().min(1).nullable().optional(),
    orderKey: z.string().trim().min(1).optional(),
    createdAt: Iso.optional(),
    updatedAt: Iso.optional(),
  })
  .refine((value) => value.status !== 'blocked' || Boolean(value.blockReason), {
    message: 'blocked allocations require a block reason',
    path: ['blockReason'],
  })
  .refine(
    (value) => {
      const prepValues = [value.prepBatchId, value.prepLineId, value.boxContentId];
      return (
        prepValues.every((entry) => entry == null) || prepValues.every((entry) => entry != null)
      );
    },
    {
      message: 'prep allocations require complete batch, line, and box-content provenance',
      path: ['prepBatchId'],
    }
  )
  .refine(
    (value) =>
      value.prepBatchId == null ||
      (value.lotId != null && value.prepLineId != null && value.boxContentId != null),
    {
      message: 'prep allocations require an exact lot and complete provenance',
      path: ['lotId'],
    }
  );
export type WarehouseChargeAllocation = z.infer<typeof WarehouseChargeAllocation>;

export const WarehouseChargeAllocationResponse = WarehouseChargeAllocation.and(
  z.object({
    lotId: Uuid.nullable(),
    shipmentAllocationId: Uuid.nullable(),
    prepBatchId: Uuid.nullable().default(null),
    prepLineId: Uuid.nullable().default(null),
    boxContentId: Uuid.nullable().default(null),
    vendorUnits: WarehouseInteger.positive(),
    orderKey: z.string().trim().min(1),
    createdAt: Iso,
    updatedAt: Iso,
  })
);
export type WarehouseChargeAllocationResponse = z.infer<typeof WarehouseChargeAllocationResponse>;
export const WarehouseWorkChargeResponse = z.object({
  charge: WarehouseWorkCharge.and(
    z.object({
      sourceId: Uuid.nullable(),
      sourceType: z
        .enum(['receipt', 'shipment', 'shipment_line', 'box', 'prep_box', 'prep_batch'])
        .nullable(),
      sourceReceiptId: Uuid.nullable(),
      sourceShipmentId: Uuid.nullable(),
      sourceShipmentLineId: Uuid.nullable(),
      sourceBoxId: Uuid.nullable(),
      sourcePrepBatchId: Uuid.nullable().default(null),
      blockReason: z.string().trim().min(1).nullable(),
      idempotencyKey: z.string().trim().min(1),
      reversalOfChargeId: Uuid.nullable(),
      activeInvoice: z
        .object({
          id: Uuid,
          status: WarehouseInvoiceStatus,
          documentType: z.enum(['standard', 'credit_note']),
        })
        .nullable()
        .default(null),
      createdAt: Iso,
      updatedAt: Iso,
    })
  ),
  allocations: z.array(WarehouseChargeAllocationResponse),
});
export type WarehouseWorkChargeResponse = z.infer<typeof WarehouseWorkChargeResponse>;
export const ListWarehouseWorkChargesResponse = z.array(WarehouseWorkChargeResponse);
export type ListWarehouseWorkChargesResponse = z.infer<typeof ListWarehouseWorkChargesResponse>;

export const WarehouseInvoice = z
  .object({
    id: Uuid,
    tenantId: Uuid,
    warehouseId: Uuid,
    currency: WarehouseCurrency,
    status: WarehouseInvoiceStatus,
    documentType: z.enum(['standard', 'credit_note']),
    creditNoteForInvoiceId: Uuid.nullable(),
    version: z.number().int().positive(),
    servicePeriodStart: Iso,
    servicePeriodEnd: Iso,
    issueDate: Iso,
    dueDate: Iso.nullable(),
    subtotalCents: WarehouseCents,
    discountCents: WarehouseNonnegativeCents,
    taxCents: WarehouseCents,
    totalCents: WarehouseCents,
    submittedAt: Iso.nullable(),
    approvedAt: Iso.nullable(),
    createdAt: Iso,
    updatedAt: Iso,
  })
  .refine(
    (value) =>
      value.documentType !== 'credit_note' ||
      (value.creditNoteForInvoiceId !== null &&
        value.discountCents === 0 &&
        value.subtotalCents <= 0 &&
        value.taxCents <= 0 &&
        value.totalCents <= 0),
    { message: 'credit note signs are invalid' }
  )
  .refine(
    (value) =>
      value.documentType !== 'standard' ||
      (value.creditNoteForInvoiceId === null &&
        value.subtotalCents >= 0 &&
        value.discountCents >= 0 &&
        value.taxCents >= 0 &&
        value.totalCents >= 0),
    { message: 'standard invoice signs are invalid' }
  )
  .refine(
    (value) =>
      BigInt(value.subtotalCents) - BigInt(value.discountCents) + BigInt(value.taxCents) ===
      BigInt(value.totalCents),
    {
      message: 'invoice total must equal subtotal - discount + tax',
      path: ['totalCents'],
    }
  );
export type WarehouseInvoice = z.infer<typeof WarehouseInvoice>;

export const WarehouseInvoiceLine = z
  .object({
    id: Uuid,
    tenantId: Uuid,
    invoiceId: Uuid,
    chargeId: Uuid,
    code: z.string().trim().min(1),
    description: WarehouseDescription,
    currency: WarehouseCurrency,
    quantityMillis: WarehouseMillis,
    rateCents: WarehouseCents,
    amountCents: WarehouseCents,
    discountCents: WarehouseNonnegativeCents,
    netAmountCents: WarehouseCents,
    billingBasis: WarehouseBillingBasis,
    accountingTreatment: WarehouseAccountingTreatment,
    reservationReleasedAt: Iso.nullable(),
  })
  .refine(
    (value) =>
      BigInt(value.netAmountCents) === BigInt(value.amountCents) - BigInt(value.discountCents),
    {
      message: 'net amount must equal amount minus discount',
      path: ['netAmountCents'],
    }
  )
  .refine((value) => value.amountCents > 0 || value.discountCents === 0, {
    message: 'non-positive gross lines cannot have a discount',
    path: ['discountCents'],
  })
  .refine((value) => value.amountCents <= 0 || value.discountCents <= value.amountCents, {
    message: 'discount cannot exceed a positive gross amount',
    path: ['discountCents'],
  });
export type WarehouseInvoiceLine = z.infer<typeof WarehouseInvoiceLine>;

export const WarehouseInvoicePayment = z.object({
  id: Uuid,
  tenantId: Uuid,
  warehouseId: Uuid,
  invoiceId: Uuid,
  amountCents: WarehouseInteger.positive(),
  paidAt: Iso,
  reference: z.string().trim().min(1).nullable(),
  reversedAt: Iso.nullable(),
  createdAt: Iso,
});
export type WarehouseInvoicePayment = z.infer<typeof WarehouseInvoicePayment>;

export const CreateWarehouseServiceRequest = z.object({
  warehouseId: Uuid,
  code: z.string().trim().min(1),
  description: WarehouseDescription,
  defaultRateCents: WarehouseNonnegativeCents,
  currency: WarehouseCurrency,
  billingBasis: WarehouseBillingBasis,
  accountingTreatment: WarehouseAccountingTreatment,
  category: WarehouseServiceCategory,
});
export type CreateWarehouseServiceRequest = z.infer<typeof CreateWarehouseServiceRequest>;

export const UpdateWarehouseServiceRequest = z
  .object({
    description: WarehouseDescription.optional(),
    defaultRateCents: WarehouseNonnegativeCents.optional(),
    currency: WarehouseCurrency.optional(),
    billingBasis: WarehouseBillingBasis.optional(),
    accountingTreatment: WarehouseAccountingTreatment.optional(),
    category: WarehouseServiceCategory.optional(),
    active: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'at least one service field is required',
  });
export type UpdateWarehouseServiceRequest = z.infer<typeof UpdateWarehouseServiceRequest>;

const WarehouseAllocationTarget = z.object({
  targetId: Uuid,
  orderKey: z.string().trim().min(1),
  weight: WarehouseInteger.positive(),
});

export const WarehouseAllocationBasis = z.enum([
  'vendor_unit',
  'listing_unit',
  'weight',
  'volume',
  'time',
]);
export type WarehouseAllocationBasis = z.infer<typeof WarehouseAllocationBasis>;

const LegacyCreateWarehouseWorkChargeRequest = z
  .object({
    serviceId: Uuid,
    quantityMillis: WarehouseInteger.positive(),
    idempotencyKey: z.string().trim().min(1),
    allocationBasis: WarehouseAllocationBasis.optional(),
    allocationTargets: z.array(WarehouseAllocationTarget).optional(),
    ...WarehouseSourceRequestIds,
  })
  .refine(warehouseSourceReferenceIsExclusive, {
    message: 'exactly one complete source reference is required',
  })
  .refine(
    (value) =>
      value.allocationTargets === undefined ||
      new Set(value.allocationTargets.map((target) => target.targetId)).size ===
        value.allocationTargets.length,
    {
      message: 'allocationTargets must have unique targetId values',
      path: ['allocationTargets'],
    }
  )
  .refine(
    (value) =>
      value.allocationTargets === undefined ||
      new Set(value.allocationTargets.map((target) => target.orderKey)).size ===
        value.allocationTargets.length,
    {
      message: 'allocationTargets must have unique orderKey values',
      path: ['allocationTargets'],
    }
  );

const WarehousePrepSelectedContent = z
  .object({
    boxContentId: Uuid,
    prepLineId: Uuid,
    vendorUnits: WarehouseInteger.positive(),
    weight: WarehouseInteger.positive().optional(),
  })
  .strict();

const WarehousePrepChargeRequest = z
  .object({
    serviceId: Uuid,
    idempotencyKey: z.string().trim().min(1),
    reason: z.string().trim().min(1),
    sourceType: z.literal('prep_box'),
    sourcePrepBatchId: Uuid,
    sourceBoxId: Uuid,
    expectedBatchVersion: WarehouseInteger.positive(),
    expectedBoxVersion: WarehouseInteger.positive(),
    quantityMillis: z.literal(1_000),
  })
  .strict();

const WarehousePrepBatchChargeRequest = z
  .object({
    serviceId: Uuid,
    idempotencyKey: z.string().trim().min(1),
    reason: z.string().trim().min(1),
    sourceType: z.literal('prep_batch'),
    sourcePrepBatchId: Uuid,
    expectedBatchVersion: WarehouseInteger.positive(),
    quantityMillis: WarehouseInteger.positive(),
    allocationBasis: WarehouseAllocationBasis.optional(),
    selectedContents: z.array(WarehousePrepSelectedContent).min(1),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.selectedContents.map((content) => content.boxContentId)).size ===
      value.selectedContents.length,
    { message: 'selectedContents must have unique boxContentId values' }
  );

export const CreateWarehouseWorkChargeRequest = z.union([
  LegacyCreateWarehouseWorkChargeRequest,
  WarehousePrepChargeRequest,
  WarehousePrepBatchChargeRequest,
]);
export type CreateWarehouseWorkChargeRequest = z.infer<typeof CreateWarehouseWorkChargeRequest>;

const NestedWarehousePrepBoxChargeRequest = z
  .object({
    serviceId: Uuid,
    idempotencyKey: z.string().trim().min(1),
    reason: z.string().trim().min(1),
    sourceType: z.literal('prep_box'),
    sourceBoxId: Uuid,
    expectedBatchVersion: WarehouseInteger.positive(),
    expectedBoxVersion: WarehouseInteger.positive(),
    quantityMillis: z.literal(1_000),
  })
  .strict();

const NestedWarehousePrepBatchChargeRequest = z
  .object({
    serviceId: Uuid,
    idempotencyKey: z.string().trim().min(1),
    reason: z.string().trim().min(1),
    sourceType: z.literal('prep_batch'),
    expectedBatchVersion: WarehouseInteger.positive(),
    quantityMillis: WarehouseInteger.positive(),
    allocationBasis: WarehouseAllocationBasis.optional(),
    selectedContents: z.array(WarehousePrepSelectedContent).min(1),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.selectedContents.map((content) => content.boxContentId)).size ===
      value.selectedContents.length,
    { message: 'selectedContents must have unique boxContentId values', path: ['selectedContents'] }
  );

/** Body for a prep-batch-nested charge command. The route binds the batch id. */
export const CreateWarehousePrepWorkChargeRequest = z.union([
  NestedWarehousePrepBoxChargeRequest,
  NestedWarehousePrepBatchChargeRequest,
]);
export type CreateWarehousePrepWorkChargeRequest = z.infer<
  typeof CreateWarehousePrepWorkChargeRequest
>;

export const ReverseWarehouseWorkChargeRequest = z.object({
  idempotencyKey: z.string().trim().min(1),
  reason: z.string().trim().min(1),
});
export type ReverseWarehouseWorkChargeRequest = z.infer<typeof ReverseWarehouseWorkChargeRequest>;

export const ListWarehouseWorkChargesQuery = z.object({
  warehouseId: Uuid.optional(),
  shipmentId: Uuid.optional(),
  sourceType: z
    .enum(['receipt', 'shipment', 'shipment_line', 'box', 'prep_box', 'prep_batch'])
    .optional(),
  allocationStatus: WarehouseAllocationStatus.optional(),
});
export type ListWarehouseWorkChargesQuery = z.infer<typeof ListWarehouseWorkChargesQuery>;

export const CreateWarehouseInvoiceRequest = z
  .object({
    warehouseId: Uuid,
    chargeIds: z
      .array(Uuid)
      .min(1)
      .refine((chargeIds) => new Set(chargeIds).size === chargeIds.length, {
        message: 'chargeIds must be unique',
      }),
    servicePeriodStart: Iso,
    servicePeriodEnd: Iso,
    issueDate: Iso,
    dueDate: Iso.nullable().optional(),
    discountCents: WarehouseNonnegativeCents.default(0),
    taxCents: WarehouseNonnegativeCents.default(0),
    idempotencyKey: z.string().trim().min(1),
  })
  .refine((value) => new Date(value.servicePeriodStart) <= new Date(value.servicePeriodEnd), {
    message: 'service period start must be on or before end',
    path: ['servicePeriodEnd'],
  })
  .refine((value) => !value.dueDate || new Date(value.dueDate) >= new Date(value.issueDate), {
    message: 'due date must be on or after issue date',
    path: ['dueDate'],
  });
export type CreateWarehouseInvoiceRequest = z.infer<typeof CreateWarehouseInvoiceRequest>;

export const UpdateWarehouseInvoiceDraftRequest = z
  .object({
    chargeIds: z
      .array(Uuid)
      .min(1)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'chargeIds must be unique',
      }),
    servicePeriodStart: Iso,
    servicePeriodEnd: Iso,
    issueDate: Iso,
    dueDate: Iso.nullable().optional(),
    discountCents: WarehouseNonnegativeCents.default(0),
    taxCents: WarehouseNonnegativeCents.default(0),
    expectedVersion: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1),
  })
  .refine((value) => new Date(value.servicePeriodStart) <= new Date(value.servicePeriodEnd), {
    path: ['servicePeriodEnd'],
    message: 'service period start must be on or before end',
  })
  .refine((value) => !value.dueDate || new Date(value.dueDate) >= new Date(value.issueDate), {
    path: ['dueDate'],
    message: 'due date must be on or after issue date',
  });
export type UpdateWarehouseInvoiceDraftRequest = z.infer<typeof UpdateWarehouseInvoiceDraftRequest>;

export const TransitionWarehouseInvoiceRequest = z
  .object({
    action: z.enum(['submit', 'approve', 'dispute', 'void']),
    reason: z.string().trim().min(1).optional(),
    expectedVersion: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1),
  })
  .refine((value) => value.action !== 'submit' || value.reason === undefined, {
    message: 'submit does not accept a reason',
    path: ['reason'],
  });
export type TransitionWarehouseInvoiceRequest = z.infer<typeof TransitionWarehouseInvoiceRequest>;

export const RecordWarehouseInvoicePaymentRequest = z.object({
  amountCents: WarehouseInteger.positive(),
  paidAt: Iso,
  reference: z.string().trim().min(1).nullable().optional(),
  expectedVersion: z.number().int().positive(),
  idempotencyKey: z.string().trim().min(1),
});
export type RecordWarehouseInvoicePaymentRequest = z.infer<
  typeof RecordWarehouseInvoicePaymentRequest
>;

export const ListWarehouseInvoicesQuery = z.object({
  warehouseId: Uuid.optional(),
  status: z.preprocess((value) => {
    const values = Array.isArray(value) ? value : value === undefined ? undefined : [value];
    return values ? [...new Set(values)] : values;
  }, z.array(WarehouseInvoiceStatus).optional()),
  currency: WarehouseCurrency.optional(),
});
export type ListWarehouseInvoicesQuery = z.infer<typeof ListWarehouseInvoicesQuery>;

export const WarehouseInvoiceSummary = z.object({
  invoice: WarehouseInvoice,
  paidCents: WarehouseCents,
  balanceCents: WarehouseCents,
  paymentStatus: z.enum(['unpaid', 'partial', 'paid', 'not_applicable']),
});
export type WarehouseInvoiceSummary = z.infer<typeof WarehouseInvoiceSummary>;

export const WarehouseInvoiceLineDetail = z.object({
  line: WarehouseInvoiceLine,
  allocations: z.array(
    z.object({
      allocationId: Uuid,
      lotId: Uuid.nullable(),
      shipmentAllocationId: Uuid.nullable(),
      amountCents: WarehouseCents,
      vendorUnits: WarehouseInteger,
      orderKey: z.string(),
    })
  ),
});
export type WarehouseInvoiceLineDetail = z.infer<typeof WarehouseInvoiceLineDetail>;

export const WarehouseInvoiceDetailResponse = z.object({
  invoice: WarehouseInvoice,
  appendix: z.lazy(() => WarehouseInvoiceAppendixPresentation).nullable(),
  lines: z.array(WarehouseInvoiceLineDetail),
  payments: z.array(WarehouseInvoicePayment),
  paidCents: WarehouseCents,
  balanceCents: WarehouseCents,
  paymentStatus: z.enum(['unpaid', 'partial', 'paid', 'not_applicable']),
});
export type WarehouseInvoiceDetailResponse = z.infer<typeof WarehouseInvoiceDetailResponse>;
export const ListWarehouseInvoicesResponse = z.array(WarehouseInvoiceSummary);
export type ListWarehouseInvoicesResponse = z.infer<typeof ListWarehouseInvoicesResponse>;
export const CreateWarehouseCreditNoteRequest = z
  .object({
    invoiceLineIds: z
      .array(Uuid)
      .min(1)
      .refine((v) => new Set(v).size === v.length),
    issueDate: Iso,
    servicePeriodStart: Iso,
    servicePeriodEnd: Iso,
    taxCents: WarehouseInteger.max(0).default(0),
    reason: z.string().trim().min(1),
    expectedVersion: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1),
  })
  .refine((v) => new Date(v.servicePeriodStart) <= new Date(v.servicePeriodEnd), {
    path: ['servicePeriodEnd'],
    message: 'service period start must be on or before end',
  });
export type CreateWarehouseCreditNoteRequest = z.infer<typeof CreateWarehouseCreditNoteRequest>;
export const RecordWarehouseInvoicePaymentResponse = z.object({
  invoice: WarehouseInvoiceSummary,
  payment: WarehouseInvoicePayment,
});
export type RecordWarehouseInvoicePaymentResponse = z.infer<
  typeof RecordWarehouseInvoicePaymentResponse
>;
export const ReverseWarehouseInvoicePaymentRequest = z.object({
  reason: z.string().trim().min(1),
  expectedVersion: z.number().int().positive(),
  idempotencyKey: z.string().trim().min(1),
});
export type ReverseWarehouseInvoicePaymentRequest = z.infer<
  typeof ReverseWarehouseInvoicePaymentRequest
>;
export const ReverseWarehouseInvoicePaymentResponse = RecordWarehouseInvoicePaymentResponse;
export type ReverseWarehouseInvoicePaymentResponse = z.infer<
  typeof ReverseWarehouseInvoicePaymentResponse
>;

// ─────────────────────────────────────────────────────────
// Warehouse expense period attestation (R1 item 4, 2026-09-01 design doc)
// ─────────────────────────────────────────────────────────

/** POST /api/warehouse/billing/period-expenses/attest */
export const AttestWarehousePeriodExpensesRequest = z
  .object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be in YYYY-MM format'),
    attested: z.literal(true),
    note: z.string().trim().min(1).max(2000).optional(),
  })
  .strict();
export type AttestWarehousePeriodExpensesRequest = z.infer<
  typeof AttestWarehousePeriodExpensesRequest
>;

export const WarehouseExpenseAttestation = z.object({
  month: z.string(),
  currency: z.string(),
  expenseCount: z.number().int(),
  expenseTotalCents: z.number().int(),
  attestedAt: Iso,
  attestedBy: z.string().nullable(),
  stale: z.boolean(),
});
export type WarehouseExpenseAttestation = z.infer<typeof WarehouseExpenseAttestation>;

export const AttestWarehousePeriodExpensesResponse = WarehouseExpenseAttestation;
export type AttestWarehousePeriodExpensesResponse = z.infer<
  typeof AttestWarehousePeriodExpensesResponse
>;

/** GET /api/warehouse/billing/period-expenses/attestations */
export const ListWarehousePeriodExpenseAttestationsResponse = z.array(WarehouseExpenseAttestation);
export type ListWarehousePeriodExpenseAttestationsResponse = z.infer<
  typeof ListWarehousePeriodExpenseAttestationsResponse
>;

// R1 item 4 follow-up (2026-09-02) — "can I see a summary with a test
// button instead of going into details?" (Faraz). GET
// /billing/period-expenses/preview itemizes exactly what a month's
// attestation covers so Finance can show a row summary and an itemized
// confirm dialog instead of a bare "Expenses complete for June 2026?"
// question.
const MONTH_FORMAT_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export const WarehouseExpensePeriodPreviewWarehouseItem = z.object({
  id: Uuid,
  description: z.string(),
  amountCents: z.number().int(),
  kind: z.enum(['service_line', 'tax']),
  invoiceId: Uuid.nullable(),
  invoiceLabel: z.string().nullable(),
  recognizedAt: Iso,
});
export type WarehouseExpensePeriodPreviewWarehouseItem = z.infer<
  typeof WarehouseExpensePeriodPreviewWarehouseItem
>;

export const WarehouseExpensePeriodPreviewOperatingItem = z.object({
  id: Uuid,
  description: z.string(),
  amountCents: z.number().int(),
  category: OperatingExpenseCategory,
  recurring: z.boolean(),
});
export type WarehouseExpensePeriodPreviewOperatingItem = z.infer<
  typeof WarehouseExpensePeriodPreviewOperatingItem
>;

export const WarehouseExpensePeriodPreview = z.object({
  month: z.string(),
  currency: z.literal('USD'),
  warehouse: z.object({
    count: z.number().int(),
    totalCents: z.number().int(),
    items: z.array(WarehouseExpensePeriodPreviewWarehouseItem),
  }),
  operating: z.object({
    count: z.number().int(),
    totalCents: z.number().int(),
    items: z.array(WarehouseExpensePeriodPreviewOperatingItem),
  }),
  attested: z
    .object({
      attestedAt: Iso,
      stale: z.boolean(),
    })
    .nullable(),
});
export type WarehouseExpensePeriodPreview = z.infer<typeof WarehouseExpensePeriodPreview>;

/** GET /api/warehouse/billing/period-expenses/preview */
export const WarehouseExpensePeriodPreviewResponse = z.object({
  previews: z.array(WarehouseExpensePeriodPreview),
});
export type WarehouseExpensePeriodPreviewResponse = z.infer<
  typeof WarehouseExpensePeriodPreviewResponse
>;

export const WarehouseExpensePeriodPreviewQuery = z.object({
  months: z
    .string()
    .min(1)
    .transform((value) =>
      value
        .split(',')
        .map((month) => month.trim())
        .filter((month) => month.length > 0)
    )
    .pipe(
      z
        .array(z.string().regex(MONTH_FORMAT_RE, 'month must be in YYYY-MM format'))
        .min(1, 'at least one month is required')
        .max(12, 'at most 12 months per request')
    ),
});
export type WarehouseExpensePeriodPreviewQuery = z.infer<typeof WarehouseExpensePeriodPreviewQuery>;

// ─────────────────────────────────────────────────────────
// Immutable prep-dispatch and invoice-appendix presentations (Task 8)
// ─────────────────────────────────────────────────────────

export const ManualPrepDispatchLabelEvidence = z
  .object({
    boxReference: PrepBoxReference,
    attestedApplied: z.literal(true),
    reason: z.string().trim().min(1),
    externalReference: z.string().trim().min(1).optional(),
  })
  .strict();
export type ManualPrepDispatchLabelEvidence = z.infer<typeof ManualPrepDispatchLabelEvidence>;

/** POST /api/warehouse/prep-batches/:id/dispatch */
export const DispatchWarehousePrepBatchRequest = z
  .object({
    expectedVersion: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1),
    workComplete: z.literal(true),
    manualLabelEvidence: z.array(ManualPrepDispatchLabelEvidence).max(500),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.manualLabelEvidence.map((evidence) => evidence.boxReference)).size ===
      value.manualLabelEvidence.length,
    {
      message: 'manualLabelEvidence must contain each box at most once',
      path: ['manualLabelEvidence'],
    }
  );
export type DispatchWarehousePrepBatchRequest = z.infer<typeof DispatchWarehousePrepBatchRequest>;

export const WarehousePrepDispatchReceipt = z
  .object({
    prepId: PrepReference,
    status: z.literal('dispatched'),
    version: z.number().int().positive(),
    dispatchedAt: Iso,
    dispatchedBy: WarehousePrepActor,
    workComplete: z.literal(true),
    manualLabelEvidence: z.array(ManualPrepDispatchLabelEvidence),
  })
  .strict();
export type WarehousePrepDispatchReceipt = z.infer<typeof WarehousePrepDispatchReceipt>;

export const WarehousePrepDispatchResponse = z
  .object({ replayed: z.boolean(), dispatch: WarehousePrepDispatchReceipt })
  .strict();
export type WarehousePrepDispatchResponse = z.infer<typeof WarehousePrepDispatchResponse>;

export const WarehouseInvoiceAppendixPresentation = z
  .object({
    invoiceId: Uuid,
    version: z.number().int().positive(),
    frozen: z.boolean(),
    fingerprint: z.string().trim().min(1).nullable(),
    frozenAt: Iso.nullable(),
    subtotalCents: WarehouseNonnegativeCents,
    discountCents: WarehouseNonnegativeCents,
    taxCents: WarehouseNonnegativeCents,
    totalCents: WarehouseNonnegativeCents,
    groups: z.array(
      z
        .object({
          category: WarehouseServiceCategory,
          grossCents: WarehouseNonnegativeCents,
          discountCents: WarehouseNonnegativeCents,
          netCents: WarehouseNonnegativeCents,
          lineCount: z.number().int().nonnegative(),
        })
        .strict()
    ),
    lines: z.array(
      z
        .object({
          id: Uuid,
          chargeId: Uuid.optional(),
          serviceId: Uuid.optional(),
          sourceType: z.string().trim().min(1).nullable().optional(),
          sourceId: Uuid.nullable().optional(),
          sourceReceiptId: Uuid.nullable().optional(),
          sourceShipmentId: Uuid.nullable().optional(),
          sourceShipmentLineId: Uuid.nullable().optional(),
          sourceBoxId: Uuid.nullable().optional(),
          sourcePrepBatchId: Uuid.nullable().optional(),
          createdBy: Uuid.nullable().optional(),
          reason: z.string().nullable().optional(),
          category: WarehouseServiceCategory,
          prepReference: PrepReference.nullable(),
          poReference: z.string().trim().min(1).nullable(),
          poReferences: z.array(z.string().trim().min(1)).default([]),
          receiptId: Uuid.nullable(),
          receiptIds: z.array(Uuid).default([]),
          workerName: z.string().trim().min(1).nullable(),
          quantityMillis: WarehouseInteger.positive(),
          rateCents: WarehouseNonnegativeCents,
          grossCents: WarehouseNonnegativeCents,
          discountCents: WarehouseNonnegativeCents,
          netCents: WarehouseNonnegativeCents,
          code: z.string().trim().min(1),
          description: WarehouseDescription,
          currency: WarehouseCurrency,
          billingBasis: WarehouseBillingBasis.optional(),
          accountingTreatment: WarehouseAccountingTreatment,
          allocations: z.array(
            z
              .object({
                prepBoxReference: PrepBoxReference.nullable(),
                sellerSku: z.string().trim().min(1).nullable(),
                lotId: Uuid.nullable(),
                chargeAllocationId: Uuid.nullable(),
                shipmentAllocationId: Uuid.nullable(),
                prepBatchId: Uuid.nullable().default(null),
                prepLineId: Uuid.nullable().default(null),
                boxContentId: Uuid.nullable().default(null),
                vendorUnits: WarehouseInteger.positive().optional(),
                orderKey: z.string().trim().min(1).optional(),
                poReference: z.string().trim().min(1).nullable().default(null),
                receiptId: Uuid.nullable().default(null),
                allocationGrossCents: WarehouseNonnegativeCents,
                allocationNetCents: WarehouseNonnegativeCents,
              })
              .strict()
          ),
        })
        .strict()
    ),
  })
  .strict()
  .refine(
    (value) => (value.frozen ? value.frozenAt !== null && value.fingerprint !== null : true),
    { message: 'frozen appendices require timestamp and fingerprint', path: ['frozen'] }
  )
  .refine(
    (value) => (!value.frozen ? value.frozenAt === null && value.fingerprint === null : true),
    { message: 'draft appendix previews cannot claim immutable evidence', path: ['frozen'] }
  )
  .superRefine((value, context) => {
    const issue = (path: Array<string | number>, message: string) =>
      context.addIssue({ code: z.ZodIssueCode.custom, path, message });
    const groupsByCategory = new Map<string, (typeof value.groups)[number]>();
    const linesByCategory = new Map<string, (typeof value.lines)[number][]>();

    for (const [index, group] of value.groups.entries()) {
      if (groupsByCategory.has(group.category)) {
        issue(['groups', index, 'category'], 'appendix groups must have unique categories');
      }
      groupsByCategory.set(group.category, group);
    }
    for (const line of value.lines) {
      const lines = linesByCategory.get(line.category) ?? [];
      lines.push(line);
      linesByCategory.set(line.category, lines);
    }

    for (const [index, line] of value.lines.entries()) {
      if (
        sumWarehouseCents(line.allocations.map((allocation) => allocation.allocationGrossCents)) !==
        BigInt(line.grossCents)
      ) {
        issue(['lines', index, 'allocations'], 'allocation gross must conserve line gross');
      }
      if (
        sumWarehouseCents(line.allocations.map((allocation) => allocation.allocationNetCents)) !==
        BigInt(line.netCents)
      ) {
        issue(['lines', index, 'allocations'], 'allocation net must conserve line net');
      }
    }

    for (const [index, group] of value.groups.entries()) {
      const lines = linesByCategory.get(group.category) ?? [];
      if (!linesByCategory.has(group.category)) {
        issue(['groups', index, 'category'], 'appendix groups must exactly cover line categories');
      }
      if (lines.length !== group.lineCount) {
        issue(['groups', index, 'lineCount'], 'group line count must match its category lines');
      }
      if (sumWarehouseCents(lines.map((line) => line.grossCents)) !== BigInt(group.grossCents)) {
        issue(['groups', index, 'grossCents'], 'group gross must conserve its lines');
      }
      if (
        sumWarehouseCents(lines.map((line) => line.discountCents)) !== BigInt(group.discountCents)
      ) {
        issue(['groups', index, 'discountCents'], 'group discount must conserve its lines');
      }
      if (sumWarehouseCents(lines.map((line) => line.netCents)) !== BigInt(group.netCents)) {
        issue(['groups', index, 'netCents'], 'group net must conserve its lines');
      }
    }
    for (const category of linesByCategory.keys()) {
      if (!groupsByCategory.has(category)) {
        issue(['groups'], 'appendix groups must exactly cover line categories');
      }
    }

    if (
      sumWarehouseCents(value.groups.map((group) => group.grossCents)) !==
      BigInt(value.subtotalCents)
    ) {
      issue(['subtotalCents'], 'groups must conserve invoice subtotal');
    }
    if (
      sumWarehouseCents(value.groups.map((group) => group.discountCents)) !==
      BigInt(value.discountCents)
    ) {
      issue(['discountCents'], 'groups must conserve invoice discount');
    }
    const groupNet = sumWarehouseCents(value.groups.map((group) => group.netCents));
    if (groupNet !== BigInt(value.subtotalCents) - BigInt(value.discountCents)) {
      issue(['subtotalCents'], 'groups must conserve invoice net');
    }
    if (groupNet + BigInt(value.taxCents) !== BigInt(value.totalCents)) {
      issue(['totalCents'], 'group net plus tax must conserve invoice total');
    }
  });
export type WarehouseInvoiceAppendixPresentation = z.infer<
  typeof WarehouseInvoiceAppendixPresentation
>;

// ─────────────────────────────────────────────────────────
// Warehouse physical profiles and carton planning (Task 4.1)
// ─────────────────────────────────────────────────────────

/** Canonical dimensions are decimal strings so the API can carry arbitrary
 * integer micrometres/milligrams without a lossy JavaScript number. */
export const WarehousePhysicalIntegerString = z
  .string()
  .regex(/^[1-9][0-9]*$/, 'must be a positive integer string')
  .refine((value) => BigInt(value) <= BigInt(Number.MAX_SAFE_INTEGER), 'must be a safe integer');
export const WarehousePhysicalNonnegativeIntegerString = z
  .string()
  .regex(/^(?:0|[1-9][0-9]*)$/, 'must be a nonnegative integer string')
  .refine((value) => BigInt(value) <= BigInt(Number.MAX_SAFE_INTEGER), 'must be a safe integer');
export const WarehousePhysicalDimensions = z
  .object({
    lengthUm: WarehousePhysicalIntegerString,
    widthUm: WarehousePhysicalIntegerString,
    heightUm: WarehousePhysicalIntegerString,
  })
  .strict();
export type WarehousePhysicalDimensions = z.infer<typeof WarehousePhysicalDimensions>;

export const WarehousePhysicalIdentity = z
  .object({
    sellerSku: z.string().trim().min(1),
    asin: z.string().trim().min(1).nullable(),
    unitsPerListing: z.number().int().positive().safe(),
  })
  .strict();
export type WarehousePhysicalIdentity = z.infer<typeof WarehousePhysicalIdentity>;

export const WarehousePhysicalProfileTarget = z.enum(['retail_item', 'listing_package']);
export type WarehousePhysicalProfileTarget = z.infer<typeof WarehousePhysicalProfileTarget>;
export const WarehousePhysicalEvidenceScope = z.enum(['item', 'package']);
export type WarehousePhysicalEvidenceScope = z.infer<typeof WarehousePhysicalEvidenceScope>;
export const WarehousePhysicalConfidence = z.enum(['high', 'medium', 'low']);
export type WarehousePhysicalConfidence = z.infer<typeof WarehousePhysicalConfidence>;

export const WarehousePhysicalOrientationRule = z.enum(['any', 'upright_only']);
export type WarehousePhysicalOrientationRule = z.infer<typeof WarehousePhysicalOrientationRule>;
export const WarehousePhysicalStackingRule = z.enum(['stackable', 'top_only', 'solo']);
export type WarehousePhysicalStackingRule = z.infer<typeof WarehousePhysicalStackingRule>;

// ─────────────────────────────────────────────────────────
// Warehouse physical-profile planning API (Task 4.5)
// ─────────────────────────────────────────────────────────

const WarehousePhysicalSafeInteger = z.number().int().safe();
const WarehousePhysicalVersion = WarehousePhysicalSafeInteger.positive();
const WarehousePhysicalNonnegativeCents = WarehousePhysicalSafeInteger.nonnegative();
const WarehousePhysicalMarketplaceId = z.string().trim().min(1);
export const WarehousePhysicalStoredStackingRule = z.enum([
  'standard',
  'fragile',
  'top_only',
  'solo',
]);
const WarehousePhysicalPackingGroup = z.string().trim().min(1).nullable();
const WarehousePhysicalIncompatibleGroups = z
  .array(z.string().trim().min(1))
  .refine((groups) => new Set(groups).size === groups.length, {
    message: 'incompatibleGroups must be unique',
  });

export type WarehousePhysicalStoredStackingRule = z.infer<
  typeof WarehousePhysicalStoredStackingRule
>;

const WarehouseRetailPhysicalProfileQuery = z
  .object({
    target: z.literal('retail_item'),
    vendorItemNo: z.string().trim().min(1),
    warehouseId: Uuid.optional(),
  })
  .strict();
const WarehouseListingPhysicalProfileQuery = z
  .object({
    target: z.literal('listing_package'),
    sellerSku: z.string().trim().min(1),
    asin: z
      .string()
      .trim()
      .min(1)
      .optional()
      .transform((value) => value ?? null),
    unitsPerListing: WarehousePhysicalSafeInteger.positive(),
    warehouseId: Uuid.optional(),
    marketplaceId: WarehousePhysicalMarketplaceId.optional(),
  })
  .strict();
export const WarehousePhysicalProfileQuery = z.discriminatedUnion('target', [
  WarehouseRetailPhysicalProfileQuery,
  WarehouseListingPhysicalProfileQuery,
]);
export type WarehousePhysicalProfileQuery = z.infer<typeof WarehousePhysicalProfileQuery>;

const WarehousePhysicalObservationValues = {
  measurementScope: WarehousePhysicalEvidenceScope,
  dimensions: WarehousePhysicalDimensions,
  weightMg: WarehousePhysicalIntegerString,
  orientation: WarehousePhysicalOrientationRule,
  stacking: WarehousePhysicalStoredStackingRule,
  packingGroup: WarehousePhysicalPackingGroup,
  incompatibleGroups: WarehousePhysicalIncompatibleGroups,
  treatmentEnvelope: WarehousePhysicalDimensions.nullable(),
};
const WarehouseRetailPhysicalObservationRequest = z
  .object({
    target: z.literal('retail_item'),
    vendorItemNo: z.string().trim().min(1),
    warehouseId: Uuid.optional(),
    ...WarehousePhysicalObservationValues,
    observedAt: Iso,
    idempotencyKey: z.string().trim().min(1),
  })
  .strict();
const WarehouseListingPhysicalObservationRequest = z
  .object({
    target: z.literal('listing_package'),
    sellerSku: z.string().trim().min(1),
    asin: z.string().trim().min(1).nullable(),
    unitsPerListing: WarehousePhysicalSafeInteger.positive(),
    warehouseId: Uuid.optional(),
    ...WarehousePhysicalObservationValues,
    observedAt: Iso,
    idempotencyKey: z.string().trim().min(1),
  })
  .strict();
export const AppendWarehousePhysicalProfileObservationRequest = z.discriminatedUnion('target', [
  WarehouseRetailPhysicalObservationRequest,
  WarehouseListingPhysicalObservationRequest,
]);
export type AppendWarehousePhysicalProfileObservationRequest = z.infer<
  typeof AppendWarehousePhysicalProfileObservationRequest
>;

const WarehouseRetailPhysicalProfileObservation = z
  .object({
    id: Uuid,
    target: z.literal('retail_item'),
    vendorItemNo: z.string().trim().min(1),
    warehouseId: Uuid,
    ...WarehousePhysicalObservationValues,
    observedAt: Iso,
  })
  .strict();
const WarehouseListingPhysicalProfileObservation = z
  .object({
    id: Uuid,
    target: z.literal('listing_package'),
    sellerSku: z.string().trim().min(1),
    asin: z.string().trim().min(1).nullable(),
    unitsPerListing: WarehousePhysicalSafeInteger.positive(),
    warehouseId: Uuid,
    ...WarehousePhysicalObservationValues,
    observedAt: Iso,
  })
  .strict();
export const WarehousePhysicalProfileObservation = z.discriminatedUnion('target', [
  WarehouseRetailPhysicalProfileObservation,
  WarehouseListingPhysicalProfileObservation,
]);
export type WarehousePhysicalProfileObservation = z.infer<
  typeof WarehousePhysicalProfileObservation
>;
export const AppendWarehousePhysicalProfileObservationResponse = z
  .object({ observation: WarehousePhysicalProfileObservation, replayed: z.boolean() })
  .strict();
export type AppendWarehousePhysicalProfileObservationResponse = z.infer<
  typeof AppendWarehousePhysicalProfileObservationResponse
>;

export const WarehouseAmazonPhysicalEvidence = z
  .object({
    id: Uuid,
    marketplaceId: WarehousePhysicalMarketplaceId,
    sellerSku: z.string().trim().min(1),
    asin: z.string().trim().min(1),
    unitsPerListing: WarehousePhysicalSafeInteger.positive(),
    measurementScope: WarehousePhysicalEvidenceScope,
    dimensions: WarehousePhysicalDimensions.nullable(),
    weightMg: WarehousePhysicalIntegerString.nullable(),
    observedAt: Iso,
    lastSeenAt: Iso,
  })
  .strict();
export type WarehouseAmazonPhysicalEvidence = z.infer<typeof WarehouseAmazonPhysicalEvidence>;

const WarehouseEffectivePhysicalProfileValues = {
  dimensions: WarehousePhysicalDimensions,
  weightMg: WarehousePhysicalIntegerString,
  confidence: WarehousePhysicalConfidence,
  reason: z.string().trim().min(1).nullable(),
  observedAt: Iso,
};
const WarehouseRetailEffectivePhysicalProfile = z
  .object({
    source: z.literal('warehouse'),
    target: z.literal('retail_item'),
    warehouseId: Uuid,
    vendorItemNo: z.string().trim().min(1),
    measurementScope: WarehousePhysicalEvidenceScope,
    ...WarehouseEffectivePhysicalProfileValues,
  })
  .strict();
const WarehouseListingWarehouseEffectivePhysicalProfile = z
  .object({
    source: z.literal('warehouse'),
    target: z.literal('listing_package'),
    warehouseId: Uuid,
    sellerSku: z.string().trim().min(1),
    asin: z.string().trim().min(1).nullable(),
    unitsPerListing: WarehousePhysicalSafeInteger.positive(),
    measurementScope: z.literal('package'),
    ...WarehouseEffectivePhysicalProfileValues,
  })
  .strict();
const WarehouseListingAmazonEffectivePhysicalProfile = z
  .object({
    source: z.literal('amazon_catalog'),
    target: z.literal('listing_package'),
    marketplaceId: WarehousePhysicalMarketplaceId,
    sellerSku: z.string().trim().min(1),
    asin: z.string().trim().min(1).nullable(),
    unitsPerListing: WarehousePhysicalSafeInteger.positive(),
    measurementScope: z.literal('package'),
    lastSeenAt: Iso,
    ...WarehouseEffectivePhysicalProfileValues,
  })
  .strict();
export const WarehouseEffectivePhysicalProfile = z.union([
  WarehouseRetailEffectivePhysicalProfile,
  WarehouseListingWarehouseEffectivePhysicalProfile,
  WarehouseListingAmazonEffectivePhysicalProfile,
]);
export type WarehouseEffectivePhysicalProfile = z.infer<typeof WarehouseEffectivePhysicalProfile>;

export const WarehousePhysicalProfileResponse = z
  .object({
    warehouseEvidence: z.array(WarehousePhysicalProfileObservation),
    amazonEvidence: z.array(WarehouseAmazonPhysicalEvidence),
    effective: WarehouseEffectivePhysicalProfile.nullable(),
  })
  .strict();
export type WarehousePhysicalProfileResponse = z.infer<typeof WarehousePhysicalProfileResponse>;

const WarehouseCartonFields = {
  code: z
    .string()
    .trim()
    .min(1)
    .transform((value) => value.toUpperCase()),
  description: z.string().trim().min(1).nullable(),
  internal: WarehousePhysicalDimensions,
  external: WarehousePhysicalDimensions,
  tareWeightMg: WarehousePhysicalNonnegativeIntegerString,
  maxGrossWeightMg: WarehousePhysicalIntegerString,
  materialCostCents: WarehousePhysicalNonnegativeCents,
  currency: z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .refine((value) => /^[A-Z]{3}$/.test(value), 'currency must be a 3-letter ISO code'),
  active: z.boolean(),
};
const warehouseCartonGeometryIsValid = (value: {
  internal: WarehousePhysicalDimensions;
  external: WarehousePhysicalDimensions;
  tareWeightMg: string;
  maxGrossWeightMg: string;
}): boolean =>
  BigInt(value.external.lengthUm) >= BigInt(value.internal.lengthUm) &&
  BigInt(value.external.widthUm) >= BigInt(value.internal.widthUm) &&
  BigInt(value.external.heightUm) >= BigInt(value.internal.heightUm) &&
  BigInt(value.maxGrossWeightMg) > BigInt(value.tareWeightMg);

export const WarehouseCarton = z
  .object({
    id: Uuid,
    warehouseId: Uuid,
    ...WarehouseCartonFields,
    version: WarehousePhysicalVersion,
    createdAt: Iso,
    updatedAt: Iso,
  })
  .strict()
  .refine(warehouseCartonGeometryIsValid, {
    message:
      'external dimensions must cover internal dimensions and max gross weight must exceed tare',
  });
export type WarehouseCarton = z.infer<typeof WarehouseCarton>;

export const ListWarehouseCartonsQuery = z.object({ warehouseId: Uuid }).strict();
export type ListWarehouseCartonsQuery = z.infer<typeof ListWarehouseCartonsQuery>;
export const ListWarehouseCartonsResponse = z.array(WarehouseCarton);
export type ListWarehouseCartonsResponse = z.infer<typeof ListWarehouseCartonsResponse>;

export const CreateWarehouseCartonRequest = z
  .object({ warehouseId: Uuid, ...WarehouseCartonFields })
  .strict()
  .refine(warehouseCartonGeometryIsValid, {
    message:
      'external dimensions must cover internal dimensions and max gross weight must exceed tare',
  });
export type CreateWarehouseCartonRequest = z.infer<typeof CreateWarehouseCartonRequest>;
export const CreateWarehouseCartonResponse = z.object({ carton: WarehouseCarton }).strict();
export type CreateWarehouseCartonResponse = z.infer<typeof CreateWarehouseCartonResponse>;

export const UpdateWarehouseCartonRequest = z
  .object({
    expectedVersion: WarehousePhysicalVersion,
    code: WarehouseCartonFields.code.optional(),
    description: WarehouseCartonFields.description.optional(),
    internal: WarehouseCartonFields.internal.optional(),
    external: WarehouseCartonFields.external.optional(),
    tareWeightMg: WarehouseCartonFields.tareWeightMg.optional(),
    maxGrossWeightMg: WarehouseCartonFields.maxGrossWeightMg.optional(),
    materialCostCents: WarehouseCartonFields.materialCostCents.optional(),
    currency: WarehouseCartonFields.currency.optional(),
    active: WarehouseCartonFields.active.optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 1, {
    message: 'at least one carton field is required',
  });
export type UpdateWarehouseCartonRequest = z.infer<typeof UpdateWarehouseCartonRequest>;
export const UpdateWarehouseCartonResponse = CreateWarehouseCartonResponse;
export type UpdateWarehouseCartonResponse = z.infer<typeof UpdateWarehouseCartonResponse>;

const WarehouseCartonizationSettingsFields = {
  clearanceUm: WarehousePhysicalNonnegativeIntegerString,
  perCartonHandlingCents: WarehousePhysicalNonnegativeCents,
  transportCentsPerKg: WarehousePhysicalNonnegativeCents,
  dimensionalWeightDivisorCm3PerKg: WarehousePhysicalSafeInteger.positive(),
  currency: WarehouseCartonFields.currency,
};
export const WarehouseCartonizationSettings = z
  .object({
    warehouseId: Uuid,
    ...WarehouseCartonizationSettingsFields,
    version: WarehousePhysicalVersion,
    createdAt: Iso,
    updatedAt: Iso,
  })
  .strict();
export type WarehouseCartonizationSettings = z.infer<typeof WarehouseCartonizationSettings>;
export const GetWarehouseCartonizationSettingsQuery = z.object({ warehouseId: Uuid }).strict();
export type GetWarehouseCartonizationSettingsQuery = z.infer<
  typeof GetWarehouseCartonizationSettingsQuery
>;
export const WarehouseCartonizationSettingsResponse = z
  .object({ settings: WarehouseCartonizationSettings.nullable() })
  .strict();
export type WarehouseCartonizationSettingsResponse = z.infer<
  typeof WarehouseCartonizationSettingsResponse
>;
export const WarehouseCartonizationSettingsRequest = z
  .object({
    warehouseId: Uuid,
    expectedVersion: WarehousePhysicalVersion.optional(),
    ...WarehouseCartonizationSettingsFields,
  })
  .strict();
export type WarehouseCartonizationSettingsRequest = z.infer<
  typeof WarehouseCartonizationSettingsRequest
>;

export const WarehouseCartonRecommendationBlockerCode = z.enum([
  'mapping_required',
  'physical_profile_required',
  'carton_catalog_empty',
  'cartonization_settings_required',
  'currency_mismatch',
  'no_eligible_carton',
]);
export type WarehouseCartonRecommendationBlockerCode = z.infer<
  typeof WarehouseCartonRecommendationBlockerCode
>;
export const WarehouseCartonRecommendationBlocker = z
  .object({
    code: WarehouseCartonRecommendationBlockerCode,
    message: z.string().trim().min(1),
    lineId: Uuid.optional(),
    sellerSku: z.string().trim().min(1).optional(),
  })
  .strict();
export type WarehouseCartonRecommendationBlocker = z.infer<
  typeof WarehouseCartonRecommendationBlocker
>;

export const WarehouseCartonRecommendationEvidence = z
  .object({
    lineId: Uuid,
    source: z.enum(['warehouse', 'amazon_catalog']),
    observationId: Uuid,
    confidence: WarehousePhysicalConfidence,
    reason: z.string().trim().min(1),
    observedAt: Iso,
    lastSeenAt: Iso.optional(),
  })
  .strict();
export type WarehouseCartonRecommendationEvidence = z.infer<
  typeof WarehouseCartonRecommendationEvidence
>;

export const WarehouseCartonRecommendationPlacement = z
  .object({
    lineId: Uuid,
    unit: WarehousePhysicalSafeInteger.positive(),
    xUm: WarehousePhysicalNonnegativeIntegerString,
    yUm: WarehousePhysicalNonnegativeIntegerString,
    zUm: WarehousePhysicalNonnegativeIntegerString,
    lengthUm: WarehousePhysicalIntegerString,
    widthUm: WarehousePhysicalIntegerString,
    heightUm: WarehousePhysicalIntegerString,
    orientation: z.string().trim().min(1),
  })
  .strict();
export type WarehouseCartonRecommendationPlacement = z.infer<
  typeof WarehouseCartonRecommendationPlacement
>;

export const WarehouseCartonRecommendationPackedCarton = z
  .object({
    cartonId: Uuid,
    code: WarehouseCartonFields.code,
    contents: z
      .array(z.object({ lineId: Uuid, quantity: WarehousePhysicalSafeInteger.positive() }).strict())
      .min(1),
    placements: z.array(WarehouseCartonRecommendationPlacement),
    actualWeightMg: WarehousePhysicalIntegerString,
    dimensionalWeightMg: WarehousePhysicalIntegerString,
    grossWeightMg: WarehousePhysicalIntegerString,
    billableWeightMg: WarehousePhysicalIntegerString,
    projectedCents: WarehousePhysicalNonnegativeIntegerString,
  })
  .strict();
export type WarehouseCartonRecommendationPackedCarton = z.infer<
  typeof WarehouseCartonRecommendationPackedCarton
>;

export const WarehouseCartonRecommendationScenario = z
  .object({
    source: z.enum(['single_sku_baseline', 'mixed_best_fit']),
    selectionReason: z.enum(['baseline_retained', 'cheaper_mixed']),
    cartons: z.array(WarehouseCartonRecommendationPackedCarton),
    actualWeightMg: WarehousePhysicalNonnegativeIntegerString,
    dimensionalWeightMg: WarehousePhysicalNonnegativeIntegerString,
    billableWeightMg: WarehousePhysicalNonnegativeIntegerString,
    projectedCents: WarehousePhysicalNonnegativeIntegerString,
    confidence: WarehousePhysicalConfidence,
    provisional: z.boolean(),
  })
  .strict();
export type WarehouseCartonRecommendationScenario = z.infer<
  typeof WarehouseCartonRecommendationScenario
>;

export const WarehouseCartonRecommendationRequest = z
  .object({
    marketplaceId: WarehousePhysicalMarketplaceId,
    expectedBatchVersion: WarehousePhysicalVersion,
  })
  .strict();
export type WarehouseCartonRecommendationRequest = z.infer<
  typeof WarehouseCartonRecommendationRequest
>;
const WarehouseCartonRecommendationResponseValues = {
  algorithmVersion: z.string().trim().min(1),
  evidence: z.array(WarehouseCartonRecommendationEvidence),
  savingsCents: WarehousePhysicalNonnegativeIntegerString,
};
const WarehouseReadyCartonRecommendationResponse = z
  .object({
    status: z.literal('ready'),
    ...WarehouseCartonRecommendationResponseValues,
    blockers: z.array(WarehouseCartonRecommendationBlocker).length(0),
    baseline: WarehouseCartonRecommendationScenario,
    optimized: WarehouseCartonRecommendationScenario,
  })
  .strict();
const WarehouseBlockedCartonRecommendationResponse = z
  .object({
    status: z.literal('blocked'),
    ...WarehouseCartonRecommendationResponseValues,
    blockers: z.array(WarehouseCartonRecommendationBlocker).min(1),
    baseline: z.null(),
    optimized: z.null(),
  })
  .strict();
export const WarehouseCartonRecommendationResponse = z.discriminatedUnion('status', [
  WarehouseReadyCartonRecommendationResponse,
  WarehouseBlockedCartonRecommendationResponse,
]);
export type WarehouseCartonRecommendationResponse = z.infer<
  typeof WarehouseCartonRecommendationResponse
>;

export const WarehouseCartonPlanningErrorCode = z.enum([
  'PHYSICAL_PROFILE_NOT_FOUND',
  'PHYSICAL_PROFILE_CONFLICT',
  'CARTON_NOT_FOUND',
  'CARTON_CONFLICT',
  'CARTONIZATION_SETTINGS_CONFLICT',
  'CARTONIZATION_LIMIT_EXCEEDED',
  'PREP_BATCH_NOT_FOUND',
  'PREP_BATCH_CONFLICT',
]);
export type WarehouseCartonPlanningErrorCode = z.infer<typeof WarehouseCartonPlanningErrorCode>;
export const WarehouseCartonPlanningErrorResponse = z
  .object({
    error: z
      .object({ code: WarehouseCartonPlanningErrorCode, message: z.string().trim().min(1) })
      .strict(),
  })
  .strict();
export type WarehouseCartonPlanningErrorResponse = z.infer<
  typeof WarehouseCartonPlanningErrorResponse
>;
